import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Card, CardContent, CardHeader, Typography, Button, Dialog, DialogContent,
  DialogTitle, DialogActions, Box, Grid, IconButton, Chip, TextField, MenuItem, Menu,
  useMediaQuery, Drawer, FormControl, Select, InputLabel, Tooltip,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Avatar,
  CircularProgress, Tabs, Tab, Autocomplete, InputAdornment, Stack, Skeleton, Fab,
  RadioGroup, Radio, FormControlLabel, Popover
} from '@mui/material';
import {
  ArrowLeft, Mic, CheckSquare, Calendar, Search, Phone,
  Building, Users, FileText, Video, Play, Edit, Eye, Edit2, StickyNote
} from 'lucide-react';
import {
  Add, Warning,
  CheckBox, CalendarToday, CloudUpload, AutoAwesome, Close as CloseIcon,
  Edit as EditIcon, Check as CheckIcon, Delete as DeleteIcon, Search as SearchIcon, MoreVert as MoreVertIcon,
  DoNotDisturbOn, DoNotDisturbAltOutlined
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import apiInstance from 'src/utils/apiAuth';
import * as constants from 'src/utils/constants';
import { getStageDisplayName } from 'src/utils/stageDisplay';
import UserInfo from 'src/utils/Authorization/UserInfo';
import { showErrorToast, showToast } from 'src/utils/Toast/SnackbarProvider';
import { getCountryColor, getDDMMYYYYFormat, getHHMMFormat } from 'src/utils/commonUtils';
import AddCompany from '../company/AddCompany';
import { tasksApi } from 'src/utils/tasksApi';
import MediaViewer from 'src/components/MediaViewer';
import TextEditor from 'src/components/TextEditor';
import React from 'react';
import { createVideoThumbnail } from 'src/utils/videoThumbnailUtil';
import {
  DndContext,
  DragOverlay,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { DroppableColumn, TopDropZone, DraggableDocumentCard, DraggableMeetingCard, DraggableAudioCard, DraggableScheduledMeetingCard, DragDropWrapper } from './EditJourneyDragDrop';
import MeetingScheduler from './MeetingScheduler';
import { debounce } from 'src/utils/debounceSearch';
import { DoneButton, StyledTableRow, KanbanBoardSkeleton, DocumentListSkeleton, TasksViewSkeleton } from './components';
import ScoreCardDrawer from './components/ScoreCardDrawer';
import ActivityTracker from './components/ActivityTracker';
import FinancialCaptureDialog from 'src/components/HrDashboard/pages/dashboard/FinancialCaptureDialog';
import { buildStagePercentMap, isGatedStage } from 'src/utils/stageGate';
import AddToDndDialog from '../company/AddToDndDialog';
import { getDndDetails, removeDnd } from 'src/utils/dndApi';

const STAGE_ROLES_CONFIG = {
  'outreach': {
    suggested: [],
    required: []
  },
  'future-outreach': {
    suggested: [],
    required: []
  },
  'intro call': {
    suggested: ['BM', 'Intro Specialist', 'BA'],  // BM=SAM, BA=PM
    required: ['BM']  // (must)
  },
  'workshop + dm': {
    suggested: ['BA', 'DM'],  // BA=PM
    required: ['BA']  // (must)
  },
  'nb loi + msa': {
    suggested: ['Legal Strategist'],
    required: []  // need based only
  },
  'dd - phase': {
    suggested: ['Deal Lead', 'Commercial Leader'],
    required: []
  },
  'closing': {
    suggested: ['BM', 'BA'],  // BM=SAM, BA=PM
    required: []
  },
  'closed': {
    suggested: ['Engagement Lead'],
    required: []  // post-signing stage
  },
  '100': {
    suggested: ['BM', 'BA', 'Engagement Lead'],  // BM=SAM, BA=PM
    required: ['BM', 'BA']  // mandatory at 100%
  }
};

// Team assignment drawer: fixed set of assignable roles, keyed on stable role.name.
// Order maps to the displayed fields: BA (PM), BM (SAM), Intro Call Taken By (Intro Specialist).
const TEAM_DRAWER_ROLE_NAMES = ['PM', 'SAM', 'Intro Specialist']; // stable role.name order → BA, BM, Intro Call Taken By
const teamRoleDisplay = (role) =>
  role?.name === 'Intro Specialist' ? 'Intro Call Taken By' : (role?.label || role?.name || '');

const getFilteredRolesByStage = (stageName, allRoles) => {
  if (!stageName || !allRoles || allRoles.length === 0) return allRoles || [];

  const stageNameLower = stageName.toLowerCase();

  // Find matching stage key from config
  let suggestedRoles = [];
  for (const [key, config] of Object.entries(STAGE_ROLES_CONFIG)) {
    if (stageNameLower.includes(key)) {
      suggestedRoles = config.suggested;
      break;
    }
  }

  // If no specific mapping found, return all roles
  if (suggestedRoles.length === 0) return allRoles;

  // Filter roles based on suggested roles for this stage
  return allRoles.filter(role =>
    suggestedRoles.some(suggestedRole =>
      role.label.toLowerCase().includes(suggestedRole.toLowerCase()) ||
      suggestedRole.toLowerCase().includes(role.label.toLowerCase())
    )
  );
};

const isCreatedByAgent = (company) => {
  return company?.created_by?.first_name === 'GWX' && company?.created_by?.last_name === 'Agent';
};

/**
 * Check if a stage should be locked for actions (no upload, no drag, no notes, etc.)
 * Stages are locked when company has been at 100% for more than 10 days,
 * and only the 100% stage (stage_id=7) remains editable.
 */
const isStageLockedForActions = (companyData, stage) => {
  if (!companyData?.is_stages_locked) return false;
  
  // Stage ID 7 is the 100% stage which should remain unlocked
  const stageId = stage?.id;
  const stageName = (stage?.name || stage?.label || '').toLowerCase();
  
  // Check if this is the 100% stage (either by ID or by name containing "100")
  const is100PercentStage = stageId === 7 || stageName.includes('100%') || stageName === 'closed';
  
  // If it's the 100% stage, it should NOT be locked
  // All other stages should be locked when is_stages_locked is true
  return !is100PercentStage;
};

function EditJourneyContent() {
  const { id } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const matches = useMediaQuery(theme.breakpoints.down('md'));
  const userInfo = UserInfo();

  const [companyData, setCompanyData] = useState({});
  const [journeyData, setJourneyData] = useState([]);
  const [currentStage, setCurrentStage] = useState('INTRO');
  const [searchQuery, setSearchQuery] = useState('');
  const [searchFocused, setSearchFocused] = useState(false);
  const [addCardOpen, setAddCardOpen] = useState(false);
  const [selectedAction, setSelectedAction] = useState('call');
  const [editCompanyOpen, setEditCompanyOpen] = useState(false);
  const [editCallingCardOpen, setEditCallingCardOpen] = useState(false);
  const [viewCallingCardOpen, setViewCallingCardOpen] = useState(false);
  const [editScoreCardOpen, setEditScoreCardOpen] = useState(false);
  const [viewScoreCardOpen, setViewScoreCardOpen] = useState(false);
  const [viewMode, setViewMode] = useState('stage');
  const [companies, setCompanies] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [meetings, setMeetings] = useState([]);
  const [audioRecordings, setAudioRecordings] = useState([]);
  const [notes, setNotes] = useState([]);
  const [scheduledMeetings, setScheduledMeetings] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [teams, setTeams] = useState([]);
  const [scoreCardData, setScoreCardData] = useState({
    productMarketFit: null,
    executionBottleneck: null,
    gatewayOperatorLeverage: null,
    founderAlignment: null,
    capitalEfficiency: null,
    notes: ''
  });
  const [callingCardData, setCallingCardData] = useState({
    startedYr: '',
    numberOfCustomers: '',
    currentYearRev: '',
    projectedRev: '',
    teamSize: '',
    noOfShareholders: '',
    ownershipWithManagement: '',
    institutionalInvestor: '',
    revenueLast3Years: '',
    mrrMonthlyRecurringRevenue: '',
    arrAnnualRecurringRevenue: '',
    esopPlan: '',
    lastFundRaise: '',
    anyConvertibleSafe: '',
    currentFundRaise: '',
    commitmentForFund: '',
    existingInvestorParticipating: '',
    totalExpensesPerMonth: '',
    utilizationOfFund: '',
    bankLoans: '',
    techSalesOutsourcedBreakdown: ''
  });
  const [teamMembers, setTeamMembers] = useState([]);
  const [userRoles, setUserRoles] = useState([]); // Centralized user roles from API
  const [tasksByStage, setTasksByStage] = useState({});
  const [stages, setStages] = useState([]);
  const [stageCallingCards, setStageCallingCards] = useState({});
  const [editTeamOpen, setEditTeamOpen] = useState(false);
  const [selectedStage, setSelectedStage] = useState(null);

  const [closedDealDetails, setClosedDealDetails] = useState(null);
  const [editClosedDealOpen, setEditClosedDealOpen] = useState(false);
  const [viewClosedDealOpen, setViewClosedDealOpen] = useState(false);
  const [businessTypeOptions, setBusinessTypeOptions] = useState([]);
  // T12 (FR-017/SC-002): read-only financial change-history list shown below the Deal Snapshot
  // in the Deal Details drawer's view mode. Newest-first, per the T7 endpoint contract.
  const [financialsHistory, setFinancialsHistory] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadingProgress, setLoadingProgress] = useState(0);
  const [pendingStageChange, setPendingStageChange] = useState(null);
  const [taskAssignmentDrawerOpen, setTaskAssignmentDrawerOpen] = useState(false);

  // 5% Stage Reminder states
  const [fivePercentReminderDialogOpen, setFivePercentReminderDialogOpen] = useState(false);
  const [companyReminder, setCompanyReminder] = useState(null);
  const [reminderNoteDialogOpen, setReminderNoteDialogOpen] = useState(false);
  const [reminderRemovalConfirmOpen, setReminderRemovalConfirmOpen] = useState(false);
  const [pendingStageChangeFromFivePercent, setPendingStageChangeFromFivePercent] = useState(null);

  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarAction, setSidebarAction] = useState('call');
  const [currentStageForAction, setCurrentStageForAction] = useState(null);
  const [recordingStages, setRecordingStages] = useState({});
  const [recordingTimes, setRecordingTimes] = useState({});
  const [mediaRecorders, setMediaRecorders] = useState({});
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [documentPreviewOpen, setDocumentPreviewOpen] = useState(false);
  const [currentStageId, setCurrentStageId] = useState("");
  const [offerings, setOfferings] = useState([]);
  const [currentOfferingId, setCurrentOfferingId] = useState("");
  const [companyTeamAssignments, setCompanyTeamAssignments] = useState([]);

  // Company search dropdown states
  const [companySearchInput, setCompanySearchInput] = useState('');
  const [debouncedCompanySearch, setDebouncedCompanySearch] = useState('');
  const [companyOptions, setCompanyOptions] = useState([]);
  const [isSearchingCompanies, setIsSearchingCompanies] = useState(false);

  // Text Note states
  const [textNoteDialogOpen, setTextNoteDialogOpen] = useState(false);
  const [textNoteTitle, setTextNoteTitle] = useState('');
  const [textNoteContent, setTextNoteContent] = useState('');
  const [textNoteStage, setTextNoteStage] = useState(null);
  const [editingNoteId, setEditingNoteId] = useState(null); // Track if editing existing note
  const [isListening, setIsListening] = useState(false);
  const [isSavingNote, setIsSavingNote] = useState(false);
  const recognitionRef = useRef(null);

  // Outreach states
  const [outreachDetails, setOutreachDetails] = useState(null);
  const [markOutreachDrawerOpen, setMarkOutreachDrawerOpen] = useState(false);
  const [viewOutreachDrawerOpen, setViewOutreachDrawerOpen] = useState(false);
  const [isEditingOutreach, setIsEditingOutreach] = useState(false);
  const [outreachComment, setOutreachComment] = useState('');
  const [outreachMethod, setOutreachMethod] = useState('email');
  const [outreachDate, setOutreachDate] = useState('');
  const [outreachTime, setOutreachTime] = useState('');
  const [isMarkingOutreach, setIsMarkingOutreach] = useState(false);
  const [isRemovingOutreach, setIsRemovingOutreach] = useState(false);
  const [isSavingOutreachEdit, setIsSavingOutreachEdit] = useState(false);

  // QM-exit comment prompt states/ref (US-4)
  const [qmExitCommentDialogOpen, setQmExitCommentDialogOpen] = useState(false);
  const [qmExitCommentInput, setQmExitCommentInput] = useState('');
  const [pendingQmExitTarget, setPendingQmExitTarget] = useState(null);
  const qmExitCommentToSubmitRef = useRef('');

  // B1 financial-capture gate: moving into a >=50% stage must first confirm TCI/TCV via the
  // FinancialCaptureDialog. financialsCapturedRef mirrors qmExitCommentToSubmitRef's lifecycle so
  // onConfirm can re-dispatch handleStageChange and COMPOSE with the downstream 100%/team/QM/instant
  // branches (FR-008/010/011/012). Reset to false after every completed/failed move to re-prompt.
  const [financialCaptureOpen, setFinancialCaptureOpen] = useState(false);
  const [financialCaptureTarget, setFinancialCaptureTarget] = useState(null);
  const financialsCapturedRef = useRef(false);
  const stagePercentMap = React.useMemo(() => buildStagePercentMap(stages), [stages]);
  // Stable identity so the dialog's [open, initialValues] effect doesn't re-seed (wipe) inputs on
  // unrelated parent re-renders while it's open; only changes when the source financials change.
  const financialCaptureInitialValues = React.useMemo(() => ({
    tci: companyData?.tci ?? '',
    total_contract_value: companyData?.total_contract_value ?? '',
    one_time_saas: companyData?.one_time_saas ?? '',
    mrr: companyData?.mrr ?? '',
  }), [companyData?.tci, companyData?.total_contract_value, companyData?.one_time_saas, companyData?.mrr]);

  // QM-exit comment read-only view state (US-4, Task 7)
  const [viewQmExitCommentOpen, setViewQmExitCommentOpen] = useState(false);

  // DND (Do-Not-Disturb) states — mirrors the invalid-company pattern below.
  const [dndDetails, setDndDetails] = useState(null);
  const [addDndDialogOpen, setAddDndDialogOpen] = useState(false);
  const [dndPopoverAnchor, setDndPopoverAnchor] = useState(null);
  const [isRemovingDnd, setIsRemovingDnd] = useState(false);

  // Invalid company states
  const [invalidDetails, setInvalidDetails] = useState(null);
  const [markInvalidDrawerOpen, setMarkInvalidDrawerOpen] = useState(false);
  const [viewInvalidDrawerOpen, setViewInvalidDrawerOpen] = useState(false);
  const [invalidReasons, setInvalidReasons] = useState([]);
  const [selectedInvalidReason, setSelectedInvalidReason] = useState('');
  const [invalidDescription, setInvalidDescription] = useState('');
  const [isMarkingInvalid, setIsMarkingInvalid] = useState(false);
  const [isRemovingInvalid, setIsRemovingInvalid] = useState(false);
  const [isEditingInvalid, setIsEditingInvalid] = useState(false);
  const [isSavingInvalidEdit, setIsSavingInvalidEdit] = useState(false);

  // Archive company states
  // Activity tracker state
  const [activityDrawerOpen, setActivityDrawerOpen] = useState(false);

  const [archiveDetails, setArchiveDetails] = useState(null);
  const [archiveDrawerOpen, setArchiveDrawerOpen] = useState(false);
  const [viewArchiveDrawerOpen, setViewArchiveDrawerOpen] = useState(false);
  const [archiveNote, setArchiveNote] = useState('');
  const [isArchiving, setIsArchiving] = useState(false);
  const [isUnarchiving, setIsUnarchiving] = useState(false);

  // Drag and drop states
  const [activeId, setActiveId] = useState(null);
  const [draggedItem, setDraggedItem] = useState(null);

  // Drag and drop sensors
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // File upload handlers
  const handleUniversalFileUpload = (event, stage) => {
    const file = event.target.files[0];
    if (!file) return;

    // Get the accept attribute from the input element
    const acceptAttr = event.target.accept;

    // Validate file type based on accept attribute
    const fileName = file.name.toLowerCase();

    // Check if it's a video upload button (accept contains video/*)
    if (acceptAttr.includes('video/*') || acceptAttr.includes('.mp4')) {
      const videoExtensions = ['.mp4', '.avi', '.mov', '.webm', '.mkv', '.flv', '.wmv', '.m4a'];
      if (!videoExtensions.some(ext => fileName.endsWith(ext))) {
        showErrorToast('Please upload only video files (MP4, AVI, MOV, WEBM, MKV, FLV, WMV, M4A)');
        event.target.value = ''; // Reset input
        return;
      }
    }

    // Check if it's a document upload button (accept contains .pdf, .doc, etc.)
    if (acceptAttr.includes('.pdf') && !acceptAttr.includes('video')) {
      const docExtensions = ['.pdf', '.doc', '.docx', '.ppt', '.pptx', '.xls', '.xlsx', '.txt', '.csv', '.html', '.htm'];
      if (!docExtensions.some(ext => fileName.endsWith(ext))) {
        showErrorToast('Please upload only document files (PDF, DOC, DOCX, PPT, PPTX, XLS, XLSX, TXT, CSV, HTML)');
        event.target.value = ''; // Reset input
        return;
      }
    }

    uploadUniversalFile(file, stage);
  };

  const [deleteDocDialog, setDeleteDocDialog] = useState({ open: false, documentId: null });
  const [uploadProgress, setUploadProgress] = useState({});

  const canDeleteDocument = (document) => {
    // Check if user is super admin
    const isSuperAdmin = userInfo?.role?.includes('SUPER_ADMIN');
    if (isSuperAdmin) return { canDelete: true, reason: '' };

    // Calculate seconds since upload
    const uploadTime = new Date(document.created_at || document.created || document.uploaded_on);
    const now = new Date();
    return { canDelete: true, reason: '' };
  };

  const handleDeleteDocument = (documentId) => {
    const document = documents.find(d => d.id === documentId);
    if (!document) {
      setDeleteDocDialog({ open: true, documentId });
      return;
    }

    const { canDelete, reason } = canDeleteDocument(document);
    if (!canDelete) {
      showErrorToast(reason);
      return;
    }

    setDeleteDocDialog({ open: true, documentId });
  };

  const confirmDeleteDocument = async () => {
    const itemId = deleteDocDialog.documentId;
    try {
      // Try to delete as document first, if fails try as meeting
      try {
        await apiInstance.delete(`company-documents/${itemId}/`, {
          headers: {
            Authorization: `Token ${localStorage.getItem('authToken')}`
          }
        });

        // Immediately update local state to remove the item
        setDocuments(prev => prev.filter(doc => doc.id !== itemId));
        setMeetings(prev => prev.filter(meeting => meeting.id !== itemId));
        setAudioRecordings(prev => prev.filter(audio => audio.id !== itemId));

        showToast('File deleted successfully', 'success');
        setDeleteDocDialog({ open: false, documentId: null });
        // Refresh data in background
        getJourneyData();
      } catch (docError) {
        // Check if it's a 403 error (permission denied)
        if (docError.response?.status === 403) {
          const errorMessage = docError.response?.data?.message || 'You do not have permission to delete this document';
          showToast(errorMessage, 'error');
          setDeleteDocDialog({ open: false, documentId: null });
          return;
        }

        /* Commenting out meeting delete fallback for now as per latest decision. This endpoint does not exists */
        // // If document delete fails with other error, try meeting endpoint
        // try {
        //   await apiInstance.delete(`journey/meeting/${itemId}/`, {
        //     headers: {
        //       Authorization: `Token ${localStorage.getItem('authToken')}`
        //     }
        //   });

        //   // Immediately update local state to remove the item
        //   setDocuments(prev => prev.filter(doc => doc.id !== itemId));
        //   setMeetings(prev => prev.filter(meeting => meeting.id !== itemId));
        //   setAudioRecordings(prev => prev.filter(audio => audio.id !== itemId));

        //   showToast('File deleted successfully', 'success');
        //   setDeleteDocDialog({ open: false, documentId: null });
        //   // Refresh data in background
        //   getJourneyData();
        // } catch (meetingError) {
        //   // Check if it's a 403 error for meeting
        //   if (meetingError.response?.status === 403) {
        //     const errorMessage = meetingError.response?.data?.message || 'You do not have permission to delete this item';
        //     showToast(errorMessage, 'error');
        //   } else {
        //     showToast('Error deleting file', 'error');
        //   }
        //   setDeleteDocDialog({ open: false, documentId: null });
        // }
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Error deleting file';
      showToast(errorMessage, 'error');
      setDeleteDocDialog({ open: false, documentId: null });
    }
  };

  const handleDeleteMeeting = (meetingId) => {
    const meeting = meetings.find(m => m.id === meetingId);
    if (!meeting) {
      setDeleteDocDialog({ open: true, documentId: meetingId });
      return;
    }

    const { canDelete, reason } = canDeleteDocument(meeting);
    if (!canDelete) {
      showErrorToast(reason);
      return;
    }

    setDeleteDocDialog({ open: true, documentId: meetingId });
  };

  const handleDeleteAudio = (audioId) => {
    const audio = audioRecordings.find(a => a.id === audioId);
    if (!audio) {
      setDeleteDocDialog({ open: true, documentId: audioId });
      return;
    }

    const { canDelete, reason } = canDeleteDocument(audio);
    if (!canDelete) {
      showErrorToast(reason);
      return;
    }

    setDeleteDocDialog({ open: true, documentId: audioId });
  };

  const uploadUniversalFile = async (file, stage) => {
    const stageKey = `stage-${stage.id}`;
    try {
      setUploadProgress(prev => ({ ...prev, [stageKey]: 0 }));

      const formData = new FormData();
      formData.append('file', file);
      formData.append('title', file.name);
      formData.append('company_id', id);
      formData.append('stage_id', stage.id);
      formData.append('stage_name', stage.name);
      formData.append('uploaded_on', new Date().toISOString());

      const response = await apiInstance.post('company-journey/upload-document/', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          Authorization: `Token ${localStorage.getItem('authToken')}`
        },
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadProgress(prev => ({ ...prev, [stageKey]: percentCompleted }));
        }
      });

      showToast('File uploaded successfully', 'success');
      setUploadProgress(prev => ({ ...prev, [stageKey]: null }));
      getJourneyData();

    } catch (error) {
      showErrorToast('Error uploading file: ' + (error.response?.data?.message || error.message));
      setUploadProgress(prev => ({ ...prev, [stageKey]: null }));
    }
  };

  const uploadAudioFile = async (file, stage) => {
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('title', `Audio Recording - ${stage.name}`);
      formData.append('company_id', id);
      formData.append('stage_id', stage.id);
      formData.append('stage_name', stage.name);

      await apiInstance.post('company-journey/upload-document/', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          Authorization: `Token ${localStorage.getItem('authToken')}`
        }
      });

      showToast('Audio recording saved successfully', 'success');
      getJourneyData();
    } catch (error) {
      showErrorToast('Error saving audio recording');
    }
  };

  // Text Note Functions
  const handleStartListening = () => {
    if (!('webkitSpeechRecognition' in window) && !('SpeechRecognition' in window)) {
      showErrorToast(null, 'Speech recognition is not supported in your browser');
      return;
    }

    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    const recognition = new SpeechRecognition();

    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.lang = 'en-US';

    recognition.onstart = () => {
      setIsListening(true);
      showToast('Listening...', 'info', 1000);
    };

    recognition.onresult = (event) => {
      let interimTranscript = '';
      let finalTranscript = '';

      for (let i = event.resultIndex; i < event.results.length; i++) {
        const transcript = event.results[i][0].transcript;
        if (event.results[i].isFinal) {
          finalTranscript += transcript + ' ';
        } else {
          interimTranscript += transcript;
        }
      }

      if (finalTranscript) {
        // Append text to existing content
        const currentText = textNoteContent.replace(/<[^>]*>/g, ''); // Strip HTML tags
        const newText = currentText + (currentText ? ' ' : '') + finalTranscript;
        setTextNoteContent(newText);
      }
    };

    recognition.onerror = (event) => {
      console.error('Speech recognition error:', event.error);
      setIsListening(false);
      if (event.error === 'no-speech') {
        showErrorToast(null, 'No speech detected. Please try again.');
      } else if (event.error === 'not-allowed') {
        showErrorToast(null, 'Microphone access denied. Please allow microphone access.');
      } else {
        showErrorToast(null, `Speech recognition error: ${event.error}`);
      }
    };

    recognition.onend = () => {
      setIsListening(false);
    };

    recognitionRef.current = recognition;
    recognition.start();
  };

  const handleStopListening = () => {
    if (recognitionRef.current) {
      recognitionRef.current.stop();
      setIsListening(false);
      showToast('Stopped listening', 'info', 1000);
    }
  };

  const handleSaveTextNote = async () => {
    if (!textNoteContent || !textNoteContent.trim()) {
      showErrorToast(null, 'Please enter some text before saving');
      return;
    }

    setIsSavingNote(true);
    try {
      console.log('💾 Saving note with stage:', textNoteStage);
      console.log('💾 Stage ID:', textNoteStage?.id);
      console.log('💾 Stage Name:', textNoteStage?.name || textNoteStage?.label);

      const noteData = {
        title: textNoteTitle,
        note_text: textNoteContent,
        stage_id: textNoteStage?.id || null,
        task_id: 1, // Required by backend
        company_id: parseInt(id)
        // created_by and modified_by are set automatically by backend from auth token
      };

      console.log('💾 Note data being sent:', noteData);

      if (editingNoteId) {
        // UPDATE existing note
        await apiInstance.put(`notes/${editingNoteId}/`, noteData, {
          headers: {
            Authorization: `Token ${localStorage.getItem('authToken')}`,
          }
        });
        showToast('Text note updated successfully!', 'success');
      } else {
        // CREATE new note
        const response = await apiInstance.post('notes/', noteData, {
          headers: {
            Authorization: `Token ${localStorage.getItem('authToken')}`,
          }
        });
        console.log('💾 Note created, response:', response.data);
        showToast('Text note saved successfully!', 'success');
      }

      setTextNoteDialogOpen(false);
      setTextNoteTitle('');
      setTextNoteContent('');
      setTextNoteStage(null);
      setEditingNoteId(null);
      getJourneyData(); // Refresh to show new/updated note
    } catch (error) {
      showErrorToast(error);
    } finally {
      setIsSavingNote(false);
    }
  };

  const handleCloseTextNoteDialog = () => {
    if (isListening) {
      handleStopListening();
    }
    setTextNoteDialogOpen(false);
    setTextNoteTitle('');
    setTextNoteContent('');
    setTextNoteStage(null);
    setEditingNoteId(null);
  };

  // Function to open note in edit mode
  const handleEditNote = (note) => {
    setTextNoteTitle(note.title || '');
    setTextNoteContent(note.note_text);
    setTextNoteStage({ id: note.stage_id, label: note.stage });
    setEditingNoteId(note.id);
    setTextNoteDialogOpen(true);
  };

  // Delete note states and handlers
  const [deleteNoteDialogOpen, setDeleteNoteDialogOpen] = useState(false);
  const [deletingNote, setDeletingNote] = useState(null);
  const [isDeletingNote, setIsDeletingNote] = useState(false);

  const handleDeleteNote = (note) => {
    setDeletingNote(note);
    setDeleteNoteDialogOpen(true);
  };

  const confirmDeleteNote = async () => {
    if (!deletingNote) return;
    setIsDeletingNote(true);
    try {
      await apiInstance.delete(`notes/${deletingNote.id}/`, {
        headers: { Authorization: `Token ${localStorage.getItem('authToken')}` }
      });
      showToast('Note deleted successfully', 'success');
      setDeleteNoteDialogOpen(false);
      setDeletingNote(null);
      getJourneyData(); // Refresh data
    } catch (error) {
      showErrorToast('Failed to delete note');
    } finally {
      setIsDeletingNote(false);
    }
  };

  const NoteCard = ({ note, formatDateTime, onEdit, onDelete }) => {
    const canDelete = userInfo?.role?.includes('SUPER_ADMIN') || userInfo?.role?.includes('ADMIN');
    
    // Strip HTML tags and get plain text preview
    const getTextPreview = (htmlContent) => {
      const div = document.createElement('div');
      div.innerHTML = htmlContent || '';
      const text = div.textContent || div.innerText || '';
      return text.length > 100 ? text.substring(0, 100) + '...' : text;
    };

    const notePreview = getTextPreview(note.note_text);
    const [menuAnchorEl, setMenuAnchorEl] = useState(null);
    const isMenuOpen = Boolean(menuAnchorEl);

    const handleCardClick = () => {
      if (onEdit) {
        onEdit(note);
      }
    };

    const handleMenuOpen = (event) => {
      event.stopPropagation();
      setMenuAnchorEl(event.currentTarget);
    };

    const handleMenuClose = (event) => {
      if (event) {
        event.stopPropagation();
      }
      setMenuAnchorEl(null);
    };

    const handleEditClick = (event) => {
      event.stopPropagation();
      setMenuAnchorEl(null);
      if (onEdit) {
        onEdit(note);
      }
    };

    const handleDeleteClick = (event) => {
      event.stopPropagation();
      setMenuAnchorEl(null);
      if (onDelete) {
        onDelete(note);
      }
    };

    return (
      <Card
        sx={{
          mb: 2,
          cursor: 'pointer',
          '&:hover': {
            borderColor: '#00AB55',
            border: '2px solid #00AB55'
          }
        }}
        onClick={handleCardClick}
      >
        <Box sx={{
          height: 128,
          backgroundColor: '#fff3e0',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          position: 'relative',
          p: 2
        }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ width: '100%', position: 'absolute', top: 0, left: 0, p: 1 }}>
            {/* Three-dot menu in top-left corner */}
            <IconButton
              size="small"
              sx={{
                color: 'text.secondary',
                '&:hover': { backgroundColor: 'action.hover' }
              }}
              onClick={handleMenuOpen}
            >
              <MoreVertIcon fontSize="small" />
            </IconButton>

            {formatDateTime && note.modified_at && (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ fontSize: '0.7rem' }}
              >
                {formatDateTime(note.modified_at)}
              </Typography>
            )}

            <Chip
              label="NOTE"
              size="small"
              sx={{
                backgroundColor: '#f57c00',
                color: 'white',
                fontSize: '0.75rem',
                fontWeight: 'bold'
              }}
            />
          </Stack>

          <Menu
            anchorEl={menuAnchorEl}
            open={isMenuOpen}
            onClose={handleMenuClose}
            anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
            transformOrigin={{ vertical: 'top', horizontal: 'left' }}
          >
            <MenuItem onClick={handleEditClick}>
              <EditIcon sx={{ mr: 1, fontSize: 18 }} />
              Edit Note
            </MenuItem>
            {onDelete && canDelete && (
              <MenuItem onClick={handleDeleteClick}>
                <DeleteIcon sx={{ mr: 1, fontSize: 18, color: '#d32f2f' }} />
                <Typography color="#d32f2f">Delete Note</Typography>
              </MenuItem>
            )}
          </Menu>

          <StickyNote size={48} color="#f57c00" />

          <Tooltip title={note.title || notePreview} arrow>
            <Typography
              variant="caption"
              sx={{
                position: 'absolute',
                bottom: 8,
                left: 8,
                right: 8,
                textAlign: 'center',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical'
              }}
            >
              {note.title || notePreview || 'Text Note'}
            </Typography>
          </Tooltip>
        </Box>
      </Card>
    );
  };

  const AudioCard = ({ audio, formatDateTime }) => {
    const [open, setOpen] = useState(false);
    const fileUrl = audio?.file || audio?.audioUrl || '';
    const audioSrc = fileUrl.startsWith('http') ? fileUrl : `${constants.HTTP_METHOD}${constants.HTTP_URL}${constants.HTTP_PORT}${fileUrl}`;

    // Prevent body scrolling when modal is open
    useEffect(() => {
      if (open) {
        const previousOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        return () => {
          document.body.style.overflow = previousOverflow || '';
        };
      }
      return undefined;
    }, [open]);

    return (
      <>
        <Card
          sx={{
            mb: 2,
            cursor: 'pointer',
            '&:hover': {
              borderColor: '#1976d2',
              border: '2px solid #1976d2'
            }
          }}
          onClick={() => setOpen(true)}
        >
          <Box sx={{
            height: 128,
            backgroundColor: '#fff3e0',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            position: 'relative',
            p: 2
          }}>
            <Mic size={48} color="#ff9800" />
            <Chip
              label="AUDIO"
              size="small"
              sx={{
                position: 'absolute',
                top: 8,
                right: 8,
                backgroundColor: '#ff9800',
                color: 'white',
                fontSize: '0.75rem',
                fontWeight: 'bold'
              }}
            />
          </Box>
          <CardContent sx={{ p: 1.5, pb: '12px !important' }}>
            <Tooltip title={audio.title || 'Audio Recording'} arrow>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }} noWrap>
                {audio.title || 'Audio Recording'}
              </Typography>
            </Tooltip>
            <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', mb: 0.25 }} noWrap>
              Created By: {audio.created_by ? `${audio.created_by.first_name} ${audio.created_by.last_name}` : 'Unknown'}
            </Typography>
            <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary' }}>
              Date: {audio.uploaded_on || audio.created_at ? new Date(audio.uploaded_on || audio.created_at).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }) : 'N/A'}
            </Typography>
          </CardContent>
        </Card>

        <Dialog
          open={open}
          onClose={() => setOpen(false)}
          maxWidth="lg"
          fullWidth
          PaperProps={{
            sx: {
              height: '80vh',
              minHeight: 600
            }
          }}
        >
          <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">{audio.title || 'Audio Recording'}</Typography>
            <IconButton onClick={() => setOpen(false)}>
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent sx={{ p: 2, height: '100%' }}>
            <MediaViewer
              file={{ fileUrl: fileUrl }}
              showPlayBackSpeed={true}
              matches={matches}
            />
          </DialogContent>
        </Dialog>
      </>
    );
  };

  const startRecording = async (stage) => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const recorder = new MediaRecorder(stream);
      const chunks = [];

      recorder.ondataavailable = (e) => chunks.push(e.data);
      recorder.onstop = () => {
        const blob = new Blob(chunks, { type: 'audio/wav' });
        const file = new File([blob], `recording-${Date.now()}.wav`, { type: 'audio/wav' });
        uploadAudioFile(file, stage);
        stream.getTracks().forEach(track => track.stop());

        // Clean up recording state
        setRecordingStages(prev => ({ ...prev, [stage.id]: false }));
        setRecordingTimes(prev => ({ ...prev, [stage.id]: 0 }));
        setMediaRecorders(prev => ({ ...prev, [stage.id]: null }));
      };

      recorder.start();
      setMediaRecorders(prev => ({ ...prev, [stage.id]: recorder }));
      setRecordingStages(prev => ({ ...prev, [stage.id]: true }));
      setRecordingTimes(prev => ({ ...prev, [stage.id]: 0 }));

      // Start timer
      const timer = setInterval(() => {
        setRecordingTimes(prev => ({ ...prev, [stage.id]: (prev[stage.id] || 0) + 1 }));
      }, 1000);

      recorder.onstop = () => {
        clearInterval(timer);
        const blob = new Blob(chunks, { type: 'audio/wav' });
        const file = new File([blob], `recording-${Date.now()}.wav`, { type: 'audio/wav' });
        uploadAudioFile(file, stage);
        stream.getTracks().forEach(track => track.stop());

        setRecordingStages(prev => ({ ...prev, [stage.id]: false }));
        setRecordingTimes(prev => ({ ...prev, [stage.id]: 0 }));
        setMediaRecorders(prev => ({ ...prev, [stage.id]: null }));
      };
    } catch (error) {
      showErrorToast('Error accessing microphone');
    }
  };

  const stopRecording = (stage) => {
    const recorder = mediaRecorders[stage.id];
    if (recorder && recordingStages[stage.id]) {
      recorder.stop();
    }
  };

  // Update document stage API call
  const updateDocumentStage = async (documentId, newStageId) => {
    try {
      const response = await apiInstance.patch(
        `company-journey/bulk-update-stage/`,
        { document_ids: [documentId], stage_id: newStageId },
        {
          headers: { Authorization: `Token ${localStorage.getItem('authToken')}` }
        }
      );
      return response.data.success;
    } catch (error) {
      console.error('Error updating document stage:', error);
      return false;
    }
  };

  const updateMeetingStage = async (meetingId, newStageId) => {
    try {
      const response = await apiInstance.patch(
        `company-journey/${meetingId}/update-stage/`,
        { stage_id: newStageId },
        {
          headers: { Authorization: `Token ${localStorage.getItem('authToken')}` }
        }
      );
      return response.data.success;
    } catch (error) {
      showErrorToast(`Failed to update meeting: ${error.response?.data?.message || error.message}`);
      return false;
    }
  };

  const updateAudioStage = async (audioId, newStageId) => {
    try {
      const response = await apiInstance.patch(
        `company-journey/${audioId}/update-audio-stage/`,
        { stage_id: newStageId },
        {
          headers: { Authorization: `Token ${localStorage.getItem('authToken')}` }
        }
      );
      return response.data.success;
    } catch (error) {
      console.error('Error updating audio stage:', error);
      return false;
    }
  };

  const handleDragStart = (event) => {
    const { active } = event;
    setActiveId(active.id);

    // Find the dragged item
    let foundItem = null;
    let itemType = null;

    // Check documents
    documents.forEach(doc => {
      if (doc.id.toString() === active.id) {
        foundItem = doc;
        itemType = 'document';
      }
    });

    // Check meetings
    if (!foundItem) {
      meetings.forEach(meeting => {
        if (meeting.id.toString() === active.id) {
          foundItem = meeting;
          itemType = 'meeting';
        }
      });
    }

    // Check audio recordings
    if (!foundItem) {
      audioRecordings.forEach(audio => {
        if (audio.id.toString() === active.id) {
          foundItem = audio;
          itemType = 'audio';
        }
      });
    }

    setDraggedItem({ item: foundItem, type: itemType });
  };

  const handleDragEnd = async (event) => {
    const { active, over } = event;

    setActiveId(null);
    setDraggedItem(null);

    if (!over || !draggedItem) return;

    const overId = over.id;

    // Find target stage from either droppable column or top drop zone
    let targetStage = null;
    if (overId.toString().startsWith('droppable-')) {
      const stageId = overId.toString().replace('droppable-', '');
      targetStage = stages.find(s => s.id.toString() === stageId);
    } else if (overId.toString().startsWith('top-drop-')) {
      const stageId = overId.toString().replace('top-drop-', '');
      targetStage = stages.find(s => s.id.toString() === stageId);
    }

    if (!targetStage) {
      return;
    }

    // Check if stages are locked (company at 100% for 10+ days)
    // Don't allow moving items to locked stages
    if (isStageLockedForActions(companyData, targetStage)) {
      showErrorToast('Cannot move items - Editing is disabled because the company has been at 100% stage for over 10 days');
      return;
    }

    // Also check if source stage is locked - don't allow moving items FROM locked stages
    const sourceStage = stages.find(s => s.name === draggedItem.item.stage);
    if (sourceStage && isStageLockedForActions(companyData, sourceStage)) {
      showErrorToast('Cannot move items - Editing is disabled because the company has been at 100% stage for over 10 days');
      return;
    }

    // Check if item is already in the target stage
    if (draggedItem.item.stage === targetStage.name) {
      return;
    }

    // Update UI immediately
    const itemId = draggedItem.item.id;
    const itemType = draggedItem.type;

    if (itemType === 'document') {
      setDocuments(prev => prev.map(doc =>
        doc.id === itemId ? { ...doc, stage: targetStage.name } : doc
      ));

      // Update backend
      const success = await updateDocumentStage(itemId, targetStage.id);
      if (!success) {
        // Revert on failure
        setDocuments(prev => prev.map(doc =>
          doc.id === itemId ? { ...doc, stage: draggedItem.item.stage } : doc
        ));
        showErrorToast('Failed to update document stage');
      } else {
        showToast('Document moved successfully', 'success');
      }
    } else if (itemType === 'meeting') {
      setMeetings(prev => prev.map(meeting =>
        meeting.id === itemId ? { ...meeting, stage: targetStage.name } : meeting
      ));

      // Update backend - use document endpoint for recordings
      const isRecording = draggedItem.item.isDocument;
      const success = isRecording
        ? await updateDocumentStage(itemId, targetStage.id)
        : await updateMeetingStage(itemId, targetStage.id);

      if (!success) {
        // Revert on failure
        setMeetings(prev => prev.map(meeting =>
          meeting.id === itemId ? { ...meeting, stage: draggedItem.item.stage } : meeting
        ));
        showErrorToast('Failed to update meeting stage');
      } else {
        showToast('Meeting moved successfully', 'success');
      }
    } else if (itemType === 'audio') {
      setAudioRecordings(prev => prev.map(audio =>
        audio.id === itemId ? { ...audio, stage: targetStage.name } : audio
      ));

      // Update backend
      const success = await updateAudioStage(itemId, targetStage.id);
      if (!success) {
        // Revert on failure
        setAudioRecordings(prev => prev.map(audio =>
          audio.id === itemId ? { ...audio, stage: draggedItem.item.stage } : audio
        ));
        showErrorToast('Failed to update audio stage');
      } else {
        showToast('Audio moved successfully', 'success');
      }
    }
  };

  const getBusinessTypeOptions = async () => {
    try {
      const res = await apiInstance.get('business-type/', { headers: { Authorization: `Token ${localStorage.getItem('authToken')}` } });
      setBusinessTypeOptions((res?.data?.data || []).filter(b => b?.is_active));
    } catch (e) {
      setBusinessTypeOptions([]); // FR-007: failure must not block editing the other three
    }
  };

  // T12 (FR-017/SC-002): fetch the financial change-history list for the Deal Details drawer.
  // Endpoint already returns newest-first — do NOT re-sort here.
  const getFinancialsHistory = async () => {
    try {
      const res = await apiInstance.get(`company/${id}/financials-history/`, { headers: { Authorization: `Token ${localStorage.getItem('authToken')}` } });
      setFinancialsHistory(res?.data?.data || []);
    } catch (e) {
      setFinancialsHistory([]); // failure must not block viewing the rest of the drawer
    }
  };

  const getCompanyData = async () => {
    try {
      // Get basic company data with timeout
      const response = await Promise.race([
        apiInstance.get(`company/${id}/`, {
          headers: { Authorization: `Token ${localStorage.getItem('authToken')}` },
          timeout: 10000 // 10 second timeout
        }),
        new Promise((_, reject) =>
          setTimeout(() => reject(new Error('Request timeout')), 10000)
        )
      ]);

      if (response.data.status === 403) {
        navigate('/403', { replace: true });
        return;
      }

      let companyData = response?.data?.data || {};

      // Check if company has last_stage_task which contains the current stage
      if (companyData.last_stage_task) {
        // Handle both nested stage object and direct stage reference
        if (companyData.last_stage_task.stage) {
          companyData.current_stage_name = companyData.last_stage_task.stage.label || companyData.last_stage_task.stage.name;
          companyData.current_stage_id = companyData.last_stage_task.stage.id;
        } else if (typeof companyData.last_stage_task === 'object' && companyData.last_stage_task.label) {
          // Handle case where last_stage_task is the stage object itself
          companyData.current_stage_name = companyData.last_stage_task.label || companyData.last_stage_task.name;
          companyData.current_stage_id = companyData.last_stage_task.id;
        } else if (typeof companyData.last_stage_task === 'number') {
          // Handle case where last_stage_task is just the stage ID
          companyData.current_stage_id = companyData.last_stage_task;
        }
      }

      // Ensure current_stage_id is set even if not found above
      if (!companyData.current_stage_id && stages.length > 0) {
        const stageName = companyData.current_stage_name || companyData.stage || companyData.status;
        if (stageName) {
          const foundStage = stages.find(s =>
            s.name === stageName ||
            s.label === stageName ||
            s.name.toLowerCase() === stageName.toLowerCase()
          );
          if (foundStage) {
            companyData.current_stage_id = foundStage.id;

          }
        }
      }

      // Final fallback - if still no current_stage_id, use the first stage
      if (!companyData.current_stage_id && stages.length > 0) {
        companyData.current_stage_id = stages[0].id;

      }
      // Check if company has a direct stage field
      else if (companyData.stage) {
        if (typeof companyData.stage === 'object') {
          companyData.current_stage_name = companyData.stage.name || companyData.stage.label;
          companyData.current_stage_id = companyData.stage.id;
        } else {
          companyData.current_stage_name = companyData.stage;
        }
      }
      // Fallback to status field if available
      else if (companyData.status) {
        companyData.current_stage_name = companyData.status;
      }
      // Final fallback - try to get from journey data
      else {
        try {
          const journeyResponse = await apiInstance.get(
            `${constants.HTTP_METHOD}${constants.HTTP_URL}${constants.HTTP_PORT}/api/v1/company/${id}/journey`,
            { headers: { Authorization: `Token ${localStorage.getItem('authToken')}` } }
          );

          const journeyData = journeyResponse?.data || [];
          if (journeyData.length > 0) {
            // Get the most recent item's stage
            const mostRecentItem = journeyData[0];
            const stageName = mostRecentItem.journey_item?.stage?.name || mostRecentItem.journey_item?.stage?.label;

            if (stageName) {
              companyData.current_stage_name = stageName;
            }
          }
        } catch (journeyError) {
          console.log('Could not fetch journey data:', journeyError);
        }
      }

      // If still no stage found, default to INTRODUCTION
      if (!companyData.current_stage_name) {
        companyData.current_stage_name = 'INTRODUCTION';
      }

      setCompanyData(companyData);
      setCurrentStage(companyData?.current_stage_name || 'INTRODUCTION');
      setLoadingProgress(prev => Math.max(prev, 40));

    } catch (error) {
      console.error('Error fetching company data:', error);
      if (error.message === 'Request timeout') {
        showErrorToast('Loading company data is taking longer than expected. Please refresh the page.');
      } else {
        showErrorToast('Error loading company data');
      }
      setIsLoading(false);
    }
  };

  // Check if company is at 0% stage
  const isZeroPercentStage = () => {
    // Get stage name from stages array using currentStageId (most reliable source)
    const currentStageFromId = stages.find(s => s.id === currentStageId)?.name || '';
    // Prioritize currentStageFromId since it's the most accurate
    const stageName = currentStageFromId || companyData?.current_stage_name || '';
    const stageNameLower = stageName.toLowerCase();
    
    // Use regex to match exactly "0%" not "10%" or "100%"
    const isZero = stageNameLower.includes('- 0%') || 
           stageNameLower.includes('outreach - 0') ||
           stageNameLower === 'outreach - 0%' ||
           /(?<!\d)0%/.test(stageNameLower);
    
    return isZero;
  };

  // Check if company is at 100% stage (Closed)
  const isHundredPercentStage = () => {
    const currentStageFromId = stages.find(s => s.id === currentStageId)?.name || '';
    const stageName = currentStageFromId || companyData?.current_stage_name || '';
    const stageNameLower = stageName.toLowerCase();
    
    return stageNameLower.includes('- 100%') || 
           stageNameLower.includes('closed') ||
           stageNameLower.includes('100%');
  };

  // Check if user is super admin
  const isSuperAdmin = userInfo?.is_superuser === true;

  // Fetch outreach details for 0% stage companies
  const fetchOutreachDetails = async () => {
    if (!id || !isZeroPercentStage()) {
      setOutreachDetails(null);
      return;
    }
    
    try {
      const response = await apiInstance.get(
        `${constants.HTTP_METHOD}${constants.HTTP_URL}${constants.HTTP_PORT}/api/v1/company/${id}/outreach-details/`,
        { headers: { "Authorization": `Token ${localStorage.getItem('authToken')}` } }
      );
      if (response.data.success) {
        setOutreachDetails(response.data.data);
      }
    } catch (error) {
      // Company not outreached - this is expected
      setOutreachDetails(null);
    }
  };

  // Open mark outreach drawer
  const openMarkOutreachDrawer = () => {
    console.log('Opening mark outreach drawer...');
    const now = new Date();
    setOutreachComment('');
    setOutreachMethod('email');
    setOutreachDate(now.toISOString().split('T')[0]);
    setOutreachTime(now.toTimeString().slice(0, 5));
    setMarkOutreachDrawerOpen(true);
    console.log('markOutreachDrawerOpen set to true');
  };

  // Submit mark as outreached
  const submitMarkOutreached = async () => {
    if (!outreachComment.trim()) {
      showErrorToast('Comment is required');
      return;
    }
    if (!outreachDate || !outreachTime) {
      showErrorToast('Date and time are required');
      return;
    }

    setIsMarkingOutreach(true);
    try {
      const outreachedAt = new Date(`${outreachDate}T${outreachTime}:00`).toISOString();
      
      const response = await apiInstance.post(
        `${constants.HTTP_METHOD}${constants.HTTP_URL}${constants.HTTP_PORT}/api/v1/company/${id}/mark-outreached/`,
        {
          comment: outreachComment.trim(),
          outreach_method: outreachMethod,
          outreached_at: outreachedAt
        },
        { headers: { "Authorization": `Token ${localStorage.getItem('authToken')}` } }
      );

      if (response.data.success) {
        showToast('Company marked as outreached successfully');
        setMarkOutreachDrawerOpen(false);
        setOutreachDetails(response.data.data);
      }
    } catch (error) {
      console.error('Mark outreach error:', error);
      showErrorToast(error.response?.data?.message || 'Failed to mark as outreached');
    } finally {
      setIsMarkingOutreach(false);
    }
  };

  // Remove outreach mark
  const removeOutreachMark = async () => {
    if (!id || !outreachDetails) return;
    
    setIsRemovingOutreach(true);
    try {
      const response = await apiInstance.delete(
        `${constants.HTTP_METHOD}${constants.HTTP_URL}${constants.HTTP_PORT}/api/v1/company/${id}/remove-outreach/`,
        { headers: { "Authorization": `Token ${localStorage.getItem('authToken')}` } }
      );

      if (response.data.success) {
        showToast('Outreach mark removed successfully');
        setViewOutreachDrawerOpen(false);
        setOutreachDetails(null);
      }
    } catch (error) {
      console.error('Remove outreach error:', error);
      showErrorToast(error.response?.data?.message || 'Failed to remove outreach mark');
    } finally {
      setIsRemovingOutreach(false);
    }
  };

  // Start editing outreach
  const startEditingOutreach = () => {
    if (!outreachDetails) return;
    
    // Populate form with existing data
    setOutreachMethod(outreachDetails.outreach_method || 'email');
    setOutreachComment(outreachDetails.comment || '');
    
    // Parse existing date/time
    const existingDate = new Date(outreachDetails.outreached_at);
    setOutreachDate(existingDate.toISOString().split('T')[0]);
    setOutreachTime(existingDate.toTimeString().slice(0, 5));
    
    setIsEditingOutreach(true);
  };

  // Cancel editing outreach
  const cancelEditingOutreach = () => {
    setIsEditingOutreach(false);
  };

  // Save outreach edit
  const saveOutreachEdit = async () => {
    if (!outreachComment.trim()) {
      showErrorToast('Comment is required');
      return;
    }
    if (!outreachDate || !outreachTime) {
      showErrorToast('Date and time are required');
      return;
    }

    setIsSavingOutreachEdit(true);
    try {
      const outreachedAt = new Date(`${outreachDate}T${outreachTime}:00`).toISOString();
      
      const response = await apiInstance.patch(
        `${constants.HTTP_METHOD}${constants.HTTP_URL}${constants.HTTP_PORT}/api/v1/company/${id}/update-outreach/`,
        {
          comment: outreachComment.trim(),
          outreach_method: outreachMethod,
          outreached_at: outreachedAt
        },
        { headers: { "Authorization": `Token ${localStorage.getItem('authToken')}` } }
      );

      if (response.data.success) {
        showToast('Outreach details updated successfully');
        setOutreachDetails(response.data.data);
        setIsEditingOutreach(false);
      }
    } catch (error) {
      console.error('Update outreach error:', error);
      showErrorToast(error.response?.data?.message || 'Failed to update outreach details');
    } finally {
      setIsSavingOutreachEdit(false);
    }
  };

  // Fetch invalid reasons from API
  const fetchInvalidReasons = async () => {
    try {
      const response = await apiInstance.get(
        `${constants.HTTP_METHOD}${constants.HTTP_URL}${constants.HTTP_PORT}/api/v1/company/invalid-reasons/`,
        { headers: { "Authorization": `Token ${localStorage.getItem('authToken')}` } }
      );
      console.log('Invalid reasons response:', response.data);
      if (response.data.success) {
        setInvalidReasons(response.data.data || []);
      } else if (response.data && Array.isArray(response.data)) {
        setInvalidReasons(response.data);
      }
    } catch (error) {
      console.error('Failed to fetch invalid reasons:', error);
      showErrorToast('Failed to load invalid reasons');
    }
  };

  // Fetch invalid details for company
  const fetchInvalidDetails = async () => {
    if (!id) {
      setInvalidDetails(null);
      return;
    }

    try {
      const response = await apiInstance.get(
        `${constants.HTTP_METHOD}${constants.HTTP_URL}${constants.HTTP_PORT}/api/v1/company/${id}/invalid-details/`,
        { headers: { "Authorization": `Token ${localStorage.getItem('authToken')}` } }
      );
      if (response.data.success) {
        setInvalidDetails(response.data.data);
      }
    } catch (error) {
      setInvalidDetails(null);
    }
  };

  // Fetch DND (Do-Not-Disturb) status for this company. `data` is the CompanyDnd
  // record or null (spec: dnd-details returns { success, data }).
  const fetchDndDetails = async () => {
    if (!id) {
      setDndDetails(null);
      return;
    }
    try {
      const response = await getDndDetails(id);
      setDndDetails(response?.data?.data || null);
    } catch (error) {
      setDndDetails(null);
    }
  };

  // Remove this company from the DND list, then refresh status.
  const handleRemoveDnd = async () => {
    if (!id) return;
    setIsRemovingDnd(true);
    try {
      const response = await removeDnd(id);
      showToast(response?.data?.message || 'Company removed from DND list');
      setDndPopoverAnchor(null);
      setDndDetails(null);
    } catch (error) {
      showErrorToast(error.response?.data?.message || 'Failed to remove company from DND list');
    } finally {
      setIsRemovingDnd(false);
    }
  };

  // Fetch archive details for company
  const fetchArchiveDetails = async () => {
    if (!id) {
      setArchiveDetails(null);
      return;
    }
    
    try {
      const response = await apiInstance.get(
        `${constants.HTTP_METHOD}${constants.HTTP_URL}${constants.HTTP_PORT}/api/v1/company/${id}/archive-details/`,
        { headers: { "Authorization": `Token ${localStorage.getItem('authToken')}` } }
      );
      if (response.data.success) {
        setArchiveDetails(response.data.data);
      }
    } catch (error) {
      setArchiveDetails(null);
    }
  };

  // Open archive drawer
  const openArchiveDrawer = () => {
    setArchiveNote('');
    setArchiveDrawerOpen(true);
  };

  // Submit archive company
  const submitArchiveCompany = async () => {
    setIsArchiving(true);
    try {
      const response = await apiInstance.post(
        `${constants.HTTP_METHOD}${constants.HTTP_URL}${constants.HTTP_PORT}/api/v1/company/${id}/archive/`,
        { note: archiveNote },
        { headers: { "Authorization": `Token ${localStorage.getItem('authToken')}` } }
      );
      
      if (response.data.success) {
        showToast('Company archived successfully');
        setArchiveDrawerOpen(false);
        setArchiveDetails(response.data.data);
      }
    } catch (error) {
      console.error('Archive company error:', error);
      showErrorToast(error.response?.data?.message || 'Failed to archive company');
    } finally {
      setIsArchiving(false);
    }
  };

  // Submit unarchive company
  const submitUnarchiveCompany = async () => {
    setIsUnarchiving(true);
    try {
      const response = await apiInstance.delete(
        `${constants.HTTP_METHOD}${constants.HTTP_URL}${constants.HTTP_PORT}/api/v1/company/${id}/unarchive/`,
        { headers: { "Authorization": `Token ${localStorage.getItem('authToken')}` } }
      );
      
      if (response.data.success) {
        showToast('Company unarchived successfully');
        setViewArchiveDrawerOpen(false);
        setArchiveDetails(null);
      }
    } catch (error) {
      console.error('Unarchive company error:', error);
      showErrorToast(error.response?.data?.message || 'Failed to unarchive company');
    } finally {
      setIsUnarchiving(false);
    }
  };

  // Open mark invalid drawer
  const openMarkInvalidDrawer = async () => {
    setSelectedInvalidReason('');
    setInvalidDescription('');
    await fetchInvalidReasons();
    setMarkInvalidDrawerOpen(true);
  };

  // Submit mark as invalid
  const submitMarkInvalid = async () => {
    if (!selectedInvalidReason) {
      showErrorToast('Please select a reason');
      return;
    }

    setIsMarkingInvalid(true);
    try {
      const response = await apiInstance.post(
        `${constants.HTTP_METHOD}${constants.HTTP_URL}${constants.HTTP_PORT}/api/v1/company/${id}/mark-invalid/`,
        {
          reason_id: selectedInvalidReason,
          description: invalidDescription.trim() || null
        },
        { headers: { "Authorization": `Token ${localStorage.getItem('authToken')}` } }
      );

      if (response.data.success) {
        showToast('Company marked as invalid and moved to 5% stage');
        setMarkInvalidDrawerOpen(false);
        setInvalidDetails(response.data.data);
        // Reload company data to update stage
        window.location.reload();
      }
    } catch (error) {
      console.error('Mark invalid error:', error);
      showErrorToast(error.response?.data?.message || 'Failed to mark as invalid');
    } finally {
      setIsMarkingInvalid(false);
    }
  };

  // Remove invalid mark
  const removeInvalidMark = async () => {
    setIsRemovingInvalid(true);
    try {
      const response = await apiInstance.delete(
        `${constants.HTTP_METHOD}${constants.HTTP_URL}${constants.HTTP_PORT}/api/v1/company/${id}/remove-invalid/`,
        { headers: { "Authorization": `Token ${localStorage.getItem('authToken')}` } }
      );

      if (response.data.success) {
        showToast('Invalid mark removed, company moved back to 0% stage');
        setViewInvalidDrawerOpen(false);
        setInvalidDetails(null);
        // Reload company data to update stage
        window.location.reload();
      }
    } catch (error) {
      console.error('Remove invalid error:', error);
      showErrorToast(error.response?.data?.message || 'Failed to remove invalid mark');
    } finally {
      setIsRemovingInvalid(false);
    }
  };

  // Save edited invalid details
  const saveInvalidEdit = async () => {
    if (!selectedInvalidReason) {
      showErrorToast('Please select a reason');
      return;
    }

    setIsSavingInvalidEdit(true);
    try {
      const response = await apiInstance.patch(
        `${constants.HTTP_METHOD}${constants.HTTP_URL}${constants.HTTP_PORT}/api/v1/company/${id}/update-invalid/`,
        {
          reason_id: selectedInvalidReason,
          description: invalidDescription.trim() || null
        },
        { headers: { "Authorization": `Token ${localStorage.getItem('authToken')}` } }
      );

      if (response.data.success) {
        showToast('Invalid company details updated successfully');
        setInvalidDetails(response.data.data);
        setIsEditingInvalid(false);
      }
    } catch (error) {
      console.error('Update invalid error:', error);
      showErrorToast(error.response?.data?.message || 'Failed to update invalid details');
    } finally {
      setIsSavingInvalidEdit(false);
    }
  };

  // Start editing invalid details
  const startEditingInvalid = async () => {
    await fetchInvalidReasons();
    setSelectedInvalidReason(invalidDetails?.reason || '');
    setInvalidDescription(invalidDetails?.description || '');
    setIsEditingInvalid(true);
  };

  // Cancel editing invalid details
  const cancelEditingInvalid = () => {
    setIsEditingInvalid(false);
    setSelectedInvalidReason('');
    setInvalidDescription('');
  };

  const getCompanyDoc = (company_id) => {
    if (!company_id) return Promise.resolve([]);

    return apiInstance({
      method: 'get',
      url: `company-documents/get_documents_by_company?company_id=${company_id}`,
      headers: {
        Authorization: `Token ${localStorage.getItem('authToken')}`,
        'Content-Type': 'application/json',
      },
    })
      .then((response) => {
        const docs = response.data || [];
        return docs;
      })
      .catch((error) => {
        showErrorToast(error);
        return [];
      });
  };

  // Search companies by name with API call
  const searchCompanies = async (searchTerm) => {
    if (!searchTerm || searchTerm.trim().length < 2) {
      setCompanyOptions([]);
      return;
    }

    setIsSearchingCompanies(true);
    try {
      const baseURL = `${constants.HTTP_METHOD}${constants.HTTP_URL}${constants.HTTP_PORT}/api/v1`;
      const response = await apiInstance.get(
        `${baseURL}/company/dashboard-tickets/?search=${encodeURIComponent(searchTerm)}&page=1&page_size=20`,
        {
          headers: { Authorization: `Token ${localStorage.getItem('authToken')}` }
        }
      );

      const tickets = response.data?.data?.tickets || [];
      const uniqueCompanies = tickets.filter((ticket, index, self) =>
        ticket.is_active !== false &&
        index === self.findIndex(t => t.id === ticket.id)
      );

      setCompanyOptions(uniqueCompanies);
    } catch (error) {
      console.error('Error searching companies:', error);
      setCompanyOptions([]);
    } finally {
      setIsSearchingCompanies(false);
    }
  };

  const getJourneyData = async (dateFilter = null) => {
    try {
      let url = constants.HTTP_METHOD + constants.HTTP_URL + constants.HTTP_PORT + "/api/v1/company/" + id + "/journey";

      if (dateFilter && (dateFilter.startDate || dateFilter.endDate)) {
        const params = new URLSearchParams();
        if (dateFilter.startDate) params.append('start_date', dateFilter.startDate);
        if (dateFilter.endDate) params.append('end_date', dateFilter.endDate);
        url += '?' + params.toString();
      }

      const response = await Promise.race([
        apiInstance.get(url, {
          headers: { Authorization: `Token ${localStorage.getItem("authToken")}` },
          timeout: 15000 // 15 second timeout
        }),
        new Promise((_, reject) =>
          setTimeout(() => reject(new Error('Journey data request timeout')), 15000)
        )
      ]);
      if (response.data.status === 403) {
        navigate('/403', { replace: true });
        return;
      }

      // Handle empty or null response data
      const journeyData = response?.data || [];
      setJourneyData(journeyData);

      // Process the journey data - backend returns flat array of all items
      setJourneyData(journeyData);

      // Extract documents, meetings, audio recordings from journey data
      const allDocuments = [];
      const allMeetings = [];
      const allAudioRecordings = [];
      const allScheduledMeetings = [];

      // Fetch company documents ONCE before processing journey items to avoid multiple API calls
      const companyDocs = await getCompanyDoc(id);

      journeyData?.forEach((item, index) => {
        // Safely access journey_item properties with null checks
        const journeyItem = item?.journey_item || {};
        const stage = journeyItem?.stage || {};
        const task = journeyItem?.task || {};

        // Get stage name from journey_item, or use uploaded_on/created to infer stage
        let stageName = stage?.name || stage?.label;

        // If no stage from journey_item, use a default based on document type
        if (!stageName) {
          // For documents without a stage, put them in INTRODUCTION by default
          stageName = 'Intro Call - 10%';
        }

        const taskType = task?.type;

        // Check if this is a scheduled meeting (has start_date and attendees)
        const isScheduledMeeting = item.start_date && item.attendees.length > 0;

        // Process scheduled meetings separately - independent of document logic
        if (isScheduledMeeting) {
          allScheduledMeetings.push({
            id: item.id,
            title: item.meeting_recording_notes || `Scheduled Meeting`,
            start_date: item.start_date,
            end_date: item.end_date,
            meeting_timezone: item.meeting_timezone,
            microsoft_teams_url: item.microsoft_teams_url,
            microsoft_meeting_id: item.microsoft_meeting_id,
            is_microsoft_teams: item.is_microsoft_teams,
            recording_document: item.recording_document,
            recording_status: item.recording_status,
            attendees: item.attendees,
            attendees_data: item.attendees_data,
            moderators: item.moderators,
            status: item.status,
            meeting_moderator_link: item.meeting_moderator_link,
            meeting_attendee_link: item.meeting_attendee_link,
            created_at: item.created,
            stage: stageName,
            created_by: item.created_by,
            comments: item.comments || []
          });
          return; // Skip further processing for scheduled meetings
        }

        if (taskType === 'MEETING') {

          const meetingTitle = item.meeting_recording_notes || `Meeting Recording`;

          // If meeting already has a file or link — push directly
          if (item.file) {
            allMeetings.push({
              id: item.id,
              title: meetingTitle,
              file: item.meeting_recording_link || item.file || '',
              created_at: item.created,
              stage: stageName,
              duration: item.duration || null,
              isDocument: false
            });
          }
          /* This is commented out to avoid multiple times rendering of the same meeting */
          // // Otherwise, try to match a company document from the cached data
          // else {
          //   // Use cached company documents instead of making multiple API calls
          //   const matchedDoc = companyDocs && companyDocs.length > 0 ? companyDocs.find((doc) => {
          //     if (!doc.uploaded_on) return false;
          //     const docDate = new Date(doc.uploaded_on);
          //     const itemDate = new Date(item.created);
          //     const timeDiff = Math.abs(docDate - itemDate);
          //     const nameMatch = doc.file_name?.toLowerCase().includes(item.title?.toLowerCase() || '');
          //     return timeDiff < 120000 || nameMatch;
          //   }) : null;       

          //   allMeetings.push({
          //     id: item.id,
          //     title: matchedDoc?.file_name || meetingTitle,
          //     file: matchedDoc ? matchedDoc.file : '',
          //     created_at: item.created,
          //     stage: stageName,
          //     duration: item.duration || null,
          //     isDocument: false,
          //     created_by: item.created_by,
          //     uploaded_on: matchedDoc?.uploaded_on
          //   });
          // }
        } else if (item.type === 'RECORDING' && item.file) {
          // Check if it's a video or audio recording - NOT a document
          const isVideo = /\.(mp4|avi|mov|webm|mkv)$/i.test(item.file);
          const isAudio = /\.(mp3|wav|m4a|ogg|aac|flac)$/i.test(item.file);
          const isDocument = /\.(pdf|doc|docx|xls|xlsx|ppt|pptx|txt|csv)$/i.test(item.file);
          const fileName = item.file.split('/').pop();

          if (isDocument) {
            // It's a document, not a recording
            allDocuments.push({
              id: item.id,
              name: item.file_name || fileName || 'Document',
              file: item.file,
              created_at: item.created,
              stage: stageName,
              title: item.file_name || fileName,
              created_by: item.created_by,
              uploaded_on: item.uploaded_on
            });
          } else if (isVideo) {
            allMeetings.push({
              id: item.id,
              title: item.file_name || fileName || 'Meeting Recording',
              file: item.file,
              created_at: item.created,
              stage: stageName,
              duration: item.duration || null,
              isDocument: true,
              created_by: item.created_by,
              uploaded_on: item.uploaded_on
            });
          } else if (isAudio) {
            allAudioRecordings.push({
              id: item.id,
              title: item.file_name || fileName || 'Audio Recording',
              file: item.file,
              created_at: item.created,
              stage: stageName,
              duration: item.duration || null,
              created_by: item.created_by,
              uploaded_on: item.uploaded_on
            });
          }
        } else if (item.file) {
          const fileName = item.file.split('/').pop();
          allDocuments.push({
            id: item.id,
            name: item.file_name || fileName || 'Document',
            file: item.file,
            created_at: item.created,
            stage: stageName,
            title: item.file_name || fileName,
            created_by: item.created_by,
            uploaded_on: item.uploaded_on
          });
        }
      });

      setDocuments(allDocuments);
      setMeetings(allMeetings);
      setAudioRecordings(allAudioRecordings);
      setScheduledMeetings(allScheduledMeetings);

      // Fetch text notes separately
      try {
        let notesUrl = `notes/?company_id=${id}`;
        if (dateFilter && (dateFilter.startDate || dateFilter.endDate)) {
          const notesParams = new URLSearchParams();
          if (dateFilter.startDate) notesParams.append('start_date', dateFilter.startDate);
          if (dateFilter.endDate) notesParams.append('end_date', dateFilter.endDate);
          notesUrl += `&${notesParams.toString()}`;
        }

        const notesResponse = await apiInstance.get(notesUrl, {
          headers: { Authorization: `Token ${localStorage.getItem("authToken")}` }
        });

        if (notesResponse?.data) {
          // Backend returns: { message, success, status, data: [...] }
          // So we need to access notesResponse.data.data to get the actual notes array
          const notesData = notesResponse.data.data || notesResponse.data.results || notesResponse.data;

          // Transform notes data to match our structure
          const allNotes = Array.isArray(notesData)
            ? notesData.map(note => {
              const stageObj = note.stage || note.journey_item?.stage || null;
              const stageId = note.stage_id || stageObj?.id || null;
              const stageName = note.stage_name || stageObj?.name || null;

              return {
                id: note.id,
                title: note.title,
                note_text: note.note_text,
                // backend exposes these as `created` / `modified`
                created_at: note.created || note.created_at,
                modified_at: note.modified || note.modified_at,
                stage: stageName || 'Intro Call - 10%',
                stage_id: stageId
              };
            })
            : [];
          setNotes(allNotes);
        } else {
          console.log('📝 NO NOTES DATA RECEIVED');
          setNotes([]);
        }
      } catch (noteError) {
        setNotes([]); // Set empty array if notes fetch fails
      }
    } catch (error) {

      // Check if it's a 404 or "Journey not found" error
      if (error?.response?.status === 404 || error?.response?.data?.detail?.includes('Journey not found')) {
        console.log('No journey data found for this company yet - this is normal for new companies');
        // Don't show error toast for 404 - it's expected when no documents exist
      } else if (error.message === 'Journey data request timeout') {
        showErrorToast('Loading journey data is taking longer than expected. Please refresh the page.');
      } else {
        showErrorToast('Error loading journey data');
      }

      // Set empty arrays to prevent loading state from hanging
      setJourneyData([]);
      setDocuments([]);
      setMeetings([]);
      setAudioRecordings([]);
      setScheduledMeetings([]);
    }
  };

  const getCompanies = async () => {
    try {
      // Use tasksApi for consistency
      const response = await Promise.race([
        tasksApi.getCompanies(),
        new Promise((_, reject) =>
          setTimeout(() => reject(new Error('Companies request timeout')), 8000)
        )
      ]);
      setCompanies(response?.data || []);
    } catch (error) {
      setCompanies([]);
    }
  };

  const getTasks = async () => {
    try {
      // Use tasksApi for consistency
      const response = await Promise.race([
        tasksApi.getAllTasks({ company_id: id }),
        new Promise((_, reject) =>
          setTimeout(() => reject(new Error('Tasks request timeout')), 8000)
        )
      ]);

      const data = response?.data;
      let tasks = [];

      if (Array.isArray(data)) {
        // Backward compatibility: API returned a plain list
        tasks = data;
      } else if (data && Array.isArray(data.results)) {
        // Paginated response from CustomPagination
        tasks = data.results;
      }

      setTasks(tasks || []);
    } catch (error) {
      setTasks([]);
    }
  };

  const getTasksByStages = async () => {
    try {
      const response = await Promise.race([
        tasksApi.getTasksByStages({ company_id: id }),
        new Promise((_, reject) =>
          setTimeout(() => reject(new Error('Tasks by stages timeout')), 8000)
        )
      ]);
      setTasksByStage(response?.data || {});
    } catch (error) {
      setTasksByStage({});
    }
  };

  const getStages = async () => {
    try {
      const response = await Promise.race([
        tasksApi.getStages(),
        new Promise((_, reject) =>
          setTimeout(() => reject(new Error('Stages request timeout')), 8000)
        )
      ]);
      // Show all stages without filtering
      setStages(response.data || []);
    } catch (error) {
      setStages([]);
    }
  };

  const getOfferings = async () => {
    try {
      const response = await apiInstance.get('offering/', {
        headers: { Authorization: `Token ${localStorage.getItem('authToken')}` }
      });
      setOfferings(response.data?.data || []);
    } catch (error) {
      setOfferings([]);
    }
  };

  const handleOfferingChange = async (event) => {
    const newOfferingId = event.target.value; // "" means clear the offering
    const previousOfferingId = currentOfferingId;

    // Optimistic update
    setCurrentOfferingId(newOfferingId);

    try {
      const response = await apiInstance.patch(`company/${id}/update-offering/`, {
        offering_id: newOfferingId === '' ? null : newOfferingId
      }, {
        headers: { Authorization: `Token ${localStorage.getItem('authToken')}` }
      });

      const updatedOffering = response.data?.data?.offering || null;
      setCompanyData(prev => ({ ...prev, offering: updatedOffering }));
      showToast('Offering updated successfully', 'success');
    } catch (error) {
      // Roll back optimistic update on failure
      setCurrentOfferingId(previousOfferingId);
      showErrorToast('Error updating offering: ' + (error.response?.data?.message || error.message));
    }
  };

  const getTeams = async () => {
    try {
      // Use tasksApi for consistency
      const response = await Promise.race([
        tasksApi.getTeams(),
        new Promise((_, reject) =>
          setTimeout(() => reject(new Error('Teams request timeout')), 8000)
        )
      ]);
      setTeams(response?.data || []);
    } catch (error) {
      setTeams([]);
    }
  };

  const getTeamMembers = async () => {
    try {
      // Use tasksApi for consistency
      const response = await Promise.race([
        tasksApi.getTeamMembers(),
        new Promise((_, reject) =>
          setTimeout(() => reject(new Error('Team members request timeout')), 8000)
        )
      ]);
      setTeamMembers(response?.data || []);
    } catch (error) {
      setTeamMembers([]);
    }
  };

  // Filter team members based on stage and role - for 50% and 75% stages, filter by specific teams per role
  const getFilteredTeamMembersForRole = async (stage, roleLabel) => {
    if (!stage || !roleLabel) return teamMembers;

    const stageNameLower = stage?.name?.toLowerCase() || stage?.label?.toLowerCase() || '';
    const is50PercentStage = stageNameLower.includes('nb loi + msa') || stageNameLower.includes('50%');
    const is75PercentStage = stageNameLower.includes('dd - phase') || stageNameLower.includes('75%');

    // Determine which team to filter by based on stage and role
    let targetTeamName = null;

    if (is50PercentStage && roleLabel === 'Legal Strategist') {
      // 50% stage - Legal Strategist: only Team Comm/Legal DD
      targetTeamName = 'Team Comm/Legal DD';
    } else if (is75PercentStage) {
      if (roleLabel === 'Technical Leader') {
        // 75% stage - Technical Leader: only Team Tech DD
        targetTeamName = 'Team Tech DD';
      } else if (roleLabel === 'Commercial Leader') {
        // 75% stage - Commercial Leader: only Team Comm/Legal DD
        targetTeamName = 'Team Comm/Legal DD';
      }
    }

    // If no filtering needed for this role/stage combination, return all team members
    if (!targetTeamName) {
      return teamMembers;
    }

    try {
      // Fetch teams to get team IDs for filtering
      const teamsResponse = await tasksApi.getTeams();
      const allTeams = teamsResponse?.data?.data || teamsResponse?.data || [];

      // Find the specific team we need
      const targetTeam = allTeams.find(team => team.name === targetTeamName);

      if (!targetTeam) {
        // If team not found, return all team members
        return teamMembers;
      }

      // Fetch team members
      const teamMembersResponse = await tasksApi.getTeamMembers();
      const allTeamMembers = teamMembersResponse?.data || [];

      // Get members from the target team
      const membersFromTeam = [];
      const memberIds = new Set(); // Track added member IDs to avoid duplicates

      // Include team lead
      if (targetTeam.lead) {
        const leadId = targetTeam.lead.toString();
        if (!memberIds.has(leadId)) {
          const leadMember = allTeamMembers.find(m =>
            (m.pk || m.id)?.toString() === leadId
          );
          if (leadMember) {
            membersFromTeam.push(leadMember);
            memberIds.add(leadId);
          }
        }
      }

      // Include team members
      if (targetTeam.team_members && Array.isArray(targetTeam.team_members)) {
        targetTeam.team_members.forEach(member => {
          const userId = (member.user_id || member.user || member.pk)?.toString();
          if (userId && !memberIds.has(userId)) {
            // Find the full member object from allTeamMembers
            const fullMember = allTeamMembers.find(m =>
              (m.pk || m.id)?.toString() === userId ||
              (m.user_id || m.user)?.toString() === userId
            );
            if (fullMember) {
              membersFromTeam.push(fullMember);
              memberIds.add(userId);
            } else if (member.user_name) {
              // Create a member object from team member data
              const nameParts = member.user_name.split(' ');
              membersFromTeam.push({
                pk: userId,
                first_name: nameParts[0] || '',
                last_name: nameParts.slice(1).join(' ') || '',
                email: member.email || ''
              });
              memberIds.add(userId);
            }
          }
        });
      }

      return membersFromTeam.length > 0 ? membersFromTeam : teamMembers;
    } catch (error) {
      console.warn('Error filtering team members by role:', error);
      // Return all team members if filtering fails
      return teamMembers;
    }
  };

  const getCompanyTeamAssignments = async () => {
    if (!id) return;

    try {
      const response = await apiInstance.get(`company-user-mapping/?company=${id}&page_size=1000`, {
        headers: {
          Authorization: `Token ${localStorage.getItem('authToken')}`,
          'Content-Type': 'application/json'
        }
      });

      const mappings = response.data?.results?.data || [];

      // Filter active mappings and enrich with user and role details
      const activeMappings = mappings.filter(mapping => mapping.is_active);

      setCompanyTeamAssignments(activeMappings);
    } catch (error) {
      console.error('Error fetching company team assignments:', error);
      setCompanyTeamAssignments([]);
    }
  };

  const getCallingCardData = async () => {
    if (!id) return;

    try {
      // Get all calling cards for the company
      const response = await tasksApi.getCallingCards(id);

      // Handle different response structures
      let cardData = null;
      if (response?.data?.success && response?.data?.data && response?.data?.data.length > 0) {
        cardData = response.data.data[0];
      } else if (response?.data && Array.isArray(response.data) && response.data.length > 0) {
        cardData = response.data[0];
      } else if (response?.data && !Array.isArray(response.data) && response.data.id) {
        cardData = response.data;
      }

      if (cardData) {
        setCallingCardData({
          startedYr: cardData.started_year || '',
          numberOfCustomers: cardData.number_of_customers || '',
          currentYearRev: cardData.current_year_revenue || '',
          projectedRev: cardData.projected_revenue || '',
          teamSize: cardData.team_size || '',
          noOfShareholders: cardData.number_of_shareholders || '',
          ownershipWithManagement: cardData.ownership_with_management || '',
          institutionalInvestor: cardData.institutional_investor || '',
          revenueYear1: cardData.revenue_year_1 || '',
          revenueYear2: cardData.revenue_year_2 || '',
          revenueYear3: cardData.revenue_year_3 || '',
          mrrMonthlyRecurringRevenue: cardData.mrr_monthly_recurring_revenue || '',
          arrAnnualRecurringRevenue: cardData.arr_annual_recurring_revenue || '',
          esopPlan: cardData.esop_plan || '',
          lastFundRaise: cardData.last_fund_raise || '',
          anyConvertibleSafe: cardData.any_convertible_safe || '',
          currentFundRaise: cardData.current_fund_raise || '',
          commitmentForFund: cardData.commitment_for_fund || '',
          existingInvestorParticipating: cardData.existing_investor_participating || '',
          totalExpensesPerMonth: cardData.total_expenses_per_month || '',
          utilizationOfFund: cardData.utilization_of_fund || '',
          bankLoans: cardData.bank_loans || '',
          techSalesOutsourcedBreakdown: cardData.tech_sales_outsourced_breakdown || '',
          comments: cardData.comments || ''
        });
      } else {
        // Set empty values when no calling card exists
        setCallingCardData({
          startedYr: '',
          numberOfCustomers: '',
          currentYearRev: '',
          projectedRev: '',
          teamSize: '',
          noOfShareholders: '',
          ownershipWithManagement: '',
          institutionalInvestor: '',
          revenueLast3Years: '',
          mrrMonthlyRecurringRevenue: '',
          arrAnnualRecurringRevenue: '',
          esopPlan: '',
          lastFundRaise: '',
          anyConvertibleSafe: '',
          currentFundRaise: '',
          commitmentForFund: '',
          existingInvestorParticipating: '',
          totalExpensesPerMonth: '',
          utilizationOfFund: '',
          bankLoans: '',
          techSalesOutsourcedBreakdown: ''
        });
      }
    } catch (error) {
      // Set empty values on error
      setCallingCardData({
        startedYr: '',
        numberOfCustomers: '',
        currentYearRev: '',
        projectedRev: '',
        teamSize: '',
        noOfShareholders: '',
        ownershipWithManagement: '',
        institutionalInvestor: '',
        revenueLast3Years: '',
        mrrMonthlyRecurringRevenue: '',
        arrAnnualRecurringRevenue: '',
        esopPlan: '',
        lastFundRaise: '',
        anyConvertibleSafe: '',
        currentFundRaise: '',
        commitmentForFund: '',
        existingInvestorParticipating: '',
        totalExpensesPerMonth: '',
        utilizationOfFund: '',
        bankLoans: '',
        techSalesOutsourcedBreakdown: ''
      });
    }
  };

  const getStageCallingCards = async () => {
    try {
      // Use the correct API endpoint from tasksApi
      const response = await Promise.race([
        tasksApi.getCallingCards(id),
        new Promise((_, reject) =>
          setTimeout(() => reject(new Error('Stage calling cards timeout')), 8000)
        )
      ]);
      setStageCallingCards(response?.data || {});
    } catch (error) {
      setStageCallingCards({});
    }
  };

  const saveCallingCard = async (cardData) => {
    try {
      // Use stage_id=1 as default or get from current stage
      const stageId = companyData?.last_stage_task?.stage?.id || 1;
      // Check if calling card already exists
      let response;
      let cardExists = false;

      try {
        const existingCard = await tasksApi.getCallingCards(id);

        // Check if card exists in different response structures
        if (existingCard?.data?.success && existingCard?.data?.data && existingCard?.data?.data.length > 0) {
          cardExists = true;
        } else if (existingCard?.data && Array.isArray(existingCard.data) && existingCard.data.length > 0) {
          cardExists = true;
        } else if (existingCard?.data && !Array.isArray(existingCard.data) && existingCard.data.id) {
          cardExists = true;
        }
      } catch (getError) {
        cardExists = false;
      }

      if (cardExists) {
        // Update existing calling card
        response = await tasksApi.updateCallingCard(id, cardData, stageId);
      } else {
        // Create new calling card
        response = await tasksApi.saveCallingCard(id, cardData, stageId);
      }

      // Refresh the calling card data immediately
      await getCallingCardData();

      showToast('Calling card saved successfully', 'success');
    } catch (error) {
      console.error('Error saving calling card:', error);
      console.error('Error response:', error.response?.data);
      showErrorToast('Error saving calling card: ' + (error.response?.data?.message || error.message));
    }
  };

  const getScoreCardData = async () => {
    if (!id) return;

    try {
      const response = await tasksApi.getScoreCards(id);

      let cardData = null;
      if (response?.data?.success && response?.data?.data && response?.data?.data.length > 0) {
        cardData = response.data.data[0];
      } else if (response?.data && Array.isArray(response.data) && response.data.length > 0) {
        cardData = response.data[0];
      } else if (response?.data && !Array.isArray(response.data) && response.data.id) {
        cardData = response.data;
      }

      if (cardData) {
        setScoreCardData({
          productMarketFit: cardData.product_market_fit || null,
          executionBottleneck: cardData.execution_bottleneck || null,
          gatewayOperatorLeverage: cardData.gateway_operator_leverage || null,
          founderAlignment: cardData.founder_alignment || null,
          capitalEfficiency: cardData.capital_efficiency || null,
          notes: cardData.notes || '',
          overallScore: cardData.overall_score || 0,
          overallColor: cardData.overall_color || 'N/A'
        });
      } else {
        setScoreCardData({
          productMarketFit: null,
          executionBottleneck: null,
          gatewayOperatorLeverage: null,
          founderAlignment: null,
          capitalEfficiency: null,
          notes: '',
          overallScore: 0,
          overallColor: 'N/A'
        });
      }
    } catch (error) {
      setScoreCardData({
        productMarketFit: null,
        executionBottleneck: null,
        gatewayOperatorLeverage: null,
        founderAlignment: null,
        capitalEfficiency: null,
        notes: '',
        overallScore: 0,
        overallColor: 'N/A'
      });
    }
  };

  const saveScoreCard = async (cardData) => {
    try {
      const stageId = companyData?.last_stage_task?.stage?.id || 1;
      let response;
      let cardExists = false;

      try {
        const existingCard = await tasksApi.getScoreCards(id);

        if (existingCard?.data?.success && existingCard?.data?.data && existingCard?.data?.data.length > 0) {
          cardExists = true;
        } else if (existingCard?.data && Array.isArray(existingCard.data) && existingCard.data.length > 0) {
          cardExists = true;
        } else if (existingCard?.data && !Array.isArray(existingCard.data) && existingCard.data.id) {
          cardExists = true;
        }
      } catch (getError) {
        cardExists = false;
      }

      if (cardExists) {
        response = await tasksApi.updateScoreCard(id, cardData, stageId);
      } else {
        response = await tasksApi.saveScoreCard(id, cardData, stageId);
      }

      await getScoreCardData();

      showToast('Score card saved successfully', 'success');
    } catch (error) {
      console.error('Error saving score card:', error);
      console.error('Error response:', error.response?.data);
      showErrorToast('Error saving score card: ' + (error.response?.data?.message || error.message));
    }
  };

  const getClosedDealDetails = async () => {
    try {
      const response = await Promise.race([
        tasksApi.getClosedDealDetails(id),
        new Promise((_, reject) =>
          setTimeout(() => reject(new Error('Closed deal details timeout')), 8000)
        )
      ]);
      if (response?.data?.success && response?.data?.data) {
        setClosedDealDetails(response.data.data);
      } else if (response?.data) {
        setClosedDealDetails(response.data);
      } else {
        setClosedDealDetails(null);
      }
    } catch (error) {
      setClosedDealDetails(null);
    }
  };

  const saveClosedDealDetails = async (dealData) => {

    try {
      let response;
      if (closedDealDetails) {
        // Update existing
        response = await tasksApi.updateClosedDealDetails(id, dealData);
      } else {
        // Create new
        response = await tasksApi.saveClosedDealDetails(id, dealData);
      }

      await getClosedDealDetails();
      showToast('Closed deal details saved successfully', 'success');

      // If there's a pending stage change to "Closed - 100%", now open team assignment drawer
      if (pendingStageChange) {
        const targetStage = pendingStageChange.stage;
        const stageNameLower = targetStage?.name?.toLowerCase() || '';
        const is100PercentStage = stageNameLower.includes('100') || stageNameLower.includes('closed - 100');

        if (is100PercentStage) {

          // Close deal details drawer
          setEditClosedDealOpen(false);

          // Open team assignment drawer after a small delay for smooth transition
          setTimeout(() => {
            setSelectedStage(targetStage);
            setEditTeamOpen(true);
          }, 300);

          // Keep pendingStageChange so team assignment drawer can complete the stage change
          // Don't clear pendingStageChange here - it will be cleared after team assignment
        } else {
          // For non-100% stages, complete stage change immediately (shouldn't happen but keep as fallback)
          try {
            const response = await apiInstance.patch(`company/${id}/update-stage/`, {
              stage_id: pendingStageChange.stageId
            }, {
              headers: { Authorization: `Token ${localStorage.getItem('authToken')}` }
            });

            // Update local state after successful API call
            setCompanyData(prev => {
              const updatedData = {
                ...prev,
                current_stage: pendingStageChange.stageId,
                current_stage_id: pendingStageChange.stageId,
                current_stage_name: pendingStageChange.stage?.name || pendingStageChange.stage?.label || '',
                stage: pendingStageChange.stage?.name || pendingStageChange.stage?.label || '',
                status: pendingStageChange.stage?.name || pendingStageChange.stage?.label || ''
              };

              // Also update last_stage_task if it exists
              if (prev.last_stage_task) {
                updatedData.last_stage_task = {
                  ...prev.last_stage_task,
                  stage: {
                    ...prev.last_stage_task.stage,
                    id: pendingStageChange.stageId,
                    name: pendingStageChange.stage?.name || pendingStageChange.stage?.label || '',
                    label: pendingStageChange.stage?.label || pendingStageChange.stage?.name || ''
                  }
                };
              }

              return updatedData;
            });

            showToast('Company stage updated successfully', 'success');
            setPendingStageChange(null); // Clear pending change
          } catch (stageError) {
            console.error('Error updating company stage:', stageError);
            showErrorToast('Deal details saved but failed to update stage: ' + (stageError.response?.data?.message || stageError.message));
          }
        }
      }
    } catch (error) {

      const errorMessage = error.response?.data?.message
        || error.response?.data?.error
        || error.response?.data?.detail
        || JSON.stringify(error.response?.data)
        || error.message
        || 'Unknown error occurred';

      showErrorToast('Error saving deal details: ' + errorMessage);
      throw error; // Re-throw so the UI knows it failed
    }
  };

  const saveDealFields = async (payload) => {
    // payload: { current_update, total_contract_value, status, business_type_id, tci, one_time_saas, mrr }
    // (all strings; '' clears). Deliberately omits is_stage_capture — this is a manual drawer save,
    // not a stage-gate capture, so a history row is recorded only when a financial value actually
    // changed (SC-002), and the deal_details activity log is always written (SC-006).
    await apiInstance.patch(`company/${id}/update-deal-fields/`, payload, {
      headers: { Authorization: `Token ${localStorage.getItem('authToken')}` }
    });
    await getCompanyData(); // FR-003: reflect saved values without a manual reload
    await getFinancialsHistory(); // T12/SC-002: show the new history row without a manual reload
    showToast('Deal details saved successfully', 'success');
  };

  const getCurrentStageId = () => {
    return companyData?.current_stage
  };

  // Helper function to check if a stage is 100% (dynamically)
  const isStage100Percent = (stage) => {
    if (!stage || !stage.name) return false;
    // Extract percentage from stage name dynamically
    const percentMatch = stage.name.match(/(\d+)%/);
    return percentMatch && parseInt(percentMatch[1]) === 100;
  };

  // Helper function to check if a stage is 5% (on-hold/rejection stage)
  const isStage5Percent = (stage) => {
    if (!stage || !stage.name) return false;
    const percentMatch = stage.name.match(/(\d+)%/);
    return percentMatch && parseInt(percentMatch[1]) === 5;
  };

  // Helper function to extract percentage from stage name
  const getStagePercentage = (stage) => {
    if (!stage || !stage.name) return null;
    const percentMatch = stage.name.match(/(\d+)%/);
    return percentMatch ? parseInt(percentMatch[1]) : null;
  };

  // Define valid stage progression sequence (excluding 5% - it's a special bypass stage)
  const STAGE_PROGRESSION = [0, 10, 25, 50, 75, 90, 100];

  // Function to validate stage progression
  const validateStageProgression = (currentStage, newStage) => {
    const currentPercent = getStagePercentage(currentStage);
    const newPercent = getStagePercentage(newStage);

    // If we can't determine percentages, allow the change
    if (currentPercent === null || newPercent === null) {
      return { isValid: true, message: '' };
    }

    // Allow moving to 5% from any stage (rejection/on-hold stage)
    if (newPercent === 5) {
      return { isValid: true, message: '' };
    }

    // Allow moving FROM 5% to any other stage (since 5% is a bypass/holding stage)
    if (currentPercent === 5) {
      return { isValid: true, message: '' };
    }

    // Allow moving backward (to a lower percentage)
    if (newPercent < currentPercent) {
      return { isValid: true, message: '' };
    }

    // Find the current and new stage positions in the progression
    const currentIndex = STAGE_PROGRESSION.indexOf(currentPercent);
    const newIndex = STAGE_PROGRESSION.indexOf(newPercent);

    // If stages not in progression array, allow the change
    if (currentIndex === -1 || newIndex === -1) {
      return { isValid: true, message: '' };
    }

    // Check if user is trying to skip stages (moving forward more than 1 step)
    if (newIndex > currentIndex + 1) {
      const nextStage = STAGE_PROGRESSION[currentIndex + 1];
      return {
        isValid: false,
        message: `You cannot skip stages. Please move to ${nextStage}% first before advancing to ${newPercent}%.`
      };
    }

    return { isValid: true, message: '' };
  };

  // Function to check if team assignments are complete for a stage
  const checkTeamAssignments = async (stageId, stageName) => {
    try {
      // Fetch only existing mappings (userRoles already available in state)
      const mappingsResponse = await apiInstance.get(`company-user-mapping/?company=${id}&page_size=1000`, {
        headers: {
          "Authorization": `Token ${localStorage.getItem('authToken')}`,
          'Content-Type': 'application/json'
        }
      });

      const mappings = mappingsResponse.data?.results?.data || [];

      // Get REQUIRED roles for validation (only those marked as "must")
      const stageNameLower = stageName.toLowerCase();
      let requiredRoleNames = [];
      for (const [key, config] of Object.entries(STAGE_ROLES_CONFIG)) {
        if (stageNameLower.includes(key)) {
          requiredRoleNames = config.required;
          break;
        }
      }
      // If no required roles for this stage, allow the change
      if (requiredRoleNames.length === 0) {
        return { isValid: true, missingRoles: [] };
      }

      // Safety check: if userRoles not loaded yet, block the change
      if (!userRoles || userRoles.length === 0) {
        console.warn('⚠️ User roles not loaded yet, blocking stage change');
        return { isValid: false, missingRoles: requiredRoleNames };
      }

      // Filter userRoles to get only the required ones
      const requiredRoles = userRoles.filter(role =>
        requiredRoleNames.some(reqName =>
          role.label.toLowerCase().includes(reqName.toLowerCase()) ||
          reqName.toLowerCase().includes(role.label.toLowerCase())
        )
      );

      // Check which REQUIRED roles are not assigned
      const missingRoles = [];
      const activeMappings = mappings.filter(m => m.is_active);

      requiredRoles.forEach(role => {
        const hasAssignment = activeMappings.some(mapping =>
          (mapping.role === role.id || mapping.role?.id === role.id) &&
          mapping.user &&
          mapping.is_active
        );

        if (!hasAssignment) {
          missingRoles.push(role.label);
        } else {
          // Show who is assigned
          const assignment = activeMappings.find(m =>
            (m.role === role.id || m.role?.id === role.id) && m.is_active
          );
        }
      });


      return {
        isValid: missingRoles.length === 0,
        missingRoles,
        requiredRoles: requiredRoles.map(r => r.label)
      };

    } catch (error) {
      console.error('Error checking team assignments:', error);
      // If we can't check assignments, allow the change but log the error
      return { isValid: true, missingRoles: [], error: error.message };
    }
  };

  const handleStageChange = async (event) => {
    const newStageId = event.target.value;
    const selectedStage = stages.find(s => s.id === newStageId);
    const currentStageId = getCurrentStageId();


    // Store previous stage for rollback
    if (currentStageId && currentStageId !== newStageId) {
      // Don't proceed if we're already processing a stage change
      if (pendingStageChange) {
        return;
      }
    }

    // 🔥 PRIORITY 0: Validate stage progression sequence
    const currentStage = stages.find(s => s.id === currentStageId);
    const progressionValidation = validateStageProgression(currentStage, selectedStage);

    if (!progressionValidation.isValid) {
      // Reset the dropdown to current stage
      setCurrentStageId(currentStageId);

      // Show error message
      showToast(progressionValidation.message, 'error');
      return;
    }

    // 🔥 PRIORITY 0.25: Check if moving FROM 5% stage with active reminder - requires confirmation
    if (isStage5Percent(currentStage) && companyReminder) {
      // Reset the dropdown to current stage
      setCurrentStageId(currentStageId);

      // Store pending change and show confirmation
      setPendingStageChangeFromFivePercent({ stageId: newStageId, stage: selectedStage, previousStage: currentStage });
      setReminderRemovalConfirmOpen(true);

      return; // Don't proceed - confirmation dialog will handle it
    }

    // 🔥 PRIORITY 0.5: Check if changing to 5% stage (on-hold/rejection) - requires reminder
    if (isStage5Percent(selectedStage)) {
      // Reset the dropdown to current stage
      setCurrentStageId(currentStageId);

      // Store the pending stage change
      setPendingStageChange({ stageId: newStageId, stage: selectedStage, previousStage: currentStage });

      // Open the 5% reminder dialog
      setFivePercentReminderDialogOpen(true);

      return; // Don't proceed with stage change yet - reminder dialog will handle it
    }

    // 🔥 PRIORITY 0.75: Gate a move into a >=50% stage behind the financial-capture dialog (FR-008).
    // Runs BEFORE the 100% branch so it intercepts ANY gated stage (incl. 100%, which is >=50). The
    // `!financialsCapturedRef.current` clause lets onConfirm's re-dispatch fall through into the
    // 100%/QM/team/instant branches below (composing with them, exactly like the QM-exit re-dispatch).
    if (isGatedStage(selectedStage, stagePercentMap) && !financialsCapturedRef.current) {
      setCurrentStageId(currentStageId);           // FR-011: revert dropdown while prompting
      setFinancialCaptureTarget({ stageId: newStageId, stage: selectedStage });
      setFinancialCaptureOpen(true);
      return;                                       // FR-008: block until financials confirmed
    }

    // 🔥 PRIORITY 1: Check if changing to 100% stage (dynamically) - applies from ANY stage
    // This MUST be checked FIRST before team assignments
    if (isStage100Percent(selectedStage)) {
      // Always open deal details drawer for 100% stage (for review/update)

      // Reset the dropdown to current stage
      setCurrentStageId(currentStageId);

      // Store the pending stage change
      setPendingStageChange({ stageId: newStageId, stage: selectedStage });

      // Open the deal details drawer
      setEditClosedDealOpen(true);

      return; // Don't proceed with stage change yet - deal details drawer will handle next step
    }

    // 🔥 PRIORITY 1.5: QM-exit note — prompt the first time a company moves FORWARD out
    // of Qualified Meetings (FR-012). Skip when already captured (FR-013). The
    // `!qmExitCommentToSubmitRef.current` clause lets the re-dispatch fall through into
    // the intro-call / team / instant branches below (composing with them).
    const srcStage = stages.find(s => s.id === currentStageId);
    const srcIdx = stages.findIndex(s => s.id === currentStageId);
    const tgtIdx = stages.findIndex(s => s.id === newStageId);
    const isForwardOutOfQM = srcStage?.name === 'Qualified Meetings' && srcIdx !== -1 && tgtIdx !== -1 && tgtIdx > srcIdx;
    const alreadyCaptured = !!(companyData?.qm_exit_comment || '').trim();
    if (isForwardOutOfQM && !alreadyCaptured && !qmExitCommentToSubmitRef.current) {   // FR-012/FR-013
      setCurrentStageId(currentStageId);           // reset dropdown while prompting
      setPendingQmExitTarget({ stageId: newStageId, stage: selectedStage });
      setQmExitCommentInput('');
      setQmExitCommentDialogOpen(true);
      return;                                       // FR-012: block until comment entered
    }

    // 🔥 PRIORITY 2: Check team assignments for the new stage (runs AFTER deal details check)
    if (currentStageId && currentStageId !== newStageId) {
      const teamValidation = await checkTeamAssignments(newStageId, selectedStage?.name || selectedStage?.label);

      // Check if this stage has any suggested roles (show drawer for verification)
      const stageNameLower = selectedStage?.name?.toLowerCase() || '';

      // 🔥 SPECIAL CASE: For "Intro Call - 10%" stage, open task assignment drawer instead
      if (stageNameLower.includes('intro call') || stageNameLower.includes('10%')) {
        // Reset the dropdown to current stage (drawer will handle the change)
        setCurrentStageId(currentStageId);

        // Open task assignment drawer for the new stage
        setSelectedStage(selectedStage);
        setTaskAssignmentDrawerOpen(true);

        // Store the target stage for the drawer to complete after assignment
        setPendingStageChange({ stageId: newStageId, stage: selectedStage });

        return; // Don't proceed with stage change yet - let drawer handle it
      }

      let hasSuggestedRoles = false;
      for (const [key, config] of Object.entries(STAGE_ROLES_CONFIG)) {
        if (stageNameLower.includes(key) && config.suggested.length > 0) {
          hasSuggestedRoles = true;
          break;
        }
      }

      // Always open drawer if stage has roles (for verification/update)
      if (hasSuggestedRoles) {
        // Reset the dropdown to current stage (drawer will handle the change)
        setCurrentStageId(currentStageId);

        // Open team assignment drawer for the new stage
        setSelectedStage(selectedStage);
        setEditTeamOpen(true);

        // Store the target stage for the drawer to complete after assignment
        setPendingStageChange({ stageId: newStageId, stage: selectedStage });

        // Log missing roles for debugging, but don't show toast
        // Inline validation in drawer will show errors for each role
        if (!teamValidation.isValid && teamValidation.missingRoles.length > 0) {
          const missingRolesList = teamValidation.missingRoles.join(', ');
        }

        return; // Don't proceed with stage change yet - let drawer handle it
      } else {
        console.log('✅ No team roles for this stage, proceeding with stage change');
      }
    }

    try {
      // Use the proper update-stage endpoint
      const response = await apiInstance.patch(`company/${id}/update-stage/`, {
        stage_id: newStageId,
        ...(qmExitCommentToSubmitRef.current ? { comment: qmExitCommentToSubmitRef.current } : {})
      }, {
        headers: { Authorization: `Token ${localStorage.getItem('authToken')}` }
      });

      // Update local state after successful API call - ensure we update all possible stage references
      setCompanyData(prev => {
        const updatedData = {
          ...prev,
          current_stage: newStageId,
          current_stage_id: newStageId,
          current_stage_name: selectedStage?.name || selectedStage?.label || '',
          stage: selectedStage?.name || selectedStage?.label || '',
          status: selectedStage?.name || selectedStage?.label || ''
        };

        // Also update last_stage_task if it exists
        if (prev.last_stage_task) {
          updatedData.last_stage_task = {
            ...prev.last_stage_task,
            stage: {
              ...prev.last_stage_task.stage,
              id: newStageId,
              name: selectedStage?.name || selectedStage?.label || '',
              label: selectedStage?.label || selectedStage?.name || ''
            }
          };
        }

        return updatedData;
      });

      // US-4: persist the QM-exit note locally so we don't prompt again, then clear the ref (FR-013)
      if (qmExitCommentToSubmitRef.current) {
        setCompanyData(prev => ({ ...prev, qm_exit_comment: prev.qm_exit_comment || qmExitCommentToSubmitRef.current }));
        qmExitCommentToSubmitRef.current = '';
      }
      financialsCapturedRef.current = false; // B1/FR-012: re-arm the financial-capture gate so the NEXT gated move re-prompts

      showToast('Company stage updated successfully', 'success');
      setPendingStageChange(null); // Clear pending change after success

    } catch (error) {
      console.error('Error updating company stage:', error);
      qmExitCommentToSubmitRef.current = ''; // Clear stale QM-exit note so a failed move can't persist it later
      financialsCapturedRef.current = false; // B1/FR-012: re-arm the financial-capture gate after a failed move
      showErrorToast('Error updating company stage: ' + (error.response?.data?.message || error.message));
    }
  };

  // QM-exit note: confirm → stash the note in the ref, then re-dispatch the stage change so it
  // composes with the normal intro-call / team / instant branches (ref clears on PATCH success).
  const handleQmExitConfirm = () => {
    if (!qmExitCommentInput.trim() || !pendingQmExitTarget) return;
    qmExitCommentToSubmitRef.current = qmExitCommentInput.trim();
    setQmExitCommentDialogOpen(false);
    handleStageChange({ target: { value: pendingQmExitTarget.stageId } });
    setPendingQmExitTarget(null);
  };

  // QM-exit note: cancel → abort the move entirely and clear state/ref (FR-017)
  const handleQmExitCancel = () => {
    setQmExitCommentDialogOpen(false);
    setPendingQmExitTarget(null);
    setQmExitCommentInput('');
    qmExitCommentToSubmitRef.current = '';
    financialsCapturedRef.current = false; // B1/FR-012: re-arm the financial-capture gate on cancel
  };

  // B1 financial capture: confirm → persist TCI/TCV via update-deal-fields, refresh company data, set the
  // ref, then RE-DISPATCH the stage change so it composes with the normal 100%/team/QM/instant branches
  // (mirrors handleQmExitConfirm; the ref clears on the update-stage PATCH success/catch). On PATCH failure
  // (FR-010) keep the dialog open and do NOT set the ref or re-dispatch — the move is aborted.
  const handleFinancialCaptureConfirm = async (values) => {
    if (!financialCaptureTarget) return;
    const targetStageId = financialCaptureTarget.stageId;
    try {
      await apiInstance.patch(`company/${id}/update-deal-fields/`, {
        tci: values.tci,
        total_contract_value: values.total_contract_value,
        one_time_saas: values.one_time_saas,
        mrr: values.mrr,
        stage_id: targetStageId,
        is_stage_capture: true,
      }, {
        headers: { Authorization: `Token ${localStorage.getItem('authToken')}` }
      });
      await getCompanyData();                        // reflect saved financials without a manual reload
      financialsCapturedRef.current = true;          // let the re-dispatch skip this gate (FR-008)
      setFinancialCaptureOpen(false);
      handleStageChange({ target: { value: targetStageId } }); // compose with downstream branches
      setFinancialCaptureTarget(null);
    } catch (error) {
      // FR-010: save-fail aborts the move — keep dialog open, do NOT set the ref or re-dispatch
      showErrorToast('Error saving deal financials: ' + (error.response?.data?.message || error.message));
    }
  };

  // B1 financial capture: cancel → abort the move (dropdown already reverted at gate) and clear target (FR-011)
  const handleFinancialCaptureCancel = () => {
    setFinancialCaptureOpen(false);
    setFinancialCaptureTarget(null);
  };

  // Base URL for API calls
  const baseURL = `${constants.HTTP_METHOD}${constants.HTTP_URL}${constants.HTTP_PORT}/api/v1`;

  // Fetch company's active reminder
  const fetchCompanyReminder = async () => {
    try {
      const response = await apiInstance.get(
        `${baseURL}/stage-reminders/by-company/${id}/`,
        { headers: { Authorization: `Token ${localStorage.getItem('authToken')}` } }
      );
      console.log('Fetched reminder response:', response?.data);
      if (response?.data?.success && response?.data?.data) {
        const reminderData = response.data.data;
        console.log('Setting companyReminder with ID:', reminderData?.id);
        setCompanyReminder(reminderData);
      }
    } catch (error) {
      // No active reminder, that's fine
      setCompanyReminder(null);
    }
  };

  // Handle 5% reminder save
  const handleFivePercentReminderSave = async (reminderData) => {
    if (!pendingStageChange) return;

    const { stageId, stage, previousStage } = pendingStageChange;
    const isAlreadyAt5Percent = previousStage?.id === stageId; // Company is already at 5%, just adding reminder

    try {
      // Create the reminder via API and capture the response
      const createResponse = await apiInstance.post(
        `${baseURL}/stage-reminders/`,
        {
          company_id: id,
          previous_stage_id: previousStage?.id || null,
          target_stage_id: stage?.id || null,
          note: reminderData.note,
          reminder_date: reminderData.reminderDate,
        },
        { headers: { Authorization: `Token ${localStorage.getItem('authToken')}` } }
      );
      
      // Get the created reminder data with ID
      const createdReminder = createResponse?.data?.data || createResponse?.data;
      console.log('Created reminder with ID:', createdReminder?.id);

      // Only update stage if we're actually changing stages (not just adding reminder)
      if (!isAlreadyAt5Percent) {
        await apiInstance.patch(`company/${id}/update-stage/`, {
          stage_id: stageId
        }, {
          headers: { Authorization: `Token ${localStorage.getItem('authToken')}` }
        });

        // Update local state for stage change
        setCompanyData(prev => ({
          ...prev,
          current_stage: stageId,
          current_stage_id: stageId,
          stage: stageId,
          current_stage_name: stage?.name || stage?.label || '',
          last_stage_task: prev.last_stage_task ? {
            ...prev.last_stage_task,
            stage: { ...prev.last_stage_task.stage, id: stageId, name: stage?.name, label: stage?.label }
          } : prev.last_stage_task
        }));
      }

      // Store the reminder locally with ID from API
      setCompanyReminder({
        id: createdReminder?.id,
        company: id,
        note: reminderData.note,
        reminder_date: reminderData.reminderDate,
        is_active: true
      });

      showToast(isAlreadyAt5Percent ? 'Reminder set successfully' : 'Company moved to 5% with reminder set', 'success');
      setFivePercentReminderDialogOpen(false);
      setPendingStageChange(null);

    } catch (error) {
      console.error('Error saving 5% reminder:', error);
      showErrorToast('Failed to save reminder. Please try again.');
    }
  };

  // Handle 5% reminder cancel
  const handleFivePercentReminderCancel = () => {
    setFivePercentReminderDialogOpen(false);
    setPendingStageChange(null);
  };

  // Handle 5% newsletter only save
  const handleFivePercentNewsletterSave = async ({ note }) => {
    if (!pendingStageChange) return;

    const { stageId, stage, previousStage } = pendingStageChange;

    try {
      // First, mark the company for newsletter only
      const newsletterResponse = await apiInstance.post(
        `${baseURL}/company/${id}/mark-newsletter/`,
        { note },
        { headers: { Authorization: `Token ${localStorage.getItem('authToken')}` } }
      );

      if (!newsletterResponse?.data?.success) {
        throw new Error(newsletterResponse?.data?.message || 'Failed to mark for newsletter');
      }

      // Then update the company stage to 5%
      await apiInstance.patch(`company/${id}/update-stage/`, {
        stage_id: stageId
      }, {
        headers: { Authorization: `Token ${localStorage.getItem('authToken')}` }
      });

      // Update local state for stage change
      setCompanyData(prev => ({
        ...prev,
        current_stage: stageId,
        current_stage_id: stageId,
        stage: stageId,
        current_stage_name: stage?.name || stage?.label || '',
        is_newsletter_only: true,
        newsletter_details: newsletterResponse.data.data,
        last_stage_task: prev.last_stage_task ? {
          ...prev.last_stage_task,
          stage: { ...prev.last_stage_task.stage, id: stageId, name: stage?.name, label: stage?.label }
        } : prev.last_stage_task
      }));

      showToast('Company moved to 5% and marked for newsletter only', 'success');
      setFivePercentReminderDialogOpen(false);
      setPendingStageChange(null);

    } catch (error) {
      console.error('Error marking newsletter only:', error);
      showErrorToast(error?.response?.data?.message || 'Failed to mark for newsletter. Please try again.');
    }
  };

  // Handle viewing reminder
  const handleViewReminder = () => {
    setReminderNoteDialogOpen(true);
  };

  // Handle editing reminder
  const handleEditReminder = async (reminderId, updateData) => {
    try {
      await apiInstance.patch(
        `${baseURL}/stage-reminders/${reminderId}/`,
        updateData,
        { headers: { Authorization: `Token ${localStorage.getItem('authToken')}` } }
      );
      showToast('Reminder updated successfully', 'success');
      fetchCompanyReminder();
      return true;
    } catch (error) {
      console.error('Error updating reminder:', error);
      showErrorToast('Failed to update reminder');
      return false;
    }
  };

  // Handle deactivating reminder
  const handleDeactivateReminder = async (reminderId) => {
    try {
      await apiInstance.post(
        `${baseURL}/stage-reminders/${reminderId}/deactivate/`,
        {},
        { headers: { Authorization: `Token ${localStorage.getItem('authToken')}` } }
      );
      setCompanyReminder(null);
      showToast('Reminder deactivated', 'success');
    } catch (error) {
      console.error('Error deactivating reminder:', error);
      showErrorToast('Failed to deactivate reminder');
    }
  };

  // Handle reminder removal confirmation (when moving from 5% to another stage)
  const handleReminderRemovalConfirm = async () => {
    if (!pendingStageChangeFromFivePercent || !companyReminder) return;
    
    const { stageId, stage } = pendingStageChangeFromFivePercent;
    
    try {
      // Deactivate the reminder first
      if (companyReminder.id) {
        await apiInstance.post(
          `${baseURL}/stage-reminders/${companyReminder.id}/deactivate/`,
          {},
          { headers: { Authorization: `Token ${localStorage.getItem('authToken')}` } }
        );
      }
      
      // Clear reminder state
      setCompanyReminder(null);
      
      // Now proceed with stage change
      const response = await apiInstance.patch(`company/${id}/update-stage/`, {
        stage_id: stageId
      }, {
        headers: { Authorization: `Token ${localStorage.getItem('authToken')}` }
      });

      // Update local state
      setCompanyData(prev => ({
        ...prev,
        current_stage: stageId,
        current_stage_id: stageId,
        current_stage_name: stage?.name || stage?.label || '',
        stage: stage?.name || stage?.label || '',
        status: stage?.name || stage?.label || ''
      }));

      showToast(`Reminder removed. Company moved to ${stage?.name}`, 'success');
    } catch (error) {
      console.error('Error removing reminder and changing stage:', error);
      showErrorToast('Failed to update. Please try again.');
    } finally {
      setReminderRemovalConfirmOpen(false);
      setPendingStageChangeFromFivePercent(null);
    }
  };

  // Handle reminder removal cancel
  const handleReminderRemovalCancel = () => {
    setReminderRemovalConfirmOpen(false);
    setPendingStageChangeFromFivePercent(null);
  };

  useEffect(() => {
    if (id) {
      getBusinessTypeOptions(); // FR-007: non-blocking; own catch leaves options [] on failure
      // Load critical data first
      const loadCriticalData = async () => {
        try {
          setLoadingProgress(20);
          await getStages(); // Load stages first
          setLoadingProgress(40);
          await getCompanyData(); // Then load company data
          setLoadingProgress(60);
        } catch (error) {
          console.error('Error loading critical data:', error);
          // Set fallback data to prevent complete failure
          if (!companyData?.id) {
            setCompanyData({ company_name: 'Company', id: id });
          }
          if (stages.length === 0) {
            setStages([{ id: 1, name: 'INTRODUCTION' }]);
          }
          setIsLoading(false);
        }
      };

      // Load non-critical data after a delay
      const loadSecondaryData = async () => {
        try {
          setLoadingProgress(80);
          const results = await Promise.allSettled([
            getCompanies().catch(e => {
              console.warn('Companies API failed:', e.message);
              setCompanies([]);
            }),
            getTasks().catch(e => {
              console.warn('Tasks API failed:', e.message);
              setTasks([]);
            }),
            getTeams().catch(e => {
              console.warn('Teams API failed:', e.message);
              setTeams([]);
            }),
            getTeamMembers().catch(e => {
              console.warn('TeamMembers API failed:', e.message);
              setTeamMembers([]);
            }),
            getCompanyTeamAssignments().catch(e => {
              console.warn('CompanyTeamAssignments API failed:', e.message);
              setCompanyTeamAssignments([]);
            }),
            getTasksByStages().catch(e => {
              console.warn('TasksByStages API failed:', e.message);
              setTasksByStage({});
            }),
            getStageCallingCards().catch(e => {
              console.warn('StageCallingCards API failed:', e.message);
              setStageCallingCards({});
            }),
            getClosedDealDetails().catch(e => {
              console.warn('ClosedDealDetails API failed:', e.message);
              setClosedDealDetails(null);
            }),
            fetchCompanyReminder().catch(e => {
              console.warn('CompanyReminder API failed:', e.message);
              setCompanyReminder(null);
            }),
            fetchInvalidDetails().catch(e => {
              console.warn('InvalidDetails API failed:', e.message);
              setInvalidDetails(null);
            }),
            fetchDndDetails().catch(e => {
              console.warn('DndDetails API failed:', e.message);
              setDndDetails(null);
            }),
            fetchArchiveDetails().catch(e => {
              console.warn('ArchiveDetails API failed:', e.message);
              setArchiveDetails(null);
            })
          ]);

          // Count successful vs failed requests
          const successful = results.filter(r => r.status === 'fulfilled').length;
          const failed = results.filter(r => r.status === 'rejected').length;

          setLoadingProgress(100);
          setTimeout(() => setIsLoading(false), 300);
        } catch (error) {
          console.error('Error loading secondary data:', error);
          setIsLoading(false);
        }
      };

      loadCriticalData().then(() => {
        const secondaryTimeoutId = setTimeout(loadSecondaryData, 100);
        return () => clearTimeout(secondaryTimeoutId);
      }).catch(() => {
        setIsLoading(false);
      });

      // Failsafe: Force stop loading after 30 seconds
      const timeoutId = setTimeout(() => {
        setIsLoading(prevLoading => {
          if (prevLoading) {
            console.warn('Force stopping loading after 30 seconds');
            showErrorToast('Some data may not have loaded completely. Please refresh if needed.');
            return false;
          }
          return prevLoading;
        });
      }, 30000);

      // Cleanup timeout on unmount
      return () => {
        clearTimeout(timeoutId);
      };
    }
  }, [id]);

  useEffect(() => {
    let journeyTimeoutId;

    if (userInfo?.active_role && userInfo?.permissions?.includes("can_view_journey") && companyData?.id) {
      // Only load journey data after company data is loaded
      journeyTimeoutId = setTimeout(() => {
        getJourneyData().catch(error => {
          console.error('Journey data failed to load:', error);
          // Continue with empty data rather than hanging
        });
      }, 200);
    }

    // Expose function globally for date filtering
    window.getJourneyDataWithFilter = (dateFilter) => {
      getJourneyData(dateFilter);
    };

    return () => {
      if (journeyTimeoutId) {
        clearTimeout(journeyTimeoutId);
      }
      delete window.getJourneyDataWithFilter;
    };
  }, [userInfo, companyData?.id]);

  // Debounce company search input
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setDebouncedCompanySearch(companySearchInput);
    }, 500); // 500ms debounce

    return () => clearTimeout(timeoutId);
  }, [companySearchInput]);

  // Search companies when debounced search term changes
  useEffect(() => {
    if (debouncedCompanySearch) {
      searchCompanies(debouncedCompanySearch);
    } else {
      setCompanyOptions([]);
    }
  }, [debouncedCompanySearch]);

  // Initialize company search input with current company name
  useEffect(() => {
    if (companyData?.company_name) {
      setCompanySearchInput(companyData.company_name);
    }
  }, [companyData?.id, companyData?.company_name]);

  // Fetch outreach details when company data is loaded and it's a 0% stage company
  useEffect(() => {
    if (companyData?.id) {
      fetchOutreachDetails();
    }
  }, [companyData?.id, companyData?.current_stage_name, currentStage]);

  // Fetch user roles once on component mount
  useEffect(() => {
    const fetchUserRoles = async () => {
      try {
        const rolesResponse = await apiInstance.get('user-role-type/', {
          headers: {
            "Authorization": `Token ${localStorage.getItem('authToken')}`,
            'Content-Type': 'application/json'
          }
        });
        const roles = rolesResponse.data?.data || [];
        setUserRoles(roles.sort((a, b) => a.order - b.order));
      } catch (error) {
        console.error('Error fetching user roles:', error);
        setUserRoles([]); // Set empty array on error to prevent undefined issues
      }
    };

    fetchUserRoles();
  }, []); // Empty dependency array - fetch only once on mount

  // Set current_stage_id after both companyData and stages are loaded
  useEffect(() => {
    if (companyData?.id && stages.length > 0 && !companyData.current_stage_id) {
      const stageName = companyData.current_stage_name || companyData.stage || companyData.status;
      if (stageName) {
        const foundStage = stages.find(s => {
          const match = s.name === stageName || s.label === stageName ||
            s.name.toLowerCase() === stageName.toLowerCase() ||
            s.label?.toLowerCase() === stageName.toLowerCase();
          return match;
        });
        if (foundStage) {
          setCompanyData(prev => ({ ...prev, current_stage_id: foundStage.id }));
        } else {
          console.log('✗ No matching stage found for:', stageName);
        }
      }
    }
  }, [companyData?.id, stages, companyData?.current_stage_name, companyData?.stage, companyData?.status]);

  useEffect(() => {
    let callingCardTimeoutId;

    if (companyData && companyData.id) {
      // Delay calling card data loading to prevent blocking
      callingCardTimeoutId = setTimeout(() => {
        getCallingCardData();
      }, 500);
    }

    return () => {
      if (callingCardTimeoutId) {
        clearTimeout(callingCardTimeoutId);
      }
    };
  }, [companyData]);

  // Add a separate effect to refresh calling card data when the edit drawer closes
  useEffect(() => {
    let refreshTimeoutId;

    if (!editCallingCardOpen && companyData && companyData.id) {
      // Refresh calling card data when drawer closes
      refreshTimeoutId = setTimeout(() => {
        getCallingCardData();
      }, 300);
    }

    return () => {
      if (refreshTimeoutId) {
        clearTimeout(refreshTimeoutId);
      }
    };
  }, [editCallingCardOpen, companyData]);

  // Load score card data when company data is available or when either drawer closes
  useEffect(() => {
    let scoreCardTimeoutId;

    if (companyData && companyData.id) {
      scoreCardTimeoutId = setTimeout(() => {
        getScoreCardData();
      }, 500);
    }

    return () => {
      if (scoreCardTimeoutId) {
        clearTimeout(scoreCardTimeoutId);
      }
    };
  }, [companyData, editScoreCardOpen, viewScoreCardOpen]);

  useEffect(() => {

    if (companyData?.current_stage && stages.length > 0) {
      setCurrentStageId(companyData?.current_stage || "");
    }
  }, [companyData, stages]);

  // Load the Offerings (categories) list once on mount
  useEffect(() => {
    getOfferings();
  }, []);

  // Keep the Offering dropdown in sync with the loaded company
  useEffect(() => {
    setCurrentOfferingId(companyData?.offering?.id || "");
  }, [companyData]);

  // T12 (FR-017/SC-002): (re)fetch the financial change-history list whenever the Deal Details
  // drawer is opened in view mode, so it's fresh on every open without a manual reload.
  useEffect(() => {
    if (viewClosedDealOpen && companyData?.id) {
      getFinancialsHistory();
    }
  }, [viewClosedDealOpen, companyData?.id]);

  const formatDateTime = (dateString, timezone) => {
    if (!dateString) return '';
    
    // Parse datetime string directly to avoid timezone conversion issues
    // Format: "2024-02-14T09:00:00" or "2024-02-14T09:00:00+05:30"
    const match = dateString.match(/(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/);
    if (match) {
      const [, year, month, day, hours, minutes] = match;
      const hour = parseInt(hours);
      const period = hour >= 12 ? 'PM' : 'AM';
      const hour12 = hour % 12 || 12;
      const tzLabel = timezone === 'Asia/Kolkata' ? 'IST' : (timezone || 'IST');
      return `${day}-${month}-${year}, ${String(hour12).padStart(2, '0')}:${minutes} ${period} ${tzLabel}`;
    }
    
    // Fallback to original method
    return `${getDDMMYYYYFormat(dateString)}, ${getHHMMFormat(dateString)}`;
  };


  const handleTaskStatusChange = async (taskId, newStatus) => {
    try {
      await tasksApi.updateTaskStatus(taskId, newStatus);
      // Refresh tasks data
      await Promise.allSettled([
        getTasks(),
        getTasksByStages()
      ]);
      showToast('Task status updated successfully', 'success');
    } catch (error) {
      console.error('Error updating task status:', error);
      showErrorToast('Error updating task status');
    }
  };

  // Closed Deal Details Drawer Component
  const handleAIPitchClick = () => {
    setEditCompanyOpen(true);
    setAddCompanyDefaultTab('pitch');
  };

  const [addCompanyDefaultTab, setAddCompanyDefaultTab] = useState(null);

  // View Field Component for Deal Details - auto-sizes based on content
  const DealDetailsViewField = ({ label, value, compact = false, showEmpty = false }) => {
    const displayValue = value || (showEmpty ? '-' : null);
    if (!displayValue && !showEmpty) return null;
    
    if (compact) {
      return (
        <Box sx={{ p: 1.5, backgroundColor: '#f8f9fa', borderRadius: 1.5, border: '1px solid #e9ecef', height: 'auto', minHeight: 50 }}>
          <Typography variant="caption" sx={{ color: '#6c757d', fontWeight: 500, fontSize: '0.7rem', display: 'block', mb: 0.5 }}>
            {label}
          </Typography>
          <Typography variant="body2" sx={{ color: displayValue === '-' ? '#adb5bd' : '#212529', fontWeight: 500, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
            {displayValue}
          </Typography>
        </Box>
      );
    }
    
    if (!value) return null;
    
    return (
      <Box sx={{ mb: 2, p: 2, backgroundColor: '#f8f9fa', borderRadius: 2, border: '1px solid #e9ecef' }}>
        <Typography variant="caption" sx={{ color: '#6c757d', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', display: 'block', mb: 0.5 }}>
          {label}
        </Typography>
        <Typography variant="body1" sx={{ color: '#212529', whiteSpace: 'pre-wrap', wordBreak: 'break-word', lineHeight: 1.6 }}>
          {value}
        </Typography>
      </Box>
    );
  };

  function ClosedDealDetailsDrawer({ closedDealDetails, onClose, onSave, isStageChangeValidation, mode = 'edit', onEdit }) {
    const isViewMode = mode === 'view';
    const [formData, setFormData] = useState({
      tech_team_count: '',
      engagement_period: '',
      total_cash_investment: '',
      equity_conversion_investment: '',
      valuation_at_investing: '',
      equity_percentage: '',
      risk_level: 'B',
      deal_date: '',
      exit_strategy: '',
      lead_gen_eng: '',
      marketing_eng: '',
      governanace_eng: ''
    });
    const [isSaving, setIsSaving] = useState(false);
    const [errors, setErrors] = useState({});
    const [dealFields, setDealFields] = useState({ current_update: '', total_contract_value: '', status: '', business_type_id: '', tci: '', one_time_saas: '', mrr: '' });

    useEffect(() => {
      setDealFields({
        current_update: companyData?.current_update || '',
        total_contract_value: companyData?.total_contract_value ?? '',
        status: companyData?.status || '',
        business_type_id: companyData?.business_type?.id ?? '',
        tci: companyData?.tci ?? '',
        one_time_saas: companyData?.one_time_saas ?? '',
        mrr: companyData?.mrr ?? '',
      });
    }, [companyData]);

    // FR-014: mark TCI as required (UI hint only) when the company's CURRENT stage is gated
    // (>=50%, via the same stagePercentMap/isGatedStage used for the stage-change gate at line
    // 289/2894). The server (update-deal-fields, T4) is the authoritative enforcer of "cannot
    // clear TCI/TCV at a gated stage" — this is cheap best-effort UI feedback, not a hard block
    // sourced from a separate lookup, so we do NOT force TCI required unconditionally.
    const dealDetailsCurrentStage = stages.find(s => s.id === getCurrentStageId());
    const isTciRequiredHint = isGatedStage(dealDetailsCurrentStage, stagePercentMap);

    useEffect(() => {
      if (closedDealDetails) {
        setFormData({
          tech_team_count: closedDealDetails.tech_team_count || '',
          engagement_period: closedDealDetails.engagement_period || '',
          total_cash_investment: closedDealDetails.total_cash_investment || '',
          equity_conversion_investment: closedDealDetails.equity_conversion_investment || '',
          valuation_at_investing: closedDealDetails.valuation_at_investing || '',
          equity_percentage: closedDealDetails.equity_percentage || '',
          risk_level: closedDealDetails.risk_level || 'B',
          deal_date: closedDealDetails.deal_date || '',
          exit_strategy: closedDealDetails.exit_strategy || '',
          lead_gen_eng: closedDealDetails.lead_gen_eng || '',
          marketing_eng: closedDealDetails.marketing_eng || '',
          governanace_eng: closedDealDetails.governanace_eng || ''
        });
      }
    }, [closedDealDetails]);

    const validateForm = () => {
      const newErrors = {};
      if (!formData.total_cash_investment?.trim()) {
        newErrors.total_cash_investment = 'Cash Investment is required';
      }
      if (!formData.equity_percentage?.trim()) {
        newErrors.equity_percentage = 'Equity Percentage is required';
      }
      setErrors(newErrors);
      return Object.keys(newErrors).length === 0;
    };

    const handleSave = async () => {
      if (isStageChangeValidation) {
        if (!validateForm()) {
          showErrorToast('Please fill in all required fields');
          return;
        }

        setIsSaving(true);
        try {
          // Prepare payload - convert empty strings to null for date fields
          const payload = {
            tech_team_count: formData.tech_team_count || null,
            engagement_period: formData.engagement_period || null,
            total_cash_investment: formData.total_cash_investment || null,
            equity_conversion_investment: formData.equity_conversion_investment || null,
            valuation_at_investing: formData.valuation_at_investing || null,
            equity_percentage: formData.equity_percentage || null,
            risk_level: formData.risk_level,
            deal_date: formData.deal_date || null, // Convert empty string to null
            exit_strategy: formData.exit_strategy || null,
            lead_gen_eng: formData.lead_gen_eng || null,
            marketing_eng: formData.marketing_eng || null,
            governanace_eng: formData.governanace_eng || null
          };


          await onSave(payload);

          // For 100% stage changes, parent will handle closing and opening team drawer
          // For normal edits (no stage change), close the drawer here
          if (!isStageChangeValidation) {
            onClose();
          }
        } catch (error) {
          console.error('Error in handleSave:', error);
          // Don't close the drawer if there was an error
        } finally {
          setIsSaving(false);
        }
      } else {
        setIsSaving(true);
        try {
          await saveDealFields({
            current_update: dealFields.current_update ?? '',
            total_contract_value: dealFields.total_contract_value ?? '',
            status: dealFields.status ?? '',
            business_type_id: dealFields.business_type_id ?? '',
            tci: dealFields.tci ?? '',
            one_time_saas: dealFields.one_time_saas ?? '',
            mrr: dealFields.mrr ?? '',
          });
          onClose();
        } catch (error) {
          showErrorToast('Error saving deal details: ' + (error.response?.data?.message || error.message));
        } finally {
          setIsSaving(false);
        }
      }
    };

    // Check if required fields are filled
    const isFormValid = isStageChangeValidation ? (formData.total_cash_investment && formData.equity_percentage) : true;

    // Get risk level label
    const getRiskLevelLabel = (value) => {
      const levels = { 'A': 'Level A - Safe investment', 'B': 'Level B - Moderate risk', 'C': 'Level C - High risk' };
      return levels[value] || value || '-';
    };

    // View Mode Render
    if (isViewMode) {
      return (
        <Box sx={{ width: 450, height: '100vh', display: 'flex', flexDirection: 'column' }}>
          <Box sx={{ p: 2.5, borderBottom: '1px solid #e0e0e0', backgroundColor: '#fafafa' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>Deal Details</Typography>
              <IconButton onClick={onClose} size="small">
                <CloseIcon />
              </IconButton>
            </Box>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>Investment and engagement overview</Typography>
          </Box>
          
          <Box sx={{ 
            flex: 1, 
            p: 2.5, 
            overflowY: 'auto',
            '&::-webkit-scrollbar': { width: '6px' },
            '&::-webkit-scrollbar-track': { background: '#f1f1f1', borderRadius: '10px' },
            '&::-webkit-scrollbar-thumb': { background: '#c1c1c1', borderRadius: '10px' }
          }}>
            {/* Deal Snapshot Section (manual company deal fields) */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle1" sx={{ mb: 1.5, fontSize: '0.95rem', fontWeight: 600, color: '#1976d2' }}>
                📄 Deal Snapshot
              </Typography>
              <Grid container spacing={1.5}>
                <Grid item xs={12}><DealDetailsViewField label="Current Update" value={companyData?.current_update} compact showEmpty /></Grid>
                <Grid item xs={12}><DealDetailsViewField label="TCV" value={companyData?.total_contract_value ? `${companyData?.currency?.label ? companyData.currency.label + ' ' : ''}${companyData.total_contract_value}` : ''} compact showEmpty /></Grid>
                <Grid item xs={12}><DealDetailsViewField label="TCI" value={companyData?.tci ? `${companyData?.currency?.label ? companyData.currency.label + ' ' : ''}${companyData.tci}` : ''} compact showEmpty /></Grid>
                <Grid item xs={12}><DealDetailsViewField label="OneTime (SaaS)" value={companyData?.one_time_saas ? `${companyData?.currency?.label ? companyData.currency.label + ' ' : ''}${companyData.one_time_saas}` : ''} compact showEmpty /></Grid>
                <Grid item xs={12}><DealDetailsViewField label="MRR" value={companyData?.mrr ? `${companyData?.currency?.label ? companyData.currency.label + ' ' : ''}${companyData.mrr}` : ''} compact showEmpty /></Grid>
                <Grid item xs={12}><DealDetailsViewField label="ARR" value={companyData?.arr ? `${companyData?.currency?.label ? companyData.currency.label + ' ' : ''}${companyData.arr}` : ''} compact showEmpty /></Grid>
                <Grid item xs={12}><DealDetailsViewField label="Status" value={companyData?.status} compact showEmpty /></Grid>
                <Grid item xs={12}><DealDetailsViewField label="Business Type" value={companyData?.business_type?.label} compact showEmpty /></Grid>
              </Grid>
            </Box>

            {/* Financial History Section (T12, FR-017/SC-002): read-only, newest-first list from
                GET company/{id}/financials-history/ (T7). Do NOT re-sort — endpoint order is authoritative. */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle1" sx={{ mb: 1.5, fontSize: '0.95rem', fontWeight: 600, color: '#1976d2' }}>
                🕑 Financial History
              </Typography>
              {financialsHistory.length === 0 ? (
                <Typography variant="body2" sx={{ color: '#6c757d' }}>
                  No financial changes recorded yet.
                </Typography>
              ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                  {financialsHistory.map((row) => {
                    const currencyPrefix = companyData?.currency?.label ? `${companyData.currency.label} ` : '';
                    return (
                      <Box key={row.id} sx={{ p: 1.5, backgroundColor: '#f8f9fa', borderRadius: 1.5, border: '1px solid #e9ecef' }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1, gap: 1 }}>
                          <Chip
                            label={row.stage_label ? `${row.stage_label}${row.stage_percentage != null ? ` (${row.stage_percentage}%)` : ''}` : '-'}
                            size="small"
                            sx={{ backgroundColor: '#e3f2fd', color: '#1976d2', fontWeight: 600 }}
                          />
                          <Typography variant="caption" sx={{ color: '#6c757d', whiteSpace: 'nowrap' }}>
                            {formatDateTime(row.created_at)}
                          </Typography>
                        </Box>
                        <Grid container spacing={1}>
                          <Grid item xs={6}>
                            <Typography variant="caption" sx={{ color: '#6c757d', display: 'block' }}>TCV</Typography>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>{row.total_contract_value ? `${currencyPrefix}${row.total_contract_value}` : '-'}</Typography>
                          </Grid>
                          <Grid item xs={6}>
                            <Typography variant="caption" sx={{ color: '#6c757d', display: 'block' }}>TCI</Typography>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>{row.tci ? `${currencyPrefix}${row.tci}` : '-'}</Typography>
                          </Grid>
                          <Grid item xs={6}>
                            <Typography variant="caption" sx={{ color: '#6c757d', display: 'block' }}>OneTime (SaaS)</Typography>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>{row.one_time_saas ? `${currencyPrefix}${row.one_time_saas}` : '-'}</Typography>
                          </Grid>
                          <Grid item xs={6}>
                            <Typography variant="caption" sx={{ color: '#6c757d', display: 'block' }}>MRR</Typography>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>{row.mrr ? `${currencyPrefix}${row.mrr}` : '-'}</Typography>
                          </Grid>
                          <Grid item xs={6}>
                            <Typography variant="caption" sx={{ color: '#6c757d', display: 'block' }}>ARR</Typography>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>{row.arr ? `${currencyPrefix}${row.arr}` : '-'}</Typography>
                          </Grid>
                          <Grid item xs={6}>
                            <Typography variant="caption" sx={{ color: '#6c757d', display: 'block' }}>Updated By</Typography>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>{row.user_display || '-'}</Typography>
                          </Grid>
                        </Grid>
                      </Box>
                    );
                  })}
                </Box>
              )}
            </Box>

            {/* Team & Engagement Section */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle1" sx={{ mb: 1.5, fontSize: '0.95rem', fontWeight: 600, color: '#1976d2' }}>
                👥 Team & Engagement
              </Typography>
              <Grid container spacing={1.5}>
                <Grid item xs={6}><DealDetailsViewField label="No. of Tech Team" value={formData.tech_team_count} compact showEmpty /></Grid>
                <Grid item xs={6}><DealDetailsViewField label="Engagement Period" value={formData.engagement_period} compact showEmpty /></Grid>
              </Grid>
            </Box>
            
            {/* Investment Details Section */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle1" sx={{ mb: 1.5, fontSize: '0.95rem', fontWeight: 600, color: '#1976d2' }}>
                💰 Investment Details
              </Typography>
              <Grid container spacing={1.5} sx={{ mb: 1.5 }}>
                <Grid item xs={6}><DealDetailsViewField label="Cash Investment" value={formData.total_cash_investment} compact showEmpty /></Grid>
                <Grid item xs={6}><DealDetailsViewField label="Equity Conversion" value={formData.equity_conversion_investment} compact showEmpty /></Grid>
              </Grid>
              <DealDetailsViewField label="Lead Gen Engineering" value={formData.lead_gen_eng} />
              <DealDetailsViewField label="Marketing Engineering" value={formData.marketing_eng} />
              <DealDetailsViewField label="Governance Engineering" value={formData.governanace_eng} />
            </Box>
            
            {/* Equity & Valuation Section */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle1" sx={{ mb: 1.5, fontSize: '0.95rem', fontWeight: 600, color: '#1976d2' }}>
                📊 Equity & Valuation
              </Typography>
              <Grid container spacing={1.5} sx={{ mb: 1.5 }}>
                <Grid item xs={6}><DealDetailsViewField label="Valuation at Investing" value={formData.valuation_at_investing} compact showEmpty /></Grid>
                <Grid item xs={6}><DealDetailsViewField label="% Equity We Take" value={formData.equity_percentage} compact showEmpty /></Grid>
              </Grid>
              <Grid container spacing={1.5}>
                <Grid item xs={6}><DealDetailsViewField label="Deal Date" value={formData.deal_date} compact showEmpty /></Grid>
                <Grid item xs={6}><DealDetailsViewField label="Risk Level" value={getRiskLevelLabel(formData.risk_level)} compact showEmpty /></Grid>
              </Grid>
            </Box>
          </Box>
          
          <Box sx={{ p: 2, borderTop: '1px solid #e0e0e0', display: 'flex', gap: 1.5 }}>
            <Button 
              variant="outlined" 
              fullWidth 
              onClick={onClose}
              sx={{ py: 1.2, fontSize: '0.95rem', fontWeight: 600 }}
            >
              Close
            </Button>
            {onEdit && (
              <Button 
                variant="contained" 
                fullWidth 
                onClick={onEdit}
                startIcon={<Edit2 size={18} />}
                sx={{ py: 1.2, fontSize: '0.95rem', fontWeight: 600, backgroundColor: '#1976d2', '&:hover': { backgroundColor: '#1565c0' } }}
              >
                Edit
              </Button>
            )}
          </Box>
        </Box>
      );
    }

    // Edit Mode Render
    return (
      <Box sx={{ width: 400, p: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Box>
            <Typography variant="h6">Complete Deal Details</Typography>
            {isStageChangeValidation && (
              <Typography variant="caption" color="text.secondary">
                Step 1 of 2 • Next: Team Assignment
              </Typography>
            )}
          </Box>
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>

        {!isStageChangeValidation && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="h6" sx={{ mb: 2, fontSize: '0.875rem', fontWeight: 600, color: 'text.secondary' }}>
              Deal Details
            </Typography>

            <TextField
              fullWidth
              multiline
              rows={3}
              label="Current Update"
              value={dealFields.current_update}
              onChange={(e) => setDealFields(p => ({ ...p, current_update: e.target.value }))}
              size="small"
              sx={{ mb: 2 }}
            />

            <TextField
              fullWidth
              type="number"
              label="TCV"
              value={dealFields.total_contract_value}
              onChange={(e) => setDealFields(p => ({ ...p, total_contract_value: e.target.value }))}
              size="small"
              InputProps={companyData?.currency?.label ? { startAdornment: <InputAdornment position="start">{companyData.currency.label}</InputAdornment> } : undefined}
              sx={{ mb: 2 }}
            />

            <TextField
              fullWidth
              type="number"
              label={isTciRequiredHint ? 'TCI *' : 'TCI'}
              value={dealFields.tci}
              onChange={(e) => setDealFields(p => ({ ...p, tci: e.target.value }))}
              size="small"
              required={isTciRequiredHint}
              helperText={isTciRequiredHint ? 'Required at this stage' : ''}
              InputProps={companyData?.currency?.label ? { startAdornment: <InputAdornment position="start">{companyData.currency.label}</InputAdornment> } : undefined}
              sx={{ mb: 2 }}
            />

            <TextField
              fullWidth
              type="number"
              label="OneTime (SaaS)"
              value={dealFields.one_time_saas}
              onChange={(e) => setDealFields(p => ({ ...p, one_time_saas: e.target.value }))}
              size="small"
              InputProps={companyData?.currency?.label ? { startAdornment: <InputAdornment position="start">{companyData.currency.label}</InputAdornment> } : undefined}
              sx={{ mb: 2 }}
            />

            <TextField
              fullWidth
              type="number"
              label="MRR"
              value={dealFields.mrr}
              onChange={(e) => setDealFields(p => ({ ...p, mrr: e.target.value }))}
              size="small"
              InputProps={companyData?.currency?.label ? { startAdornment: <InputAdornment position="start">{companyData.currency.label}</InputAdornment> } : undefined}
              sx={{ mb: 2 }}
            />

            <TextField
              fullWidth
              label="ARR"
              value={dealFields.mrr === '' || dealFields.mrr === null || isNaN(Number(dealFields.mrr)) ? '' : String(Number(dealFields.mrr) * 12)}
              size="small"
              InputProps={{
                readOnly: true,
                ...(companyData?.currency?.label ? { startAdornment: <InputAdornment position="start">{companyData.currency.label}</InputAdornment> } : {}),
              }}
              sx={{ mb: 2 }}
            />

            <TextField
              fullWidth
              label="Status"
              value={dealFields.status}
              onChange={(e) => setDealFields(p => ({ ...p, status: e.target.value }))}
              size="small"
              sx={{ mb: 2 }}
            />

            <Autocomplete
              fullWidth
              size="small"
              options={businessTypeOptions}
              getOptionLabel={(o) => o.label || ''}
              value={businessTypeOptions.find(b => b.id === dealFields.business_type_id) || null}
              isOptionEqualToValue={(o, v) => o.id === v.id}
              onChange={(e, v) => setDealFields(p => ({ ...p, business_type_id: v ? v.id : '' }))}
              renderInput={(params) => <TextField {...params} label="Business Type" placeholder="None" />}
              sx={{ mb: 3 }}
            />
          </Box>
        )}

        {isStageChangeValidation && (
          <Box sx={{ mb: 3, p: 2, backgroundColor: '#fff3e0', borderRadius: 1, borderLeft: '4px solid #ff9800' }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#e65100', mb: 0.5 }}>
              ⚠️ Mandatory to Move to 100%
            </Typography>
            <Typography variant="caption" sx={{ color: '#5d4037' }}>
              These fields are required to close the deal. Please complete Cash Investment and Equity Percentage before moving to 100% stage.
            </Typography>
          </Box>
        )}

        <Typography variant="h6" sx={{ mb: 2, fontSize: '0.875rem', fontWeight: 600, color: 'text.secondary' }}>
          Team & Engagement
        </Typography>

        <Box sx={{ display: 'flex', gap: 1.5, mb: 2 }}>
          <TextField
            label="No. of Tech Team"
            value={formData.tech_team_count}
            onChange={(e) => setFormData(prev => ({ ...prev, tech_team_count: e.target.value }))}
            size="small"
            disabled={!isStageChangeValidation}
            sx={{ flex: 1 }}
          />
          <TextField
            label="Engagement Period"
            value={formData.engagement_period}
            onChange={(e) => setFormData(prev => ({ ...prev, engagement_period: e.target.value }))}
            placeholder="e.g., 12 months"
            size="small"
            disabled={!isStageChangeValidation}
            sx={{ flex: 1 }}
          />
        </Box>

        <Typography variant="h6" sx={{ mb: 2, fontSize: '0.875rem', fontWeight: 600, color: 'text.secondary' }}>
          Investment Details
        </Typography>

        <Box sx={{ display: 'flex', gap: 1.5, mb: 3 }}>
          <TextField
            label={`Cash Investment${isStageChangeValidation ? ' *' : ''}`}
            value={formData.total_cash_investment}
            onChange={(e) => setFormData(prev => ({ ...prev, total_cash_investment: e.target.value }))}
            size="small"
            required={isStageChangeValidation}
            disabled={!isStageChangeValidation}
            error={!!errors.total_cash_investment}
            helperText={errors.total_cash_investment}
            placeholder="e.g., $100k, 500K, 1M"
            sx={{ flex: 1 }}
          />
          <TextField
            label="Equity Conversion"
            value={formData.equity_conversion_investment}
            onChange={(e) => setFormData(prev => ({ ...prev, equity_conversion_investment: e.target.value }))}
            size="small"
            disabled={!isStageChangeValidation}
            placeholder="e.g., $50k, 250K"
            sx={{ flex: 1 }}
          />
        </Box>

        <TextField
          fullWidth
          multiline
          rows={2}
          label="Lead Gen Engineering"
          value={formData.lead_gen_eng}
          onChange={(e) => setFormData(prev => ({ ...prev, lead_gen_eng: e.target.value }))}
          placeholder="Size of engineering"
          size="small"
          disabled={!isStageChangeValidation}
          sx={{ mb: 2 }}
        />

        <TextField
          fullWidth
          multiline
          rows={2}
          label="Marketing Engineering"
          value={formData.marketing_eng}
          onChange={(e) => setFormData(prev => ({ ...prev, marketing_eng: e.target.value }))}
          placeholder="Size of engineering"
          size="small"
          disabled={!isStageChangeValidation}
          sx={{ mb: 2 }}
        />

        <TextField
          fullWidth
          multiline
          rows={2}
          label="Governance Engineering"
          value={formData.governanace_eng}
          onChange={(e) => setFormData(prev => ({ ...prev, governanace_eng: e.target.value }))}
          placeholder="Size of engineering"
          size="small"
          disabled={!isStageChangeValidation}
          sx={{ mb: 3 }}
        />

        <Typography variant="h6" sx={{ mb: 2, fontSize: '0.875rem', fontWeight: 600, color: 'text.secondary' }}>
          Equity & Valuation
        </Typography>

        <Box sx={{ display: 'flex', gap: 1.5, mb: 2 }}>
          <TextField
            label="Valuation at Investing"
            value={formData.valuation_at_investing}
            onChange={(e) => setFormData(prev => ({ ...prev, valuation_at_investing: e.target.value }))}
            size="small"
            disabled={!isStageChangeValidation}
            placeholder="e.g., $5M, 10M"
            sx={{ flex: 1 }}
          />
          <TextField
            label={`% Equity We Take${isStageChangeValidation ? ' *' : ''}`}
            value={formData.equity_percentage}
            onChange={(e) => setFormData(prev => ({ ...prev, equity_percentage: e.target.value }))}
            size="small"
            required={isStageChangeValidation}
            disabled={!isStageChangeValidation}
            error={!!errors.equity_percentage}
            helperText={errors.equity_percentage}
            placeholder="e.g., 10%, 15"
            sx={{ flex: 1 }}
          />
        </Box>

        <TextField
          fullWidth
          label="Deal Date"
          type="date"
          value={formData.deal_date}
          onChange={(e) => setFormData(prev => ({ ...prev, deal_date: e.target.value }))}
          InputLabelProps={{ shrink: true }}
          size="small"
          disabled={!isStageChangeValidation}
          sx={{ mb: 2 }}
        />

        {/* <TextField
          fullWidth
          label="Exit Strategy"
          value={formData.exit_strategy}
          onChange={(e) => setFormData(prev => ({ ...prev, exit_strategy: e.target.value }))}
          placeholder="e.g., IPO/Acquisition"
          size="small"
          sx={{ mb: 2 }}
        /> */}

        <Autocomplete
          fullWidth
          size="small"
          disabled={!isStageChangeValidation}
          value={[
            { value: 'A', label: 'Level A - Safe investment' },
            { value: 'B', label: 'Level B - Moderate risk' },
            { value: 'C', label: 'Level C - High risk' }
          ].find(opt => opt.value === formData.risk_level) || null}
          onChange={(event, newValue) => setFormData(prev => ({ ...prev, risk_level: newValue ? newValue.value : '' }))}
          options={[
            { value: 'A', label: 'Level A - Safe investment' },
            { value: 'B', label: 'Level B - Moderate risk' },
            { value: 'C', label: 'Level C - High risk' }
          ]}
          getOptionLabel={(option) => option.label}
          isOptionEqualToValue={(option, value) => option.value === value.value}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Risk Level"
              placeholder="Select risk level..."
            />
          )}
          sx={{ mb: 3 }}
        />

        <Button
          variant="contained"
          fullWidth
          disabled={!isFormValid || isSaving}
          sx={{
            mt: 2,
            backgroundColor: '#4caf50',
            '&:hover': { backgroundColor: '#45a049' },
            '&:disabled': { backgroundColor: '#cccccc' }
          }}
          onClick={handleSave}
        >
          {isSaving ? 'Saving...' : 'Save Changes'}
        </Button>
      </Box>
    );
  }


  return (
    <Box sx={{
      p: 2,
      minHeight: '100%',
      display: 'flex',
      flexDirection: 'column',
      width: '100%',
      margin: 0,
      '@keyframes pulse': {
        '0%': { opacity: 1 },
        '50%': { opacity: 0.5 },
        '100%': { opacity: 1 }
      }
    }}>
      {/* Header */}
      <Box mb={2}>
        {/* Company Header with Stage Dropdown and View Tabs */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <IconButton
              onClick={() => navigate('/dashboard')}
              sx={{
                bgcolor: '#f5f5f5',
                '&:hover': { bgcolor: '#e0e0e0' },
                transition: 'all 0.2s'
              }}
            >
              <ArrowLeft size={20} />
            </IconButton>

            {/* Unified Premium Card Container */}
            <Card
              elevation={0}
              sx={{
                display: 'flex',
                alignItems: 'stretch',
                gap: 0,
                border: '1px solid #e0e0e0',
                borderRadius: 3,
                overflow: 'hidden',
                transition: 'all 0.3s ease',
                backgroundColor: '#fff',
                '&:hover': {
                  boxShadow: '0 4px 16px rgba(0,0,0,0.08)',
                  borderColor: '#1976d2'
                }
              }}
            >
              {/* Company Search Section */}
              <Box sx={{
                flex: 1,
                borderRight: '1px solid #e0e0e0',
                minWidth: 350,
                display: 'flex',
                alignItems: 'center'
              }}>
                <Autocomplete
                  value={null}
                  inputValue={companySearchInput}
                  onInputChange={(event, newInputValue, reason) => {
                    if (reason !== 'reset') {
                      setCompanySearchInput(newInputValue);
                    }
                  }}
                  onChange={(event, newValue) => {
                    if (newValue && newValue.id && newValue.id !== companyData.id) {
                      setCompanySearchInput(newValue.company_name);
                      navigate(`/companies/${newValue.id}`);
                    }
                  }}
                  options={companyOptions}
                  getOptionLabel={(option) => option.company_name || ''}
                  loading={isSearchingCompanies}
                  freeSolo
                  disableClearable
                  noOptionsText={companySearchInput.length < 2 ? "Type to search..." : "No companies found"}
                  fullWidth
                  sx={{
                    '& .MuiAutocomplete-inputRoot': {
                      paddingRight: '9px !important',
                      paddingLeft: '12px !important'
                    },
                    '& .MuiOutlinedInput-root': {
                      border: 'none',
                      '& fieldset': {
                        border: 'none'
                      }
                    }
                  }}
                  renderInput={(params) => (
                    <Box sx={{ position: 'relative', width: '100%' }}>
                      <TextField
                        {...params}
                        variant="outlined"
                        placeholder="Search company..."
                        onBlur={() => {
                          // Restore original company name if input is empty
                          if (!companySearchInput.trim() && companyData?.company_name) {
                            setCompanySearchInput(companyData.company_name);
                          }
                        }}
                        InputProps={{
                          ...params.InputProps,
                          startAdornment: (
                            <Box sx={{ display: 'flex', alignItems: 'center', mr: 1 }}>
                              <Search size={20} style={{ color: '#64748b' }} />
                            </Box>
                          ),
                          endAdornment: (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mr: 1 }}>
                              {companyData?.country?.label && !companySearchInput.includes('Search') && (
                                <Chip
                                  label={companyData.country.label}
                                  size="small"
                                  sx={{
                                    height: 22,
                                    fontSize: '0.7rem',
                                    fontWeight: 600,
                                    backgroundColor: getCountryColor(companyData.country.label),
                                    color: '#fff',
                                    '& .MuiChip-label': {
                                      px: 1
                                    }
                                  }}
                                />
                              )}
                              {isSearchingCompanies && <CircularProgress color="primary" size={18} />}
                              {params.InputProps.endAdornment}
                            </Box>
                          ),
                          sx: {
                            '& .MuiInputBase-input': {
                              fontSize: '1rem',
                              fontWeight: 600,
                              padding: '12px 4px',
                              paddingTop: '12px',
                              paddingBottom: companyData?.created_by && !companySearchInput.includes('Search') ? '30px' : '12px',
                              color: '#1e293b',
                              lineHeight: 1.4
                            }
                          }
                        }}
                      />
                      {companyData?.created_by && !companySearchInput.includes('Search') && (
                        <Box
                          sx={{
                            position: 'absolute',
                            bottom: 4,
                            left: 44,
                            pointerEvents: 'none'
                          }}
                        >
                          <Typography
                            variant="caption"
                            sx={{
                              color: '#64748b',
                              fontSize: '0.7rem',
                              fontWeight: 500,
                              lineHeight: 1
                            }}
                          >
                            Created by: {companyData.created_by.first_name} {companyData.created_by.last_name}
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  )}
                  renderOption={(props, option) => (
                    <Box
                      component="li"
                      {...props}
                      key={option.id}
                      sx={{
                        borderBottom: '1px solid #f0f0f0',
                        '&:hover': {
                          backgroundColor: '#f8fafc !important'
                        },
                        padding: '10px 14px !important',
                        transition: 'all 0.2s ease'
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', gap: 1.5 }}>
                        <Avatar
                          sx={{
                            width: 32,
                            height: 32,
                            backgroundColor: '#1976d2',
                            fontSize: '0.75rem',
                            fontWeight: 700
                          }}
                        >
                          {option.company_name?.substring(0, 2).toUpperCase() || 'C'}
                        </Avatar>
                        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="body2" sx={{ fontWeight: 600, color: '#1e293b' }}>
                              {option.company_name}
                            </Typography>
                            {option.country_name && (
                              <Chip
                                label={option.country_name}
                                size="small"
                                sx={{
                                  height: 20,
                                  fontSize: '0.65rem',
                                  fontWeight: 600,
                                  backgroundColor: getCountryColor(option.country_name),
                                  color: '#fff',
                                  '& .MuiChip-label': {
                                    px: 1
                                  }
                                }}
                              />
                            )}
                          </Box>
                          {(option.created_by_name || option.created_by) && (
                            <Typography variant="caption" sx={{ color: '#64748b', fontSize: '0.7rem' }}>
                              Created by: {
                                option.created_by_name ||
                                (option.created_by ? `${option.created_by.first_name} ${option.created_by.last_name}` : '')
                              }
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    </Box>
                  )}
                  ListboxProps={{
                    sx: {
                      maxHeight: 350,
                      padding: 0,
                      '& .MuiAutocomplete-option[aria-selected="true"]': {
                        backgroundColor: '#eff6ff !important'
                      }
                    }
                  }}
                  componentsProps={{
                    paper: {
                      elevation: 3,
                      sx: {
                        mt: 0.5,
                        borderRadius: 2,
                        border: '1px solid #e2e8f0',
                        overflow: 'hidden'
                      }
                    }
                  }}
                />
              </Box>

              {/* Stage Dropdown Section */}
              <Box sx={{
                minWidth: 220,
                bgcolor: '#fafafa',
                display: 'flex',
                alignItems: 'center'
              }}>
                <Select
                  fullWidth
                  size="small"
                  value={currentStageId || ''}
                  onChange={handleStageChange}
                  variant="standard"
                  disableUnderline
                  sx={{
                    px: 2.5,
                    py: 1.75,
                    fontSize: '1rem',
                    fontWeight: 600,
                    color: '#1976d2',
                    '&:hover': {
                      bgcolor: '#f0f4f8'
                    },
                    '& .MuiSelect-icon': {
                      color: '#1976d2',
                    },
                    '& .MuiSelect-select': {
                      padding: '0 !important',
                    }
                  }}
                >
                  {stages.map((stage) => (
                    <MenuItem key={stage.id} value={stage.id}>
                      {getStageDisplayName(stage.name)}
                    </MenuItem>
                  ))}
                </Select>

              </Box>

              {/* Offering (Category) Dropdown Section */}
              <Box sx={{
                minWidth: 220,
                bgcolor: '#fafafa',
                borderLeft: '1px solid #e0e0e0',
                display: 'flex',
                alignItems: 'center'
              }}>
                <Select
                  fullWidth
                  size="small"
                  displayEmpty
                  value={currentOfferingId || ''}
                  onChange={handleOfferingChange}
                  variant="standard"
                  disableUnderline
                  renderValue={(selected) => {
                    if (!selected) {
                      return <span style={{ color: '#9e9e9e', fontWeight: 500 }}>Set Offering</span>;
                    }
                    const found = offerings.find((o) => o.id === selected);
                    return found ? (found.label || found.name) : 'Set Offering';
                  }}
                  sx={{
                    px: 2.5,
                    py: 1.75,
                    fontSize: '1rem',
                    fontWeight: 600,
                    color: '#1976d2',
                    '&:hover': {
                      bgcolor: '#f0f4f8'
                    },
                    '& .MuiSelect-icon': {
                      color: '#1976d2',
                    },
                    '& .MuiSelect-select': {
                      padding: '0 !important',
                    }
                  }}
                >
                  <MenuItem value="">
                    <em>No Offering</em>
                  </MenuItem>
                  {offerings.map((offering) => (
                    <MenuItem key={offering.id} value={offering.id}>
                      {offering.label || offering.name}
                    </MenuItem>
                  ))}
                </Select>
              </Box>

              {/* Deadline Alert Indicator - Shows which rule triggered red highlighting */}
              {companyData?.is_red_color && companyData?.red_color_rule && (
                <Tooltip title={companyData.red_color_rule === '2_weeks' ? 'Company in 25%/50% stage for more than 2 weeks' : 'Company taking more than 6 weeks from 10% stage'}>
                  <Box 
                    sx={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: 0.5, 
                      ml: 2,
                      mr: 1,
                      px: 1.5,
                      py: 0.8,
                      borderRadius: 1.5,
                      bgcolor: '#fee2e2',
                      border: '2px solid #ef4444',
                      flexShrink: 0,
                      whiteSpace: 'nowrap',
                    }}
                  >
                    <span style={{ fontSize: '1rem' }}>⏰</span>
                    <Typography sx={{ 
                      fontSize: '0.8rem',
                      fontWeight: 700,
                      color: '#b91c1c',
                      whiteSpace: 'nowrap',
                    }}>
                      {companyData.red_color_rule === '2_weeks' ? '⚠️ 2+ Weeks in Stage' : '⚠️ 6+ Weeks Overall'}
                    </Typography>
                  </Box>
                </Tooltip>
              )}
              
              {/* Invalid Company Indicator - Shows for any invalid company */}
              {invalidDetails && (
                <Box sx={{
                  px: 2,
                  py: 1,
                  borderLeft: '1px solid #e0e0e0',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1
                }}>
                  <Chip
                    icon={<span style={{ fontSize: '0.9rem' }}>⚠️</span>}
                    label="Invalid Company"
                    size="small"
                    onClick={() => setViewInvalidDrawerOpen(true)}
                    sx={{
                      bgcolor: '#fff3e0',
                      color: '#e65100',
                      fontWeight: 600,
                      cursor: 'pointer',
                      '&:hover': { bgcolor: '#ffe0b2' }
                    }}
                  />
                </Box>
              )}

              {/* DND (Do-Not-Disturb) control — available on every company regardless
                  of stage. Shows a status chip + popover when flagged, else an "Add" button. */}
              <Box sx={{
                px: 2,
                py: 1,
                borderLeft: '1px solid #e0e0e0',
                display: 'flex',
                alignItems: 'center',
                gap: 1
              }}>
                {dndDetails ? (
                  <>
                    <Chip
                      icon={<DoNotDisturbOn sx={{ fontSize: '1rem !important', color: '#c62828 !important' }} />}
                      label="DND"
                      size="small"
                      onClick={(e) => setDndPopoverAnchor(e.currentTarget)}
                      sx={{
                        bgcolor: '#ffebee',
                        color: '#c62828',
                        fontWeight: 700,
                        cursor: 'pointer',
                        '&:hover': { bgcolor: '#ffcdd2' }
                      }}
                    />
                    <Popover
                      open={Boolean(dndPopoverAnchor)}
                      anchorEl={dndPopoverAnchor}
                      onClose={() => setDndPopoverAnchor(null)}
                      anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                      transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                      PaperProps={{ sx: { p: 2, maxWidth: 300 } }}
                    >
                      <Typography sx={{ fontSize: '0.85rem', fontWeight: 700, color: '#c62828', mb: 0.75 }}>
                        🚫 Do-Not-Disturb
                      </Typography>
                      <Typography sx={{ fontSize: '0.8rem', mb: 0.5 }}>
                        <b>Reason:</b> {dndDetails.reason_display || '—'}
                      </Typography>
                      {dndDetails.note && (
                        <Typography sx={{ fontSize: '0.8rem', mb: 0.5 }}>
                          <b>Note:</b> {dndDetails.note}
                        </Typography>
                      )}
                      {dndDetails.marked_by_name && (
                        <Typography sx={{ fontSize: '0.75rem', color: '#666' }}>
                          By {dndDetails.marked_by_name}
                        </Typography>
                      )}
                      {dndDetails.marked_at && (
                        <Typography sx={{ fontSize: '0.75rem', color: '#666', mb: 1 }}>
                          {new Date(dndDetails.marked_at).toLocaleString()}
                        </Typography>
                      )}
                      <Button
                        size="small"
                        variant="outlined"
                        color="error"
                        fullWidth
                        disabled={isRemovingDnd}
                        onClick={handleRemoveDnd}
                        sx={{ mt: 0.5, fontSize: '0.75rem', textTransform: 'none' }}
                      >
                        {isRemovingDnd ? 'Removing…' : 'Remove from DND'}
                      </Button>
                    </Popover>
                  </>
                ) : (
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => setAddDndDialogOpen(true)}
                    startIcon={<DoNotDisturbAltOutlined sx={{ fontSize: '0.9rem' }} />}
                    sx={{
                      fontSize: '0.75rem',
                      textTransform: 'none',
                      color: '#c62828',
                      borderColor: '#e57373',
                      '&:hover': {
                        borderColor: '#c62828',
                        bgcolor: 'rgba(198,40,40,0.04)'
                      }
                    }}
                  >
                    Add to DND
                  </Button>
                )}
              </Box>

              {/* Archive Section - For 100% stage and super admin */}
              {isHundredPercentStage() && isSuperAdmin && (
                <Box sx={{
                  px: 2,
                  py: 1,
                  borderLeft: '1px solid #e0e0e0',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1
                }}>
                  {archiveDetails ? (
                    <Chip
                      icon={<span style={{ fontSize: '0.9rem' }}>📦</span>}
                      label="Archived"
                      size="small"
                      onClick={() => setViewArchiveDrawerOpen(true)}
                      sx={{
                        bgcolor: '#f3e5f5',
                        color: '#7b1fa2',
                        fontWeight: 600,
                        cursor: 'pointer',
                        '&:hover': { bgcolor: '#e1bee7' }
                      }}
                    />
                  ) : (
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={(e) => {
                        e.stopPropagation();
                        openArchiveDrawer();
                      }}
                      sx={{
                        fontSize: '0.75rem',
                        textTransform: 'none',
                        color: '#9c27b0',
                        borderColor: '#9c27b0',
                        '&:hover': {
                          borderColor: '#7b1fa2',
                          bgcolor: 'rgba(156, 39, 176, 0.04)'
                        }
                      }}
                      startIcon={<span style={{ fontSize: '0.9rem' }}>📦</span>}
                    >
                      Archive
                    </Button>
                  )}
                </Box>
              )}

              {/* Outreach Status/Button - Only for 0% stage */}
              {isZeroPercentStage() && (
                <Box sx={{
                  px: 2,
                  py: 1,
                  borderLeft: '1px solid #e0e0e0',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1
                }}>
                  {outreachDetails ? (
                    <Chip
                      icon={<span style={{ fontSize: '0.9rem' }}>✅</span>}
                      label="Outreached"
                      size="small"
                      onClick={() => setViewOutreachDrawerOpen(true)}
                      sx={{
                        bgcolor: '#e8f5e9',
                        color: '#2e7d32',
                        fontWeight: 600,
                        cursor: 'pointer',
                        '&:hover': { bgcolor: '#c8e6c9' }
                      }}
                    />
                  ) : (
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Button
                        size="small"
                        variant="contained"
                        color="primary"
                        onClick={(e) => {
                          e.stopPropagation();
                          openMarkOutreachDrawer();
                        }}
                        sx={{
                          fontSize: '0.75rem',
                          textTransform: 'none',
                        }}
                        startIcon={<span style={{ fontSize: '0.9rem' }}>📤</span>}
                      >
                        Mark as Outreached
                      </Button>
                      {!invalidDetails && (
                        <Button
                          size="small"
                          variant="outlined"
                          color="warning"
                          onClick={(e) => {
                            e.stopPropagation();
                            openMarkInvalidDrawer();
                          }}
                          sx={{
                            fontSize: '0.75rem',
                            textTransform: 'none',
                          }}
                          startIcon={<span style={{ fontSize: '0.9rem' }}>⚠️</span>}
                        >
                          Invalid Company
                        </Button>
                      )}
                    </Box>
                  )}
                </Box>
              )}

              {/* QM Exit Note chip (read-only, US-4 Task 7) - shows whenever a QM-exit note exists */}
              {(companyData?.qm_exit_comment || '').trim() && (
                <Chip size="small" label="QM Exit Note"
                  onClick={() => setViewQmExitCommentOpen(true)}
                  sx={{ ml: 1, bgcolor: '#e8f5e9', color: '#2e7d32', fontWeight: 600, cursor: 'pointer', '&:hover': { bgcolor: '#c8e6c9' } }} />
              )}

            </Card>
          </Box>

          {/* View Toggle Tabs */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            {/* Team Lead, SAM and PM Section */}
            {(() => {
              // Find Team Lead, SAM and PM assignments
              const teamLeadAssignment = companyTeamAssignments.find(assignment => {
                const role = userRoles.find(r => r.id?.toString() === assignment.role?.toString());
                return role?.label?.toLowerCase() === 'team lead';
              });

              const samAssignment = companyTeamAssignments.find(assignment => {
                const role = userRoles.find(r => r.id?.toString() === assignment.role?.toString());
                // Match on stable role name (SAM), not the display label (now "BM").
                return role?.name?.toLowerCase() === 'sam';
              });

              const pmAssignment = companyTeamAssignments.find(assignment => {
                const role = userRoles.find(r => r.id?.toString() === assignment.role?.toString());
                // Match on stable role name (PM), not the display label (now "BA").
                return role?.name?.toLowerCase() === 'pm';
              });

              const displayItems = [];

              // Add Team Lead first (highest hierarchy)
              if (teamLeadAssignment) {
                const user = teamMembers.find(m => m.pk?.toString() === teamLeadAssignment.user?.toString());
                const firstName = user?.first_name || teamLeadAssignment.user_name?.split(' ')[0] || 'TL';
                displayItems.push({ emoji: '👨‍💼', role: 'Team Lead', name: firstName });
              }

              // Add SAM second
              if (samAssignment) {
                const user = teamMembers.find(m => m.pk?.toString() === samAssignment.user?.toString());
                const firstName = user?.first_name || samAssignment.user_name?.split(' ')[0] || 'BM';
                displayItems.push({ emoji: '🎯', role: 'BM', name: firstName });
              }

              // Add PM third
              if (pmAssignment) {
                const user = teamMembers.find(m => m.pk?.toString() === pmAssignment.user?.toString());
                const firstName = user?.first_name || pmAssignment.user_name?.split(' ')[0] || 'BA';
                displayItems.push({ emoji: '👤', role: 'BA', name: firstName });
              }

              if (displayItems.length === 0) return null;

              return (
                <Box sx={{ display: 'flex', alignItems: 'stretch', gap: 1.5, mr: 2 }}>
                  {displayItems.map((item, index) => (
                    <Box
                      key={index}
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 0.5,
                        minWidth: 'fit-content',
                        px: 2,
                        py: 1.5,
                        bgcolor: '#f8fafc',
                        border: '1px solid #e2e8f0',
                        borderRadius: 2.5,
                        transition: 'all 0.2s ease',
                        '&:hover': {
                          borderColor: '#cbd5e1',
                          boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
                        }
                      }}
                    >
                      <Typography
                        variant="caption"
                        sx={{
                          fontSize: '0.65rem',
                          fontWeight: 700,
                          color: '#94a3b8',
                          letterSpacing: '0.05em',
                          textAlign: 'center',
                          textTransform: 'uppercase',
                          display: 'flex',
                          alignItems: 'center',
                          gap: 0.5
                        }}
                      >
                        <span style={{ fontSize: '0.9em' }}>{item.emoji}</span> {item.role}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          fontSize: '0.85rem',
                          fontWeight: 600,
                          color: '#1e293b',
                          textAlign: 'center',
                          lineHeight: 1.2,
                          whiteSpace: 'nowrap'
                        }}
                      >
                        {item.name}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              );
            })()}

            {/* 5% Stage Reminder Indicator or Set Reminder Button */}
            {companyReminder ? (
              <Tooltip title="Click to view/edit reminder">
                <Box
                  onClick={handleViewReminder}
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    minWidth: 'fit-content',
                    px: 2,
                    py: 1.5,
                    bgcolor: new Date(companyReminder.reminder_date) <= new Date() ? '#fff5f5' : '#f0f9ff',
                    border: new Date(companyReminder.reminder_date) <= new Date() ? '1px solid #fca5a5' : '1px solid #93c5fd',
                    borderRadius: 2.5,
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      borderColor: new Date(companyReminder.reminder_date) <= new Date() ? '#f87171' : '#60a5fa',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.08)'
                    }
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{
                      fontSize: '0.65rem',
                      fontWeight: 700,
                      color: new Date(companyReminder.reminder_date) <= new Date() ? '#dc2626' : '#2563eb',
                      letterSpacing: '0.05em',
                      textAlign: 'center',
                      textTransform: 'uppercase',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.5
                    }}
                  >
                    <span style={{ fontSize: '1em' }}>🔔</span><span style={{ fontSize: '1em' }}>📝</span> {new Date(companyReminder.reminder_date) <= new Date() ? 'DUE!' : 'REMINDER'}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      fontSize: '0.8rem',
                      fontWeight: 600,
                      color: new Date(companyReminder.reminder_date) <= new Date() ? '#b91c1c' : '#1d4ed8',
                      textAlign: 'center',
                      lineHeight: 1.2,
                      whiteSpace: 'nowrap'
                    }}
                  >
                    {new Date(companyReminder.reminder_date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })} • {new Date(companyReminder.reminder_date).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                  </Typography>
                </Box>
              </Tooltip>
            ) : isStage5Percent(stages.find(s => s.id === getCurrentStageId())) && (
              <Tooltip title="Set a reminder for this 5% company">
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => {
                    const currentStage = stages.find(s => s.id === getCurrentStageId());
                    setPendingStageChange({ stageId: getCurrentStageId(), stage: currentStage, previousStage: currentStage });
                    setFivePercentReminderDialogOpen(true);
                  }}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                    px: 2,
                    py: 1,
                    borderColor: '#ff9800',
                    color: '#ff9800',
                    borderRadius: 2,
                    '&:hover': {
                      borderColor: '#f57c00',
                      bgcolor: '#fff3e0'
                    }
                  }}
                >
                  <span style={{ fontSize: '1rem' }}>🔔</span>
                  Set Reminder
                </Button>
              </Tooltip>
            )}

            {/* Activity Trail Button */}
            <Tooltip title="View activity trail">
              <Button
                size="small"
                variant="outlined"
                onClick={() => setActivityDrawerOpen(true)}
                sx={{
                  minWidth: 'auto',
                  px: 2,
                  py: 1,
                  borderColor: '#e2e8f0',
                  color: '#475569',
                  borderRadius: 2,
                  textTransform: 'none',
                  fontWeight: 600,
                  fontSize: '0.8rem',
                  '&:hover': {
                    borderColor: '#1976d2',
                    bgcolor: '#eff6ff',
                    color: '#1976d2'
                  }
                }}
                startIcon={<span style={{ fontSize: '1rem' }}>📋</span>}
              >
                Activity
              </Button>
            </Tooltip>

            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.5 }}>
              <Box sx={{ display: 'flex', backgroundColor: 'white', border: '1px solid #e0e0e0', borderRadius: 1, p: 0.5 }}>
                {['stage', 'data', 'task'].map((mode) => (
                  <Button
                    key={mode}
                    size="small"
                    onClick={() => setViewMode(mode)}
                    sx={{
                      px: 2,
                      py: 1,
                      minWidth: 'auto',
                      backgroundColor: viewMode === mode ?
                        (mode === 'stage' ? '#4caf50' : mode === 'data' ? '#2196f3' : '#9c27b0') : 'transparent',
                      color: viewMode === mode ? 'white' : 'text.secondary',
                      '&:hover': {
                        backgroundColor: viewMode === mode ?
                          (mode === 'stage' ? '#45a049' : mode === 'data' ? '#1976d2' : '#7b1fa2') : '#f5f5f5'
                      }
                    }}
                  >
                    {mode.charAt(0).toUpperCase() + mode.slice(1)}
                  </Button>
                ))}
              </Box>
              {/* Last Updated Info - Inline */}
              {(() => {
                const allItems = [
                  ...documents.map(d => ({ date: d.uploaded_on || d.created_at, user: d.created_by })),
                  ...meetings.map(m => ({ date: m.created_at, user: m.created_by })),
                  ...audioRecordings.map(a => ({ date: a.uploaded_on || a.created_at, user: a.created_by })),
                  ...notes.map(n => ({ date: n.modified_at || n.created_at, user: n.modified_by || n.created_by }))
                ].filter(item => item.date && item.user);
                if (allItems.length === 0) return null;
                const mostRecent = allItems.reduce((latest, current) => 
                  new Date(current.date) > new Date(latest.date) ? current : latest
                );
                const userName = typeof mostRecent.user === 'object' 
                  ? `${mostRecent.user.first_name || ''} ${mostRecent.user.last_name || ''}`.trim()
                  : mostRecent.user;
                if (!userName) return null;
                const date = new Date(mostRecent.date);
                const formattedDate = `${String(date.getDate()).padStart(2, '0')}-${String(date.getMonth() + 1).padStart(2, '0')}-${date.getFullYear()}`;
                return (
                  <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.75rem' }}>
                    Last updated by: <strong>{userName}</strong> • <strong>{formattedDate}</strong>
                  </Typography>
                );
              })()}
            </Box>
          </Box>
        </Box>
      </Box>

      {/* Four Cards in One Row */}
      <Grid container spacing={2} mb={2}>
        {/* Closed Deal Details */}
        <Grid item xs={12} sm={6} md={6} lg={3}>
          <Card sx={{
            p: 2.5,
            height: 'auto',
            display: 'flex',
            flexDirection: 'column',
            boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
            border: '1px solid #e8f5e8'
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
              <Box sx={{
                width: 28,
                height: 28,
                borderRadius: 1,
                backgroundColor: '#e8f5e8',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <CheckSquare size={16} color="#4caf50" />
              </Box>
              <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.primary', fontSize: '1rem' }}>Deal Details</Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                size="small"
                variant="outlined"
                startIcon={<Eye size={14} />}
                onClick={() => setViewClosedDealOpen(true)}
                sx={{
                  borderColor: '#e0e0e0',
                  color: 'text.secondary',
                  fontSize: '0.75rem',
                  px: 1.5,
                  flex: 1
                }}
              >
                View
              </Button>
              <Button
                size="small"
                variant="outlined"
                startIcon={<Edit size={14} />}
                onClick={() => setEditClosedDealOpen(true)}
                sx={{
                  borderColor: '#e0e0e0',
                  color: 'text.secondary',
                  fontSize: '0.75rem',
                  px: 1.5,
                  flex: 1
                }}
              >
                Edit
              </Button>
            </Box>
          </Card>
        </Grid>

        {/* Company Overview */}
        <Grid item xs={12} sm={6} md={6} lg={3}>
          <Card sx={{
            p: 2.5,
            height: 'auto',
            display: 'flex',
            flexDirection: 'column',
            boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
            border: '1px solid #f0f0f0'
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
              <Box sx={{
                width: 28,
                height: 28,
                borderRadius: 1,
                backgroundColor: '#e3f2fd',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <Building size={16} color="#1976d2" />
              </Box>
              <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1rem' }}>Company Overview</Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                size="small"
                variant="outlined"
                startIcon={<Eye size={14} />}
                onClick={() => setEditCompanyOpen(true)}
                sx={{
                  borderColor: '#e0e0e0',
                  color: 'text.secondary',
                  fontSize: '0.75rem',
                  px: 1.5,
                  flex: 1
                }}
              >
                View
              </Button>
              <Button
                size="small"
                variant="outlined"
                startIcon={<Edit size={14} />}
                onClick={() => setEditCompanyOpen(true)}
                sx={{
                  borderColor: '#e0e0e0',
                  color: 'text.secondary',
                  fontSize: '0.75rem',
                  px: 1.5,
                  flex: 1
                }}
              >
                Edit
              </Button>
            </Box>
          </Card>
        </Grid>

        {/* Calling Card */}
        <Grid item xs={12} sm={6} md={6} lg={3}>
          <Card sx={{
            p: 2.5,
            height: 'auto',
            display: 'flex',
            flexDirection: 'column',
            boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
            border: '1px solid #f0f0f0'
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
              <Box sx={{
                width: 28,
                height: 28,
                borderRadius: 1,
                backgroundColor: '#fff3e0',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <Phone size={16} color="#ff9800" />
              </Box>
              <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1rem' }}>Calling Card</Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                size="small"
                variant="outlined"
                startIcon={<Eye size={14} />}
                onClick={() => setViewCallingCardOpen(true)}
                sx={{
                  borderColor: '#e0e0e0',
                  color: 'text.secondary',
                  fontSize: '0.75rem',
                  px: 1.5,
                  flex: 1
                }}
              >
                View
              </Button>
              <Button
                size="small"
                variant="outlined"
                startIcon={<Edit size={14} />}
                onClick={() => setEditCallingCardOpen(true)}
                sx={{
                  borderColor: '#e0e0e0',
                  color: 'text.secondary',
                  fontSize: '0.75rem',
                  px: 1.5,
                  flex: 1
                }}
              >
                Edit
              </Button>
            </Box>
          </Card>
        </Grid>

        {/* Score Card */}
        <Grid item xs={12} sm={6} md={6} lg={3}>
          <Card sx={{
            p: 2.5,
            height: 'auto',
            display: 'flex',
            flexDirection: 'column',
            boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
            border: '1px solid #f0f0f0',
            backgroundColor: scoreCardData?.overallColor?.toLowerCase() === 'red' ? '#ffebee' : 
                           scoreCardData?.overallColor?.toLowerCase() === 'orange' ? '#fff3e0' : 
                           scoreCardData?.overallColor ? '#e8f5e9' : '#fff'
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
              <Box sx={{
                width: 28,
                height: 28,
                borderRadius: 1,
                backgroundColor: '#e8f5e9',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <CheckSquare size={16} color="#4caf50" />
              </Box>
              <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1rem' }}>Score Card</Typography>
              {scoreCardData && scoreCardData.overallScore !== undefined && (
                <Box sx={{ ml: 'auto' }}>
                  <Chip 
                    label={`${scoreCardData.overallScore}/25`}
                    size="small"
                    sx={{ 
                      backgroundColor: scoreCardData.overallColor?.toLowerCase() === 'red' ? '#d32f2f' : 
                                      scoreCardData.overallColor?.toLowerCase() === 'orange' ? '#f57c00' : '#388e3c',
                      color: '#fff',
                      fontWeight: 700,
                      fontSize: '0.85rem',
                      height: 26,
                      px: 1.5,
                      '& .MuiChip-label': {
                        px: 0.5
                      }
                    }} 
                  />
                </Box>
              )}
            </Box>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                size="small"
                variant="outlined"
                startIcon={<Eye size={14} />}
                onClick={() => setViewScoreCardOpen(true)}
                sx={{
                  borderColor: '#e0e0e0',
                  color: 'text.secondary',
                  fontSize: '0.75rem',
                  px: 1.5,
                  flex: 1
                }}
              >
                View
              </Button>
              <Button
                size="small"
                variant="outlined"
                startIcon={<Edit size={14} />}
                onClick={() => setEditScoreCardOpen(true)}
                sx={{
                  borderColor: '#e0e0e0',
                  color: 'text.secondary',
                  fontSize: '0.75rem',
                  px: 1.5,
                  flex: 1
                }}
              >
                Edit
              </Button>
            </Box>
          </Card>
        </Grid>
      </Grid>

      {/* Content Area */}
      {isLoading ? (
        /* Show appropriate skeleton based on current view mode */
        viewMode === 'task' ? (
          <Box sx={{ pb: 2 }}>
            <TasksViewSkeleton />
          </Box>
        ) : viewMode === 'data' ? (
          <Box sx={{ pb: 2 }}>
            <DocumentListSkeleton />
          </Box>
        ) : (
          /* Default: Kanban/Stage view skeleton */
          <Box sx={{ mb: 0 }}>
            <KanbanBoardSkeleton />
          </Box>
        )
      ) : viewMode === 'task' ? (
        <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
          <CompanyTasksView
            companyId={id}
            tasksByStage={tasksByStage}
            stages={stages}
            onTasksUpdate={getTasksByStages}
          />
        </Box>
      ) : viewMode === 'data' ? (
        <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', minHeight: 0, ps: 2 }}>
          <DocumentListView
            documents={documents}
            meetings={meetings}
            audioRecordings={audioRecordings}
            scheduledMeetings={scheduledMeetings}
            notes={notes}
            stages={stages}
            companyData={companyData}
            onDocumentClick={(doc) => {
              setSelectedDocument(doc);
              setDocumentPreviewOpen(true);
            }}
            onEditNote={handleEditNote}
            handleUniversalFileUpload={handleUniversalFileUpload}
            recordingStages={recordingStages}
            recordingTimes={recordingTimes}
            startRecording={startRecording}
            stopRecording={stopRecording}
            setCurrentStageForAction={setCurrentStageForAction}
            setSidebarAction={setSidebarAction}
            setSidebarOpen={setSidebarOpen}
            setTextNoteStage={setTextNoteStage}
            setTextNoteTitle={setTextNoteTitle}
            setTextNoteContent={setTextNoteContent}
            setTextNoteDialogOpen={setTextNoteDialogOpen}
            uploadUniversalFile={uploadUniversalFile}
          />
        </Box>
      ) : (
        /* Kanban Board - Stages as Columns */
        <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragStart={handleDragStart}
            onDragEnd={handleDragEnd}
          >
            <Box sx={{ backgroundColor: '#f5f5f5', p: 2, pb: 1, borderRadius: 1, mb: 0, display: 'flex', flexDirection: 'column', flexGrow: 1, minHeight: '100%' }}>
              <Box sx={{ display: 'flex', gap: 2, overflowX: 'auto', alignItems: 'stretch', minHeight: '100%', pb: 2, flexGrow: 1 }}>
                {stages
                  .filter((stage) => {
                    const stageName = stage.name || '';
                    // Only hide stages that have exactly " 0%" or " 5%" or start with "0%" or "5%"
                    return !(stageName.includes(' 0%') || stageName.includes(' 5%') ||
                      stageName.startsWith('0%') || stageName.startsWith('5%'));
                  })
                  .map((stage) => {
                    const stageDocuments = documents.filter(d => {
                      // More flexible stage matching
                      const docStage = d.stage;
                      const stageNameLower = stage.name.toLowerCase();
                      const docStageLower = docStage ? docStage.toLowerCase() : '';

                      // Exact match first
                      if (docStage === stage.name) {
                        return true;
                      }

                      // Use exact matching to avoid cross-stage document placement
                      const match = docStage === stage.name ||
                        docStage === stage.id ||
                        docStageLower === stageNameLower ||
                        (!docStage && stageNameLower.includes('intro')); // Default to INTRO stage for items without stage

                      return match;
                    });

                    // Get IDs of recordings linked to scheduled meetings to avoid duplicates
                    const scheduledMeetingRecordingIds = scheduledMeetings
                      .filter(sm => sm.recording_document?.id)
                      .map(sm => sm.recording_document.id);

                    const stageMeetings = meetings.filter(m => {
                      // Exclude meetings that are already shown as part of a scheduled meeting
                      if (scheduledMeetingRecordingIds.includes(m.id)) {
                        return false;
                      }

                      const meetingStage = m.stage;
                      const stageNameLower = stage.name.toLowerCase();
                      const meetingStageLower = meetingStage ? meetingStage.toLowerCase() : '';

                      const match = meetingStage === stage.name ||
                        meetingStage === stage.id ||
                        meetingStageLower === stageNameLower ||
                        (meetingStageLower.includes('intro') && stageNameLower.includes('intro')) ||
                        (meetingStageLower.includes('introduction') && stageNameLower.includes('intro')) ||
                        (!meetingStage && stageNameLower.includes('intro'));
                      return match;
                    });

                    const stageAudioRecordings = audioRecordings.filter(a => {
                      const audioStage = a.stage;
                      const stageNameLower = stage.name.toLowerCase();
                      const audioStageLower = audioStage ? audioStage.toLowerCase() : '';

                      const match = audioStage === stage.name ||
                        audioStage === stage.id ||
                        audioStageLower === stageNameLower ||
                        (audioStageLower.includes('intro') && stageNameLower.includes('intro')) ||
                        (audioStageLower.includes('introduction') && stageNameLower.includes('intro')) ||
                        (!audioStage && stageNameLower.includes('intro'));
                      return match;
                    });

                    const stageScheduledMeetings = scheduledMeetings.filter(sm => {
                      const meetingStage = sm.stage;
                      const stageNameLower = stage.name.toLowerCase();
                      const meetingStageLower = meetingStage ? meetingStage.toLowerCase() : '';

                      const match = meetingStage === stage.name ||
                        meetingStage === stage.id ||
                        meetingStageLower === stageNameLower ||
                        (meetingStageLower.includes('intro') && stageNameLower.includes('intro')) ||
                        (meetingStageLower.includes('introduction') && stageNameLower.includes('intro')) ||
                        (!meetingStage && stageNameLower.includes('intro'));
                      return match;
                    });

                    const stageNotes = notes.filter(n => {
                      const noteStage = n.stage;
                      const stageNameLower = (stage.name || '').toLowerCase();
                      const noteStageLower = noteStage ? noteStage.toLowerCase() : '';
                      const noteStageIdNum = n.stage_id != null ? Number(n.stage_id) : null;
                      const stageIdNum = stage.id != null ? Number(stage.id) : null;

                      const idMatch = noteStageIdNum !== null && stageIdNum !== null && noteStageIdNum === stageIdNum;
                      const nameMatch = !!noteStage && (noteStage === stage.name || noteStageLower === stageNameLower);
                      const introFallbackMatch = (!noteStage && stageNameLower.includes('intro')) ||
                        (noteStageLower.includes('intro') && stageNameLower.includes('intro')) ||
                        (noteStageLower.includes('introduction') && stageNameLower.includes('intro'));

                      const match = idMatch || nameMatch || introFallbackMatch;
                      return match;
                    });

                    const stageData = tasksByStage[stage.name];
                    const stageTasks = stageData ? stageData.tasks : [];
                    // Remove isCurrentStage logic since we're using API stages

                    const totalItems = stageDocuments.length + stageMeetings.length + stageAudioRecordings.length + stageScheduledMeetings.length + stageNotes.length + stageTasks.length;

                    // Check if this stage is locked (company at 100% for 10+ days and this is not the 100% stage)
                    const stageLocked = isStageLockedForActions(companyData, stage);

                    return (
                      <Box key={stage.id} sx={{
                        minWidth: 320,
                        maxWidth: 320,
                        display: 'flex',
                        flexDirection: 'column',
                        transition: 'all 0.3s ease',
                        minHeight: '100%'
                      }}>
                        <Card sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          border: '1px solid #e0e0e0',
                          height: '100%',
                          overflow: 'hidden'
                        }}>
                          <CardHeader
                            title={
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                  <Typography
                                    variant="h6"
                                    sx={{
                                      fontWeight: 700,
                                      fontSize: '0.95rem',
                                      color: '#1e293b',
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis',
                                      whiteSpace: 'nowrap',
                                      letterSpacing: '-0.01em'
                                    }}
                                  >
                                    {getStageDisplayName(stage.name)}
                                  </Typography>
                                </Box>
                                <Chip
                                  size="small"
                                  label={stageDocuments.length + stageMeetings.length + stageAudioRecordings.length + stageScheduledMeetings.length + stageNotes.length + stageTasks.length}
                                  sx={{
                                    ml: 1,
                                    height: 22,
                                    fontSize: '0.7rem',
                                    fontWeight: 700,
                                    bgcolor: '#f1f5f9',
                                    color: '#475569',
                                    border: '1px solid #e2e8f0',
                                    '& .MuiChip-label': {
                                      px: 1.2
                                    }
                                  }}
                                />
                              </Box>
                            }
                            sx={{
                              pb: 0.5,
                              pt: 1,
                              px: 1.5
                            }}
                          />

                          {/* Role Assignments Card */}
                          {(() => {
                            // Get roles that are valid for this stage
                            const stageRoles = getFilteredRolesByStage(stage.name, userRoles);
                            const stageRoleIds = new Set(stageRoles.map(r => r.id?.toString()));

                            // Filter assignments to only show those with roles valid for this stage
                            const stageAssignments = companyTeamAssignments.filter(assignment => {
                              return stageRoleIds.has(assignment.role?.toString());
                            });

                            if (stageAssignments.length === 0) return null;

                            // Group by role type (PM, SAM, etc.)
                            const roleGroups = {};
                            stageAssignments.forEach(assignment => {
                              const role = userRoles.find(r => r.id?.toString() === assignment.role?.toString());
                              const user = teamMembers.find(m => m.pk?.toString() === assignment.user?.toString());

                              if (role && user) {
                                const roleLabel = role.label || role.name;
                                const userName = `${user.first_name || ''} ${user.last_name || ''}`.trim() || assignment.user_name || 'User';

                                if (!roleGroups[roleLabel]) {
                                  roleGroups[roleLabel] = [];
                                }
                                roleGroups[roleLabel].push(userName);
                              }
                            });

                            return (
                              <Box sx={{ px: 1.5, pb: 1 }}>
                                <Card
                                  elevation={0}
                                  sx={{
                                    bgcolor: '#f8fafc',
                                    border: '1px solid #e2e8f0',
                                    borderRadius: 1.5,
                                    overflow: 'visible',
                                    transition: 'all 0.2s ease',
                                    '&:hover': {
                                      borderColor: '#cbd5e1',
                                      boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
                                    }
                                  }}
                                >
                                  <CardContent sx={{
                                    p: 1,
                                    '&:last-child': { pb: 1 }
                                  }}>
                                    <Box sx={{
                                      display: 'flex',
                                      justifyContent: 'space-between',
                                      alignItems: Object.entries(roleGroups).length === 1 ? 'center' : 'flex-start',
                                      gap: 1.5
                                    }}>
                                      <Box sx={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        gap: 1,
                                        flex: 1,
                                        minWidth: 0
                                      }}>
                                        {Object.entries(roleGroups)
                                          .sort(([roleA], [roleB]) => {
                                            // Sort to show BM (SAM) first, then BA (PM), then others.
                                            // roleGroups keys are display labels (now BM/BA).
                                            const roleALower = roleA.toLowerCase();
                                            const roleBLower = roleB.toLowerCase();

                                            if (roleALower === 'bm') return -1;
                                            if (roleBLower === 'bm') return 1;
                                            if (roleALower === 'ba') return -1;
                                            if (roleBLower === 'ba') return 1;
                                            return 0;
                                          })
                                          .map(([roleLabel, users]) => {
                                            return (
                                              <Box
                                                key={roleLabel}
                                                sx={{
                                                  display: 'flex',
                                                  alignItems: 'center',
                                                  gap: 1,
                                                  minHeight: 'auto'
                                                }}
                                              >
                                                <Typography
                                                  variant="caption"
                                                  sx={{
                                                    fontSize: '0.7rem',
                                                    fontWeight: 700,
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: 0.5,
                                                    letterSpacing: '0.02em',
                                                    minWidth: 'fit-content'
                                                  }}
                                                >
                                                  <span style={{ fontSize: '0.9em' }}>{roleLabel}</span>:
                                                </Typography>
                                                <Typography
                                                  variant="caption"
                                                  sx={{
                                                    fontSize: '0.7rem',
                                                    fontWeight: 600,
                                                    color: '#1e293b',
                                                    lineHeight: 1.4,
                                                    wordBreak: 'break-word'
                                                  }}
                                                >
                                                  {users.join(', ')}
                                                </Typography>
                                              </Box>
                                            );
                                          })}
                                      </Box>

                                      {/* Edit Button */}
                                      <IconButton
                                        size="small"
                                        onClick={() => {
                                          setSelectedStage(stage);
                                          setEditTeamOpen(true);
                                        }}
                                        sx={{
                                          width: 28,
                                          height: 28,
                                          bgcolor: '#fff',
                                          border: '1px solid #e2e8f0',
                                          flexShrink: 0,
                                          '&:hover': {
                                            bgcolor: '#f1f5f9',
                                            borderColor: '#1976d2',
                                            '& svg': {
                                              color: '#1976d2'
                                            }
                                          }
                                        }}
                                      >
                                        <Edit2 size={14} style={{ color: '#64748b' }} />
                                      </IconButton>
                                    </Box>
                                  </CardContent>
                                </Card>
                              </Box>
                            );
                          })()}

                          <DroppableColumn
                            stageId={stage.id}
                            stageName={stage.name}
                            stage={stage}
                            onFileDrop={uploadUniversalFile}
                          >
                            <CardContent sx={{
                              pt: 0,
                              px: 1.5,
                              pb: 1,
                              flex: 1,
                              overflowY: 'auto',
                              minHeight: 0,
                              maxHeight: 'calc(100vh - 405px)',
                              '&::-webkit-scrollbar': {
                                width: '6px'
                              },
                              '&::-webkit-scrollbar-track': {
                                background: '#f1f1f1',
                                borderRadius: '10px'
                              },
                              '&::-webkit-scrollbar-thumb': {
                                background: '#cbd5e1',
                                borderRadius: '10px',
                                '&:hover': {
                                  background: '#94a3b8'
                                }
                              }
                            }}>
                              {/* Top Drop Zone */}
                              <TopDropZone stageId={stage.id} stageName={stage.name} isDragging={!!activeId} />

                              {/* Upload Progress Bar */}
                              {uploadProgress[`stage-${stage.id}`] !== null && uploadProgress[`stage-${stage.id}`] !== undefined && (
                                <Box sx={{ mb: 2, p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
                                  <Typography variant="caption" sx={{ display: 'block', mb: 1, fontWeight: 600 }}>
                                    Uploading... {uploadProgress[`stage-${stage.id}`]}%
                                  </Typography>
                                  <Box sx={{ width: '100%', bgcolor: '#e0e0e0', borderRadius: 1, height: 8 }}>
                                    <Box sx={{
                                      width: `${uploadProgress[`stage-${stage.id}`]}%`,
                                      bgcolor: '#1976d2',
                                      height: '100%',
                                      borderRadius: 1,
                                      transition: 'width 0.3s ease'
                                    }} />
                                  </Box>
                                </Box>
                              )}

                              {/* Content based on view mode */}
                              {(viewMode === 'stage' || viewMode === 'data') && (
                                <>
                                {/* Scheduled Meetings - Independent and non-draggable */}
                                {stageScheduledMeetings.map((scheduledMeeting) => (
                                  <DraggableScheduledMeetingCard 
                                    key={scheduledMeeting.id} 
                                    scheduledMeeting={scheduledMeeting} 
                                    formatDateTime={formatDateTime} 
                                    matches={matches}
                                    companyId={id}
                                    stageId={stage.id}
                                    onMeetingCancelled={getJourneyData}
                                  />
                                ))}
                        
                                <SortableContext                           
                                  items={[
                                    ...stageDocuments.filter(d => d.id).map(d => d.id.toString()),
                                    ...stageMeetings.filter(m => m.id).map(m => m.id.toString()),
                                    ...stageAudioRecordings.filter(a => a.id).map(a => a.id.toString()),
                                    ...stageNotes.filter(n => n.id).map(n => n.id.toString())
                                  ]} 
                                  strategy={verticalListSortingStrategy}
                                >
                                  {/* Documents */}
                                  {stageDocuments.map((doc) => (
                                    <DraggableDocumentCard key={doc.id} document={doc} matches={matches} formatDateTime={formatDateTime} onDelete={handleDeleteDocument} companyId={id} stageId={stage.id} />
                                  ))}

                                  {/* Meetings */}
                                  {stageMeetings.map((meeting) => (
                                    <DraggableMeetingCard key={meeting.id} meeting={meeting} formatDateTime={formatDateTime} onDelete={handleDeleteMeeting} companyId={id} stageId={stage.id} />
                                  ))}

                                  {/* Audio Recordings */}
                                  {stageAudioRecordings.map((audio) => (
                                    <DraggableAudioCard key={audio.id} audio={audio} formatDateTime={formatDateTime} onDelete={handleDeleteAudio} />
                                  ))}

                                  {/* Text Notes */}
                                  {stageNotes.map((note) => (
                                    <NoteCard key={note.id} note={note} formatDateTime={formatDateTime} onEdit={handleEditNote} onDelete={handleDeleteNote} />
                                  ))}
                                </SortableContext>
                                </>
                              )}

                              {/* Tasks - Show in stage view mode */}
                              {viewMode === 'stage' && stageTasks.map((task) => (
                                <CompanyTaskCard key={task.id} task={task} onStatusChange={handleTaskStatusChange} />
                              ))}

                              {/* Empty State */}
                              {((viewMode === 'stage' && stageDocuments.length === 0 && stageMeetings.length === 0 && stageAudioRecordings.length === 0 && stageScheduledMeetings.length === 0 && stageNotes.length === 0 && stageTasks.length === 0) ||
                                (viewMode === 'data' && stageDocuments.length === 0 && stageMeetings.length === 0 && stageAudioRecordings.length === 0 && stageScheduledMeetings.length === 0 && stageNotes.length === 0)) && (
                                  <Box sx={{ textAlign: 'center', py: 4, color: 'text.secondary' }}>
                                    <Typography variant="caption">
                                      {viewMode === 'data' ? 'No items yet' : 'No items yet'}
                                    </Typography>
                                  </Box>
                                )}
                            </CardContent>
                          </DroppableColumn>

                          {/* Action Buttons */}
                          {(viewMode === 'stage' || viewMode === 'data') && (
                            <Box sx={{ p: 1, px: 1.5, borderTop: '1px solid #e0e0e0', opacity: stageLocked ? 0.5 : 1 }}>
                              {/* Show locked message if stage is locked */}
                              {stageLocked && (
                                <Typography variant="caption" sx={{ display: 'block', textAlign: 'center', color: '#9e9e9e', mb: 0.5, fontSize: '0.7rem' }}>
                                  🔒 Editing disabled - Company has been at 100% stage for over 10 days
                                </Typography>
                              )}
                              <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                                <Tooltip title={stageLocked ? "Editing disabled - Company at 100% for 10+ days" : "Upload Video"} arrow>
                                  <span>
                                    <IconButton
                                      size="small"
                                      component="label"
                                      disabled={stageLocked}
                                      sx={{
                                        backgroundColor: stageLocked ? '#f5f5f5' : '#ffebee',
                                        color: stageLocked ? '#bdbdbd' : '#d32f2f',
                                        width: 36,
                                        height: 36,
                                        borderRadius: 2,
                                        '&:hover': { backgroundColor: stageLocked ? '#f5f5f5' : '#ffcdd2' },
                                        cursor: stageLocked ? 'not-allowed' : 'pointer'
                                      }}
                                    >
                                      <Video size={18} />
                                      {!stageLocked && (
                                        <input
                                          type="file"
                                          hidden
                                          accept="video/*,.mp4,.avi,.mov,.webm,.mkv,.flv,.wmv,.m4a"
                                          onChange={(e) => handleUniversalFileUpload(e, stage)}
                                        />
                                      )}
                                    </IconButton>
                                  </span>
                                </Tooltip>

                                <Tooltip title={stageLocked ? "Editing disabled - Company at 100% for 10+ days" : "Upload Document"} arrow>
                                  <span>
                                    <IconButton
                                      size="small"
                                      component="label"
                                      disabled={stageLocked}
                                      sx={{
                                        backgroundColor: stageLocked ? '#f5f5f5' : '#e3f2fd',
                                        color: stageLocked ? '#bdbdbd' : '#1976d2',
                                        width: 36,
                                        height: 36,
                                        borderRadius: 2,
                                        '&:hover': { backgroundColor: stageLocked ? '#f5f5f5' : '#bbdefb' },
                                        cursor: stageLocked ? 'not-allowed' : 'pointer'
                                      }}
                                    >
                                      <FileText size={18} />
                                      {!stageLocked && (
                                        <input
                                          type="file"
                                          hidden
                                          accept=".pdf,.doc,.docx,.ppt,.pptx,.xls,.xlsx,.txt,.csv,.html,.htm"
                                          onChange={(e) => handleUniversalFileUpload(e, stage)}
                                        />
                                      )}
                                    </IconButton>
                                  </span>
                                </Tooltip>

                                {recordingStages[stage.id] ? (
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1, backgroundColor: '#ffebee', borderRadius: 1 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                      <Box sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: '#f44336', animation: 'pulse 1s infinite' }} />
                                      <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
                                        {Math.floor((recordingTimes[stage.id] || 0) / 60)}:{((recordingTimes[stage.id] || 0) % 60).toString().padStart(2, '0')}
                                      </Typography>
                                    </Box>
                                    <IconButton size="small" onClick={() => stopRecording(stage)} sx={{ color: '#f44336' }}>
                                      <Box sx={{ width: 12, height: 12, backgroundColor: '#f44336' }} />
                                    </IconButton>
                                  </Box>
                                ) : (
                                  <Tooltip title={stageLocked ? "Editing disabled - Company at 100% for 10+ days" : "Record Audio"} arrow>
                                    <span>
                                      <IconButton
                                        size="small"
                                        disabled={stageLocked}
                                        onClick={() => !stageLocked && startRecording(stage)}
                                        sx={{
                                          backgroundColor: stageLocked ? '#f5f5f5' : '#fff3e0',
                                          color: stageLocked ? '#bdbdbd' : '#f57c00',
                                          width: 36,
                                          height: 36,
                                          borderRadius: 2,
                                          '&:hover': { backgroundColor: stageLocked ? '#f5f5f5' : '#ffe0b2' },
                                          cursor: stageLocked ? 'not-allowed' : 'pointer'
                                        }}
                                      >
                                        <Mic size={18} />
                                      </IconButton>
                                    </span>
                                  </Tooltip>
                                )}

                                {/* Text Note */}
                                <Tooltip title={stageLocked ? "Editing disabled - Company at 100% for 10+ days" : "Text Note"} arrow>
                                  <span>
                                    <IconButton
                                      size="small"
                                      disabled={stageLocked}
                                      onClick={() => {
                                        if (stageLocked) return;
                                        setTextNoteStage(stage);
                                        setTextNoteTitle('');
                                        setTextNoteContent('');
                                        setTextNoteDialogOpen(true);
                                      }}
                                      sx={{
                                        backgroundColor: stageLocked ? '#f5f5f5' : '#fff3e0',
                                        color: stageLocked ? '#bdbdbd' : '#f57c00',
                                        width: 36,
                                        height: 36,
                                        borderRadius: 2,
                                        '&:hover': { backgroundColor: stageLocked ? '#f5f5f5' : '#ffe0b2' },
                                        cursor: stageLocked ? 'not-allowed' : 'pointer'
                                      }}
                                    >
                                      <StickyNote size={18} />
                                    </IconButton>
                                  </span>
                                </Tooltip>

                                <Tooltip title={stageLocked ? "Editing disabled - Company at 100% for 10+ days" : "Assign Team"} arrow>
                                  <span>
                                    <IconButton
                                      size="small"
                                      disabled={stageLocked}
                                      onClick={() => {
                                        if (stageLocked) return;
                                        setSelectedStage(stage);
                                        setEditTeamOpen(true);
                                      }}
                                      sx={{
                                        backgroundColor: stageLocked ? '#f5f5f5' : '#f3e5f5',
                                        color: stageLocked ? '#bdbdbd' : '#7b1fa2',
                                        width: 36,
                                        height: 36,
                                        borderRadius: 2,
                                        '&:hover': { backgroundColor: stageLocked ? '#f5f5f5' : '#e1bee7' },
                                        cursor: stageLocked ? 'not-allowed' : 'pointer'
                                      }}
                                    >
                                      <Users size={18} />
                                    </IconButton>
                                  </span>
                                </Tooltip>

                                <Tooltip title={stageLocked ? "Editing disabled - Company at 100% for 10+ days" : "Create Task"} arrow>
                                  <span>
                                    <IconButton
                                      size="small"
                                      disabled={stageLocked}
                                      onClick={() => {
                                        if (stageLocked) return;
                                        setCurrentStageForAction(stage);
                                        setSidebarAction('task');
                                        setSidebarOpen(true);
                                      }}
                                      sx={{
                                        backgroundColor: stageLocked ? '#f5f5f5' : '#e8f5e8',
                                        color: stageLocked ? '#bdbdbd' : '#388e3c',
                                        width: 36,
                                        height: 36,
                                        borderRadius: 2,
                                        '&:hover': { backgroundColor: stageLocked ? '#f5f5f5' : '#c8e6c9' },
                                        cursor: stageLocked ? 'not-allowed' : 'pointer'
                                      }}
                                    >
                                      <CheckSquare size={18} />
                                    </IconButton>
                                  </span>
                                </Tooltip>

                                <Tooltip title={stageLocked ? "Editing disabled - Company at 100% for 10+ days" : "Schedule Meeting"} arrow>
                                  <span>
                                    <IconButton
                                      size="small"
                                      disabled={stageLocked}
                                      onClick={() => {
                                        if (stageLocked) return;
                                        setCurrentStageForAction(stage);
                                        setSidebarAction('meeting');
                                        setSidebarOpen(true);
                                      }}
                                      sx={{
                                        backgroundColor: stageLocked ? '#f5f5f5' : '#e3f2fd',
                                        color: stageLocked ? '#bdbdbd' : '#1976d2',
                                        width: 36,
                                        height: 36,
                                        borderRadius: 2,
                                        '&:hover': { backgroundColor: stageLocked ? '#f5f5f5' : '#bbdefb' },
                                        cursor: stageLocked ? 'not-allowed' : 'pointer'
                                      }}
                                    >
                                      <CalendarToday sx={{ fontSize: 18 }} />
                                    </IconButton>
                                  </span>
                                </Tooltip>
                              </Box>
                            </Box>
                          )}
                        </Card>
                      </Box>
                    );
                  })
                }
              </Box>
              <DragOverlay>
                {activeId && draggedItem ? (
                  draggedItem.type === 'document' ? (
                    <DocumentCard document={draggedItem.item} matches={matches} />
                  ) : draggedItem.type === 'meeting' ? (
                    <MeetingCard meeting={draggedItem.item} formatDateTime={formatDateTime} />
                  ) : (
                    <AudioCard audio={draggedItem.item} formatDateTime={formatDateTime} />
                  )
                ) : null}
              </DragOverlay>
            </Box>
          </DndContext>
        </Box>
      )}

            {/* AI Pitch Floating Button - Only show for GWX Agent created companies */}
            {isCreatedByAgent(companyData) && (
              <Tooltip title="Get AI Pitch" placement="left">
                <Fab
                  color="primary"
                  aria-label="ai-pitch"
                  sx={{
                    position: 'fixed',
                    bottom: 32,
                    right: 32,
                    backgroundColor: '#1976d2',
                    '&:hover': { backgroundColor: '#1565c0' },
                    zIndex: 1200
                  }}
                  onClick={handleAIPitchClick}
                >
                  <AutoAwesome />
                </Fab>
              </Tooltip>
            )}

            {/* Edit Company Drawer */}
      <Drawer
        anchor="right"
        open={editCompanyOpen}
        onClose={() => {
          setEditCompanyOpen(false);
          setAddCompanyDefaultTab(null);
        }}
        PaperProps={{
          sx: { width: '50%', minWidth: 500 }
        }}
      >
        <AddCompany
          id={companyData?.id}
          setOpenDrawer={(value) => {
            setEditCompanyOpen(value);
            if (!value) setAddCompanyDefaultTab(null);
          }}
          companyData={companyData}
          stages={stages}
          onCompanyUpdate={(updatedData) => setCompanyData(updatedData)}
          defaultTab={addCompanyDefaultTab}
        />
      </Drawer>

      {/* Edit Team Drawer */}
      <Drawer
        anchor="right"
        open={editTeamOpen}
        onClose={(event, reason) => {
          // Prevent closing when clicking outside or pressing Escape
          if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
            return;
          }
          setEditTeamOpen(false);
          setPendingStageChange(null); // Clear pending stage change if user cancels
          qmExitCommentToSubmitRef.current = ''; // Discard abandoned QM-exit note on cancel
          financialsCapturedRef.current = false; // B1/FR-012: re-arm the financial-capture gate on cancel
        }}
      >
        <TeamAssignmentDrawer
          stage={selectedStage}
          teamMembers={teamMembers}
          companyId={id}
          userRoles={userRoles}
          companyData={companyData}
          getFilteredTeamMembersForRole={getFilteredTeamMembersForRole}
          onClose={async () => {
            setEditTeamOpen(false);
            setPendingStageChange(null); // Clear pending stage change if user cancels
            qmExitCommentToSubmitRef.current = ''; // Discard abandoned QM-exit note on cancel
            financialsCapturedRef.current = false; // B1/FR-012: re-arm the financial-capture gate on cancel
            // Refresh team assignments to update avatars
            await getCompanyTeamAssignments();
          }}
          onTeamAssignmentComplete={async (stageId) => {
            // US-4: snapshot the QM-exit note BEFORE any await. The drawer's handleComplete
            // fires this callback unawaited and then calls onClose() synchronously, which clears
            // the ref — so reading the ref after an await would drop the note on the Save path.
            const qmExitNote = qmExitCommentToSubmitRef.current;
            // Close the drawer first
            setEditTeamOpen(false);

            // Refresh team assignments
            await getCompanyTeamAssignments();

            // Check if there's a pending stage change to complete
            if (pendingStageChange && pendingStageChange.stageId) {
              const targetStage = pendingStageChange.stage;
              const teamValidation = await checkTeamAssignments(pendingStageChange.stageId, targetStage.name || targetStage.label);

              if (teamValidation.isValid) {
                // All required roles assigned, proceed with stage change
                try {
                  const response = await apiInstance.patch(`company/${id}/update-stage/`, {
                    stage_id: pendingStageChange.stageId,
                    ...(qmExitNote ? { comment: qmExitNote } : {})
                  }, {
                    headers: { Authorization: `Token ${localStorage.getItem('authToken')}` }
                  });

                  // Update local state
                  setCompanyData(prev => ({
                    ...prev,
                    current_stage: pendingStageChange.stageId,
                    current_stage_id: pendingStageChange.stageId,
                    current_stage_name: targetStage.name || targetStage.label || '',
                    stage: targetStage.name || targetStage.label || '',
                    status: targetStage.name || targetStage.label || ''
                  }));

                  // US-4: persist QM-exit note locally + clear the ref so we don't re-send (FR-013)
                  if (qmExitNote) {
                    setCompanyData(prev => ({ ...prev, qm_exit_comment: prev.qm_exit_comment || qmExitNote }));
                  }
                  qmExitCommentToSubmitRef.current = '';
                  financialsCapturedRef.current = false; // B1/FR-012: re-arm the financial-capture gate so the NEXT gated move re-prompts

                  showToast('Team assignments completed! Stage changed successfully.', 'success');
                  setPendingStageChange(null);
                } catch (error) {
                  console.error('Error updating company stage:', error);
                  qmExitCommentToSubmitRef.current = ''; // failed move → discard note so the user is re-prompted on retry
                  financialsCapturedRef.current = false; // B1/FR-012: re-arm the financial-capture gate after a failed move
                  showErrorToast('Error updating company stage: ' + (error.response?.data?.message || error.message));
                }
              } else {
                // Still missing required roles
                const missingRolesList = teamValidation.missingRoles.join(', ');
                showErrorToast(`Required roles must be assigned: ${missingRolesList}`);
              }
            }
          }}
        />
      </Drawer>

      {/* Task Assignment Drawer for 10% Intro Call Stage */}
      <Drawer
        anchor="right"
        open={taskAssignmentDrawerOpen}
        onClose={(event, reason) => {
          // Prevent closing when clicking outside or pressing Escape
          if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
            return;
          }
          setTaskAssignmentDrawerOpen(false);
          setPendingStageChange(null); // Clear pending stage change if user cancels
          qmExitCommentToSubmitRef.current = ''; // Discard abandoned QM-exit note on cancel
          financialsCapturedRef.current = false; // B1/FR-012: re-arm the financial-capture gate on cancel
        }}
      >
        <TaskAssignmentDrawer
          stage={selectedStage}
          companyId={id}
          companyData={companyData}
          teamMembers={teamMembers}
          onClose={() => {
            setTaskAssignmentDrawerOpen(false);
            setPendingStageChange(null); // Clear pending stage change if user cancels
            qmExitCommentToSubmitRef.current = ''; // Discard abandoned QM-exit note on cancel
            financialsCapturedRef.current = false; // B1/FR-012: re-arm the financial-capture gate on cancel
          }}
          onTaskAssignmentComplete={async (stageId) => {
            // US-4: snapshot the QM-exit note BEFORE any await (mirror the team drawer — defensive
            // against onClose clearing the ref before this callback's awaited PATCH reads it).
            const qmExitNote = qmExitCommentToSubmitRef.current;
            // Close the drawer
            setTaskAssignmentDrawerOpen(false);

            // Check if there's a pending stage change to complete
            if (pendingStageChange && pendingStageChange.stageId) {
              const targetStage = pendingStageChange.stage;

              try {
                // Proceed with stage change
                const response = await apiInstance.patch(`company/${id}/update-stage/`, {
                  stage_id: pendingStageChange.stageId,
                  ...(qmExitNote ? { comment: qmExitNote } : {})
                }, {
                  headers: { Authorization: `Token ${localStorage.getItem('authToken')}` }
                });

                // Update local state
                setCompanyData(prev => ({
                  ...prev,
                  current_stage: pendingStageChange.stageId,
                  current_stage_id: pendingStageChange.stageId,
                  current_stage_name: targetStage.name || targetStage.label || '',
                  stage: targetStage.name || targetStage.label || '',
                  status: targetStage.name || targetStage.label || ''
                }));

                // US-4: persist QM-exit note locally + clear the ref so we don't re-send (FR-013)
                if (qmExitNote) {
                  setCompanyData(prev => ({ ...prev, qm_exit_comment: prev.qm_exit_comment || qmExitNote }));
                }
                qmExitCommentToSubmitRef.current = '';
                financialsCapturedRef.current = false; // B1/FR-012: re-arm the financial-capture gate so the NEXT gated move re-prompts

                showToast('Task assigned successfully! Stage changed to ' + (targetStage.name || targetStage.label), 'success');
                setPendingStageChange(null);

                // Refresh journey data to show the new task
                await getJourneyData();
              } catch (error) {
                console.error('Error updating company stage:', error);
                qmExitCommentToSubmitRef.current = ''; // failed move → discard note so the user is re-prompted on retry
                financialsCapturedRef.current = false; // B1/FR-012: re-arm the financial-capture gate after a failed move
                showErrorToast('Error updating company stage: ' + (error.response?.data?.message || error.message));
              }
            }
          }}
        />
      </Drawer>

      {/* View Calling Card Drawer */}
      <Drawer anchor="right" open={viewCallingCardOpen} onClose={() => setViewCallingCardOpen(false)}>
        <CallingCardDrawer
          callingCardData={callingCardData}
          onClose={() => setViewCallingCardOpen(false)}
          onSave={saveCallingCard}
          mode="view"
          onEdit={() => {
            setViewCallingCardOpen(false);
            setEditCallingCardOpen(true);
          }}
        />
      </Drawer>

      {/* Edit Calling Card Drawer */}
      <Drawer anchor="right" open={editCallingCardOpen} onClose={() => setEditCallingCardOpen(false)}>
        <CallingCardDrawer
          callingCardData={callingCardData}
          onClose={() => setEditCallingCardOpen(false)}
          onSave={saveCallingCard}
          mode="edit"
        />
      </Drawer>

      {/* View Score Card Drawer */}
      <Drawer anchor="right" open={viewScoreCardOpen} onClose={() => setViewScoreCardOpen(false)}>
        <ScoreCardDrawer
          scoreCardData={scoreCardData}
          onClose={() => setViewScoreCardOpen(false)}
          onSave={saveScoreCard}
          mode="view"
        />
      </Drawer>

      {/* Edit Score Card Drawer */}
      <Drawer anchor="right" open={editScoreCardOpen} onClose={() => setEditScoreCardOpen(false)}>
        <ScoreCardDrawer
          scoreCardData={scoreCardData}
          onClose={() => setEditScoreCardOpen(false)}
          onSave={saveScoreCard}
          mode="edit"
        />
      </Drawer>

      {/* View Closed Deal Details Drawer */}
      <Drawer anchor="right" open={viewClosedDealOpen} onClose={() => setViewClosedDealOpen(false)}>
        <ClosedDealDetailsDrawer
          closedDealDetails={closedDealDetails}
          onClose={() => setViewClosedDealOpen(false)}
          onSave={saveClosedDealDetails}
          mode="view"
          onEdit={() => {
            setViewClosedDealOpen(false);
            setEditClosedDealOpen(true);
          }}
        />
      </Drawer>

      {/* Edit Closed Deal Details Drawer */}
      <Drawer anchor="right" open={editClosedDealOpen} onClose={() => {
        setEditClosedDealOpen(false);
        setPendingStageChange(null); // Clear pending stage change if user cancels
      }}>
        <ClosedDealDetailsDrawer
          closedDealDetails={closedDealDetails}
          onClose={() => {
            setEditClosedDealOpen(false);
            setPendingStageChange(null); // Clear pending stage change if user cancels
          }}
          onSave={saveClosedDealDetails}
          isStageChangeValidation={!!pendingStageChange}
          mode="edit"
        />
      </Drawer>

      {/* Add Card Drawer */}
      <Drawer anchor="right" open={addCardOpen} onClose={() => setAddCardOpen(false)}>
        <AddCardDrawer
          selectedAction={selectedAction}
          setSelectedAction={setSelectedAction}
          teamMembers={teamMembers}
          companyId={id}
          onClose={() => setAddCardOpen(false)}
          getJourneyData={getJourneyData}
        />
      </Drawer>

      {/* Text Note Dialog */}
      <TextNoteDialog
        open={textNoteDialogOpen}
        onClose={handleCloseTextNoteDialog}
        noteTitle={textNoteTitle}
        onTitleChange={setTextNoteTitle}
        noteContent={textNoteContent}
        onContentChange={setTextNoteContent}
        onSave={handleSaveTextNote}
        isListening={isListening}
        onStartListening={handleStartListening}
        onStopListening={handleStopListening}
        isSaving={isSavingNote}
        stageName={textNoteStage?.label || ''}
        isEditing={!!editingNoteId}
      />

      {/* Delete Note Confirmation Dialog */}
      <Dialog
        open={deleteNoteDialogOpen}
        onClose={() => setDeleteNoteDialogOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ color: '#d32f2f' }}>
          Delete Note?
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Are you sure you want to delete this note?
          </Typography>
          {deletingNote?.title && (
            <Typography variant="body2" sx={{ mt: 1 }}>
              <strong>{deletingNote.title}</strong>
            </Typography>
          )}
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDeleteNoteDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={confirmDeleteNote}
            variant="contained"
            color="error"
            disabled={isDeletingNote}
          >
            {isDeletingNote ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Activity Tracker Drawer */}
      <ActivityTracker
        open={activityDrawerOpen}
        onClose={() => setActivityDrawerOpen(false)}
        companyId={id}
      />

      {/* Action Sidebar */}
      <Drawer anchor="right" open={sidebarOpen} onClose={() => setSidebarOpen(false)}>
        <ActionSidebar
          action={sidebarAction}
          stage={currentStageForAction}
          stages={stages}
          teamMembers={teamMembers}
          companyId={id}
          companyData={companyData}
          companyTeamAssignments={companyTeamAssignments}
          userRoles={userRoles}
          onClose={() => setSidebarOpen(false)}
          getJourneyData={getJourneyData}
          getTasksByStages={getTasksByStages}
        />
      </Drawer>



      {/* Document Preview Drawer */}
      <Drawer
        anchor="right"
        open={documentPreviewOpen}
        onClose={() => setDocumentPreviewOpen(false)}
        PaperProps={{
          sx: {
            width: selectedDocument && (selectedDocument.type === 'audio' || selectedDocument.file?.toLowerCase().match(/\.(mp3|wav|m4a|ogg|aac|flac)$/))
              ? '450px'
              : '60%',
            minWidth: selectedDocument && (selectedDocument.type === 'audio' || selectedDocument.file?.toLowerCase().match(/\.(mp3|wav|m4a|ogg|aac|flac)$/))
              ? '400px'
              : 600,
            maxWidth: selectedDocument && (selectedDocument.type === 'audio' || selectedDocument.file?.toLowerCase().match(/\.(mp3|wav|m4a|ogg|aac|flac)$/))
              ? '500px'
              : 800
          }
        }}
      >
        <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
          <Box sx={{ p: 2, borderBottom: '1px solid #e0e0e0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                {selectedDocument?.name || selectedDocument?.title}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Uploaded by: {selectedDocument?.created_by_name || selectedDocument?.uploaded_by || userInfo?.first_name + ' ' + userInfo?.last_name || 'Unknown User'} • {selectedDocument?.created_at ? new Date(selectedDocument.created_at).toLocaleDateString() : ''}
              </Typography>
            </Box>
            <IconButton onClick={() => setDocumentPreviewOpen(false)}>
              <CloseIcon />
            </IconButton>
          </Box>

          {/* Enhanced UI for Audio/Video files */}
          {selectedDocument && (selectedDocument.type === 'audio' || selectedDocument.type === 'meeting' ||
            selectedDocument.file?.toLowerCase().match(/\.(mp3|wav|m4a|ogg|aac|flac|mp4|avi|mov|webm|mkv)$/)) ? (
            <Box sx={{
              p: selectedDocument.type === 'audio' || selectedDocument.file?.toLowerCase().match(/\.(mp3|wav|m4a|ogg|aac|flac)$/) ? 2 : 3
            }}>
              {/* Media Header with Icon */}
              <Box sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                p: selectedDocument.type === 'audio' || selectedDocument.file?.toLowerCase().match(/\.(mp3|wav|m4a|ogg|aac|flac)$/) ? 2 : 3,
                backgroundColor: '#f8f9fa',
                borderRadius: 2,
                mb: 3,
                border: '1px solid #e0e0e0'
              }}>
                <Box sx={{
                  width: selectedDocument.type === 'audio' || selectedDocument.file?.toLowerCase().match(/\.(mp3|wav|m4a|ogg|aac|flac)$/) ? 48 : 60,
                  height: selectedDocument.type === 'audio' || selectedDocument.file?.toLowerCase().match(/\.(mp3|wav|m4a|ogg|aac|flac)$/) ? 48 : 60,
                  borderRadius: '12px',
                  backgroundColor: '#e3f2fd',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  {selectedDocument.type === 'audio' || selectedDocument.file?.toLowerCase().match(/\.(mp3|wav|m4a|ogg|aac|flac)$/) ?
                    <Mic size={22} color="#1976d2" /> :
                    <Play size={24} color="#1976d2" />
                  }
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="h6" sx={{
                    fontWeight: 600,
                    mb: 0.5,
                    fontSize: selectedDocument.type === 'audio' || selectedDocument.file?.toLowerCase().match(/\.(mp3|wav|m4a|ogg|aac|flac)$/) ? '0.95rem' : '1.1rem',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                  }}>
                    {selectedDocument?.name || selectedDocument?.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{
                    fontSize: '0.875rem'
                  }}>
                    {selectedDocument.type === 'audio' || selectedDocument.file?.toLowerCase().match(/\.(mp3|wav|m4a|ogg|aac|flac)$/) ? 'Audio Recording' : 'Video Recording'}
                  </Typography>
                </Box>
              </Box>

              {/* Media Player */}
              <Box sx={{ mb: 3 }}>
                <MediaViewer
                  file={{ fileUrl: selectedDocument.file }}
                  showPlayBackSpeed={true}
                  matches={matches}
                />
              </Box>

              {/* Metadata */}
              <Box sx={{
                display: 'flex',
                flexDirection: selectedDocument.type === 'audio' || selectedDocument.file?.toLowerCase().match(/\.(mp3|wav|m4a|ogg|aac|flac)$/) ? 'column' : 'row',
                gap: selectedDocument.type === 'audio' || selectedDocument.file?.toLowerCase().match(/\.(mp3|wav|m4a|ogg|aac|flac)$/) ? 2 : 4,
                mb: 2,
                backgroundColor: '#f8f9fa',
                p: 2,
                borderRadius: 2
              }}>
                <Box>
                  <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600, fontSize: '0.8rem', mb: 0.5 }}>Recorded:</Typography>
                  <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                    {selectedDocument?.created_at ? new Date(selectedDocument.created_at).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    }) : selectedDocument?.uploaded_on ? new Date(selectedDocument.uploaded_on).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    }) : new Date().toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600, fontSize: '0.8rem', mb: 0.5 }}>Duration:</Typography>
                  <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                    {selectedDocument?.duration ? `${selectedDocument.duration} minutes` :
                      selectedDocument?.file ? (
                        selectedDocument.file.toLowerCase().match(/\.(mp3|wav|m4a|ogg|aac|flac)$/) ? '2:50' :
                          selectedDocument.file.toLowerCase().match(/\.(mp4|avi|mov|webm|mkv)$/) ? '5:30' : 'N/A'
                      ) : 'N/A'}
                  </Typography>
                </Box>
              </Box>

              <Box>
                <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>Recorded by:</Typography>
                <Typography variant="body2">
                  {selectedDocument?.created_by_name || selectedDocument?.uploaded_by || userInfo?.first_name + ' ' + userInfo?.last_name || 'Unknown User'}
                </Typography>
              </Box>
            </Box>
          ) : (
            <Box sx={{ flex: 1, p: 0 }}>
              {selectedDocument && (
                <MediaViewer
                  file={{ fileUrl: selectedDocument.file }}
                  showPlayBackSpeed={false}
                  matches={matches}
                />
              )}
            </Box>
          )}
        </Box>
      </Drawer>

      {/* Delete Document Confirmation Dialog */}
      <Dialog open={deleteDocDialog.open} onClose={() => setDeleteDocDialog({ open: false, documentId: null })}>
        <DialogTitle>Delete Document</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this document? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDocDialog({ open: false, documentId: null })}>Cancel</Button>
          <Button
            onClick={confirmDeleteDocument}
            variant="contained"
            color="error"
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* 5% Stage Reminder Dialog */}
      <FivePercentReminderDialog
        open={fivePercentReminderDialogOpen}
        onClose={handleFivePercentReminderCancel}
        onSave={handleFivePercentReminderSave}
        onNewsletterSave={handleFivePercentNewsletterSave}
        companyName={companyData?.company_name}
        sourceStage={pendingStageChange?.previousStage?.name}
      />

      {/* Reminder Note View/Edit Dialog */}
      <ReminderNoteDialog
        open={reminderNoteDialogOpen}
        onClose={() => setReminderNoteDialogOpen(false)}
        reminder={companyReminder}
        onEdit={handleEditReminder}
        onDeactivate={handleDeactivateReminder}
      />

      {/* Reminder Removal Confirmation Drawer */}
      <Drawer 
        anchor="right"
        open={reminderRemovalConfirmOpen} 
        onClose={handleReminderRemovalCancel}
        PaperProps={{
          sx: { width: { xs: '100%', sm: 450 } }
        }}
      >
        {/* Header */}
        <Box sx={{ bgcolor: '#ff9800', color: 'white', p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Warning sx={{ fontSize: '1.5rem' }} />
            <Typography variant="h6">Remove Reminder?</Typography>
          </Box>
          <IconButton onClick={handleReminderRemovalCancel} sx={{ color: 'white' }}>
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Content */}
        <Box sx={{ p: 3, flex: 1, overflowY: 'auto' }}>
          <Typography variant="body1" sx={{ mb: 2 }}>
            You are moving <strong>{companyData?.company_name}</strong> from 
            <strong> 5% (On Hold)</strong> to <strong>{pendingStageChangeFromFivePercent?.stage?.name}</strong>.
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            This company has an active reminder that will be <strong>removed</strong> when you proceed.
          </Typography>
          {companyReminder && (
            <Box sx={{ bgcolor: '#fff3e0', p: 2, borderRadius: 1, border: '1px solid #ffb74d' }}>
              <Typography variant="caption" color="text.secondary">Current Reminder:</Typography>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                📅 {new Date(companyReminder.reminder_date).toLocaleString()}
              </Typography>
              <Typography variant="body2" sx={{ mt: 1, whiteSpace: 'pre-wrap' }}>
                📝 {companyReminder.note?.substring(0, 200)}
                {companyReminder.note?.length > 200 ? '...' : ''}
              </Typography>
            </Box>
          )}
        </Box>

        {/* Footer */}
        <Box sx={{ p: 2, borderTop: '1px solid #e0e0e0', display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
          <Button onClick={handleReminderRemovalCancel}>Cancel</Button>
          <Button 
            variant="contained" 
            color="warning"
            onClick={handleReminderRemovalConfirm}
          >
            Remove Reminder & Move
          </Button>
        </Box>
      </Drawer>

      {/* Mark as Outreached Drawer */}
      <Drawer
        anchor="right"
        open={markOutreachDrawerOpen}
        onClose={() => setMarkOutreachDrawerOpen(false)}
        PaperProps={{
          sx: { width: '400px', maxWidth: '90vw' }
        }}
      >
        {/* Header */}
        <Box sx={{ 
          bgcolor: '#1976d2', 
          color: 'white',
          p: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <span>📤</span>
            <Typography variant="h6">Mark as Outreached</Typography>
          </Box>
          <IconButton onClick={() => setMarkOutreachDrawerOpen(false)} sx={{ color: 'white' }}>
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Content */}
        <Box sx={{ p: 3, flex: 1, overflow: 'auto' }}>
          <Box sx={{ mb: 3, p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
            <Typography variant="subtitle2" color="text.secondary">Company</Typography>
            <Typography variant="h6">{companyData?.company_name}</Typography>
          </Box>
          
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>Outreach Method *</Typography>
            <FormControl fullWidth size="small">
              <Select
                value={outreachMethod}
                onChange={(e) => setOutreachMethod(e.target.value)}
              >
                <MenuItem value="email">📧 Email</MenuItem>
                <MenuItem value="call">📞 Call</MenuItem>
                <MenuItem value="linkedin">💼 LinkedIn</MenuItem>
                <MenuItem value="other">📝 Other</MenuItem>
              </Select>
            </FormControl>
          </Box>
          
          <Box sx={{ mb: 3, display: 'flex', gap: 2 }}>
            <Box sx={{ flex: 1 }}>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>Date *</Typography>
              <TextField
                type="date"
                fullWidth
                size="small"
                value={outreachDate}
                onChange={(e) => setOutreachDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>Time *</Typography>
              <TextField
                type="time"
                fullWidth
                size="small"
                value={outreachTime}
                onChange={(e) => setOutreachTime(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Box>
          </Box>
          
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>Comment *</Typography>
            <TextField
              fullWidth
              multiline
              rows={6}
              placeholder="Enter details about the outreach (required)..."
              value={outreachComment}
              onChange={(e) => setOutreachComment(e.target.value)}
              error={!outreachComment.trim()}
              helperText={!outreachComment.trim() ? 'Comment is required' : ''}
            />
          </Box>
        </Box>

        {/* Footer */}
        <Box sx={{ p: 2, borderTop: '1px solid #e0e0e0', display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
          <Button onClick={() => setMarkOutreachDrawerOpen(false)} disabled={isMarkingOutreach}>
            Cancel
          </Button>
          <Button 
            variant="contained" 
            onClick={submitMarkOutreached}
            disabled={isMarkingOutreach || !outreachComment.trim()}
            startIcon={isMarkingOutreach ? <CircularProgress size={16} color="inherit" /> : null}
          >
            {isMarkingOutreach ? 'Saving...' : 'Mark as Outreached'}
          </Button>
        </Box>
      </Drawer>

      {/* QM-exit Note Drawer (US-4) */}
      <Drawer
        anchor="right"
        open={qmExitCommentDialogOpen}
        onClose={handleQmExitCancel}
        PaperProps={{
          sx: { width: '400px', maxWidth: '90vw' }
        }}
      >
        {/* Header */}
        <Box sx={{
          bgcolor: '#1976d2',
          color: 'white',
          p: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <span>📝</span>
            <Typography variant="h6">Qualified Meetings — Exit Note</Typography>
          </Box>
          <IconButton onClick={handleQmExitCancel} sx={{ color: 'white' }}>
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Content */}
        <Box sx={{ p: 3, flex: 1, overflow: 'auto' }}>
          <Box sx={{ mb: 3, p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
            <Typography variant="subtitle2" color="text.secondary">Company</Typography>
            <Typography variant="h6">{companyData?.company_name}</Typography>
          </Box>

          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>Exit Note *</Typography>
            <TextField
              fullWidth
              multiline
              rows={6}
              placeholder="Why is this company leaving Qualified Meetings? (required)..."
              value={qmExitCommentInput}
              onChange={(e) => setQmExitCommentInput(e.target.value)}
              error={!qmExitCommentInput.trim()}
              helperText={!qmExitCommentInput.trim() ? 'A note is required' : ''}
            />
          </Box>
        </Box>

        {/* Footer */}
        <Box sx={{ p: 2, borderTop: '1px solid #e0e0e0', display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
          <Button onClick={handleQmExitCancel}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleQmExitConfirm}
            disabled={!qmExitCommentInput.trim()}
          >
            Save & Continue
          </Button>
        </Box>
      </Drawer>

      {/* B1: Financial capture dialog — gates a move into a >=50% stage (TCI/TCV) before it proceeds */}
      <FinancialCaptureDialog
        open={financialCaptureOpen}
        currencyLabel={companyData?.currency?.label}
        targetStageName={financialCaptureTarget?.stage?.name || financialCaptureTarget?.stage?.label || ''}
        initialValues={financialCaptureInitialValues}
        onConfirm={handleFinancialCaptureConfirm}
        onCancel={handleFinancialCaptureCancel}
      />

      {/* View/Edit Outreach Details Drawer */}
      <Drawer
        anchor="right"
        open={viewOutreachDrawerOpen}
        onClose={() => { setViewOutreachDrawerOpen(false); setIsEditingOutreach(false); }}
        PaperProps={{
          sx: { width: '400px', maxWidth: '90vw' }
        }}
      >
        {/* Header */}
        <Box sx={{ 
          bgcolor: isEditingOutreach ? '#1976d2' : '#4caf50', 
          color: 'white',
          p: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <span>{isEditingOutreach ? '✏️' : '✅'}</span>
            <Typography variant="h6">{isEditingOutreach ? 'Edit Outreach' : 'Outreach Details'}</Typography>
          </Box>
          <IconButton onClick={() => { setViewOutreachDrawerOpen(false); setIsEditingOutreach(false); }} sx={{ color: 'white' }}>
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Content */}
        <Box sx={{ p: 3, flex: 1, overflow: 'auto' }}>
          <Box sx={{ mb: 3, p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
            <Typography variant="subtitle2" color="text.secondary">Company</Typography>
            <Typography variant="h6">{companyData?.company_name}</Typography>
          </Box>
          
          {isEditingOutreach ? (
            // Edit Mode
            <>
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>Outreach Method *</Typography>
                <FormControl fullWidth size="small">
                  <Select
                    value={outreachMethod}
                    onChange={(e) => setOutreachMethod(e.target.value)}
                  >
                    <MenuItem value="email">📧 Email</MenuItem>
                    <MenuItem value="call">📞 Call</MenuItem>
                    <MenuItem value="linkedin">💼 LinkedIn</MenuItem>
                    <MenuItem value="other">📝 Other</MenuItem>
                  </Select>
                </FormControl>
              </Box>
              
              <Box sx={{ mb: 3, display: 'flex', gap: 2 }}>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>Date *</Typography>
                  <TextField
                    type="date"
                    fullWidth
                    size="small"
                    value={outreachDate}
                    onChange={(e) => setOutreachDate(e.target.value)}
                  />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>Time *</Typography>
                  <TextField
                    type="time"
                    fullWidth
                    size="small"
                    value={outreachTime}
                    onChange={(e) => setOutreachTime(e.target.value)}
                  />
                </Box>
              </Box>
              
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>Comment *</Typography>
                <TextField
                  fullWidth
                  multiline
                  rows={6}
                  placeholder="Enter details about the outreach..."
                  value={outreachComment}
                  onChange={(e) => setOutreachComment(e.target.value)}
                  error={!outreachComment.trim()}
                  helperText={!outreachComment.trim() ? 'Comment is required' : ''}
                />
              </Box>
            </>
          ) : (
            // View Mode
            outreachDetails && (
              <>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5 }}>
                    Outreach Method
                  </Typography>
                  <Chip 
                    label={outreachDetails.outreach_method_display}
                    size="small"
                    sx={{ fontWeight: 600 }}
                  />
                </Box>
                
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5 }}>
                    Outreached By
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {outreachDetails.marked_by_name}
                  </Typography>
                </Box>
                
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5 }}>
                    Date & Time
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {new Date(outreachDetails.outreached_at).toLocaleDateString('en-GB', { 
                      day: '2-digit', 
                      month: 'short', 
                      year: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </Typography>
                </Box>
                
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5 }}>
                    Comment
                  </Typography>
                  <Box sx={{ 
                    p: 2, 
                    bgcolor: '#f5f5f5', 
                    borderRadius: 1,
                    border: '1px solid #e0e0e0'
                  }}>
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                      {outreachDetails.comment}
                    </Typography>
                  </Box>
                </Box>
              </>
            )
          )}
        </Box>

        {/* Footer */}
        <Box sx={{ p: 2, borderTop: '1px solid #e0e0e0', display: 'flex', gap: 2, justifyContent: 'space-between' }}>
          {isEditingOutreach ? (
            // Edit Mode Footer
            <>
              <Button onClick={cancelEditingOutreach} disabled={isSavingOutreachEdit}>
                Cancel
              </Button>
              <Button 
                variant="contained" 
                onClick={saveOutreachEdit}
                disabled={isSavingOutreachEdit || !outreachComment.trim()}
                startIcon={isSavingOutreachEdit ? <CircularProgress size={16} color="inherit" /> : null}
              >
                {isSavingOutreachEdit ? 'Saving...' : 'Save Changes'}
              </Button>
            </>
          ) : (
            // View Mode Footer
            <>
              <Button 
                variant="outlined" 
                color="error"
                onClick={removeOutreachMark}
                disabled={isRemovingOutreach}
                startIcon={isRemovingOutreach ? <CircularProgress size={16} color="inherit" /> : null}
              >
                {isRemovingOutreach ? 'Removing...' : 'Remove'}
              </Button>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button 
                  variant="outlined"
                  onClick={startEditingOutreach}
                  startIcon={<EditIcon />}
                >
                  Edit
                </Button>
                <Button onClick={() => setViewOutreachDrawerOpen(false)}>
                  Close
                </Button>
              </Box>
            </>
          )}
        </Box>
      </Drawer>

      {/* QM Exit Note View Drawer (read-only, US-4 Task 7) */}
      <Drawer
        anchor="right"
        open={viewQmExitCommentOpen}
        onClose={() => setViewQmExitCommentOpen(false)}
        PaperProps={{
          sx: { width: '400px', maxWidth: '90vw' }
        }}
      >
        {/* Header */}
        <Box sx={{
          bgcolor: '#4caf50',
          color: 'white',
          p: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <span>📝</span>
            <Typography variant="h6">Qualified Meetings — Exit Note</Typography>
          </Box>
          <IconButton onClick={() => setViewQmExitCommentOpen(false)} sx={{ color: 'white' }}>
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Content */}
        <Box sx={{ p: 3, flex: 1, overflow: 'auto' }}>
          <Box sx={{ mb: 3, p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
            <Typography variant="subtitle2" color="text.secondary">Company</Typography>
            <Typography variant="h6">{companyData?.company_name}</Typography>
          </Box>

          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5 }}>
              Captured By
            </Typography>
            <Typography variant="body1" sx={{ fontWeight: 500 }}>
              {companyData?.qm_exit_comment_by || '—'}
            </Typography>
          </Box>

          {companyData?.qm_exit_comment_at && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5 }}>
                Date & Time
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 500 }}>
                {new Date(companyData.qm_exit_comment_at).toLocaleDateString('en-GB', {
                  day: '2-digit',
                  month: 'short',
                  year: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit'
                })}
              </Typography>
            </Box>
          )}

          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5 }}>
              Exit Note
            </Typography>
            <Box sx={{
              p: 2,
              bgcolor: '#f5f5f5',
              borderRadius: 1,
              border: '1px solid #e0e0e0'
            }}>
              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                {companyData?.qm_exit_comment}
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* Footer */}
        <Box sx={{ p: 2, borderTop: '1px solid #e0e0e0', display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
          <Button onClick={() => setViewQmExitCommentOpen(false)}>
            Close
          </Button>
        </Box>
      </Drawer>

      {/* Mark as Invalid Drawer */}
      <Drawer
        anchor="right"
        open={markInvalidDrawerOpen}
        onClose={() => setMarkInvalidDrawerOpen(false)}
        PaperProps={{
          sx: { width: '400px', maxWidth: '90vw' }
        }}
      >
        {/* Header */}
        <Box sx={{ 
          bgcolor: '#ff9800', 
          color: 'white', 
          p: 2, 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between' 
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <span>⚠️</span>
            <Typography variant="h6">Mark as Invalid</Typography>
          </Box>
          <IconButton onClick={() => setMarkInvalidDrawerOpen(false)} sx={{ color: 'white' }}>
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Content */}
        <Box sx={{ p: 3, flex: 1, overflow: 'auto' }}>
          <Box sx={{ mb: 3, p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
            <Typography variant="subtitle2" color="text.secondary">Company</Typography>
            <Typography variant="h6">{companyData?.company_name}</Typography>
          </Box>
          
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>Reason *</Typography>
            <FormControl fullWidth size="small">
              <Select
                value={selectedInvalidReason}
                onChange={(e) => setSelectedInvalidReason(e.target.value)}
                displayEmpty
              >
                <MenuItem value="" disabled>Select a reason</MenuItem>
                {invalidReasons.map((reason) => (
                  <MenuItem key={reason.id} value={reason.id}>
                    {reason.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
          
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>Additional Details (Optional)</Typography>
            <TextField
              fullWidth
              multiline
              rows={4}
              placeholder="Enter additional details about why this company is invalid..."
              value={invalidDescription}
              onChange={(e) => setInvalidDescription(e.target.value)}
            />
          </Box>
          
          <Box sx={{ p: 2, bgcolor: '#fff3e0', borderRadius: 1, border: '1px solid #ff9800' }}>
            <Typography variant="caption" color="warning.dark">
              ⚠️ This company will be moved to the 5% stage and marked as invalid.
            </Typography>
          </Box>
        </Box>

        {/* Footer */}
        <Box sx={{ p: 2, borderTop: '1px solid #e0e0e0', display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
          <Button onClick={() => setMarkInvalidDrawerOpen(false)} disabled={isMarkingInvalid}>
            Cancel
          </Button>
          <Button 
            variant="contained" 
            color="warning"
            onClick={submitMarkInvalid}
            disabled={isMarkingInvalid || !selectedInvalidReason}
            startIcon={isMarkingInvalid ? <CircularProgress size={16} color="inherit" /> : null}
          >
            {isMarkingInvalid ? 'Saving...' : 'Mark as Invalid'}
          </Button>
        </Box>
      </Drawer>

      {/* Add to DND dialog (shared reusable dialog) */}
      <AddToDndDialog
        open={addDndDialogOpen}
        company={companyData?.id ? { id: companyData.id, company_name: companyData.company_name } : null}
        onClose={() => setAddDndDialogOpen(false)}
        onDone={fetchDndDetails}
      />

      {/* View Invalid Details Drawer */}
      <Drawer
        anchor="right"
        open={viewInvalidDrawerOpen}
        onClose={() => setViewInvalidDrawerOpen(false)}
        PaperProps={{
          sx: { width: '400px', maxWidth: '90vw' }
        }}
      >
        {/* Header */}
        <Box sx={{ 
          bgcolor: '#ff9800', 
          color: 'white', 
          p: 2, 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between' 
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <span>⚠️</span>
            <Typography variant="h6">Invalid Company Details</Typography>
          </Box>
          <IconButton onClick={() => setViewInvalidDrawerOpen(false)} sx={{ color: 'white' }}>
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Content */}
        <Box sx={{ p: 3, flex: 1, overflow: 'auto' }}>
          <Box sx={{ mb: 3, p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
            <Typography variant="subtitle2" color="text.secondary">Company</Typography>
            <Typography variant="h6">{companyData?.company_name}</Typography>
          </Box>
          
          {invalidDetails && !isEditingInvalid && (
            <>
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5 }}>Reason</Typography>
                <Chip 
                  label={invalidDetails.reason_label || 'No reason'} 
                  color="warning" 
                  size="small"
                />
              </Box>
              
              {invalidDetails.description && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5 }}>Description</Typography>
                  <Typography variant="body2">
                    {invalidDetails.description}
                  </Typography>
                </Box>
              )}
              
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5 }}>Marked By</Typography>
                <Typography variant="body2">
                  {invalidDetails.marked_by_name || 'Unknown'}
                </Typography>
              </Box>
              
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5 }}>Marked At</Typography>
                <Typography variant="body2">
                  {invalidDetails.marked_at 
                    ? new Date(invalidDetails.marked_at).toLocaleString()
                    : 'Unknown'}
                </Typography>
              </Box>
            </>
          )}

          {/* Edit Mode */}
          {isEditingInvalid && (
            <>
              <FormControl fullWidth sx={{ mb: 3 }}>
                <InputLabel>Reason *</InputLabel>
                <Select
                  value={selectedInvalidReason}
                  onChange={(e) => setSelectedInvalidReason(e.target.value)}
                  label="Reason *"
                >
                  {invalidReasons.map((reason) => (
                    <MenuItem key={reason.id} value={reason.id}>
                      {reason.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <TextField
                fullWidth
                multiline
                rows={3}
                label="Description (Optional)"
                value={invalidDescription}
                onChange={(e) => setInvalidDescription(e.target.value)}
                placeholder="Add any additional details..."
                sx={{ mb: 3 }}
              />
            </>
          )}
        </Box>

        {/* Footer */}
        <Box sx={{ p: 2, borderTop: '1px solid #e0e0e0', display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
          {!isEditingInvalid ? (
            <>
              <Button onClick={() => setViewInvalidDrawerOpen(false)} disabled={isRemovingInvalid}>
                Close
              </Button>
              <Button 
                variant="outlined" 
                color="primary"
                onClick={startEditingInvalid}
                disabled={isRemovingInvalid}
              >
                Edit
              </Button>
              <Button 
                variant="outlined" 
                color="success"
                onClick={removeInvalidMark}
                disabled={isRemovingInvalid}
                startIcon={isRemovingInvalid ? <CircularProgress size={16} color="inherit" /> : null}
              >
                {isRemovingInvalid ? 'Removing...' : 'Remove Invalid & Move to 0%'}
              </Button>
            </>
          ) : (
            <>
              <Button onClick={cancelEditingInvalid} disabled={isSavingInvalidEdit}>
                Cancel
              </Button>
              <Button 
                variant="contained" 
                color="warning"
                onClick={saveInvalidEdit}
                disabled={isSavingInvalidEdit || !selectedInvalidReason}
                startIcon={isSavingInvalidEdit ? <CircularProgress size={16} color="inherit" /> : null}
              >
                {isSavingInvalidEdit ? 'Saving...' : 'Save Changes'}
              </Button>
            </>
          )}
        </Box>
      </Drawer>

      {/* Archive Company Drawer */}
      <Drawer
        anchor="right"
        open={archiveDrawerOpen}
        onClose={() => setArchiveDrawerOpen(false)}
        PaperProps={{
          sx: { width: '400px', maxWidth: '90vw' }
        }}
      >
        {/* Header */}
        <Box sx={{ 
          bgcolor: '#9c27b0', 
          color: 'white', 
          p: 2, 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between' 
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <span>📦</span>
            <Typography variant="h6">Archive Company</Typography>
          </Box>
          <IconButton onClick={() => setArchiveDrawerOpen(false)} sx={{ color: 'white' }} disabled={isArchiving}>
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Content */}
        <Box sx={{ p: 3, flex: 1, overflow: 'auto' }}>
          <Box sx={{ mb: 3, p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
            <Typography variant="subtitle2" color="text.secondary">Company</Typography>
            <Typography variant="h6">{companyData?.company_name}</Typography>
          </Box>
          
          <TextField
            fullWidth
            multiline
            rows={4}
            label="Archive Note (optional)"
            placeholder="Enter a reason or note for archiving this company..."
            value={archiveNote}
            onChange={(e) => setArchiveNote(e.target.value)}
            disabled={isArchiving}
          />
          
          <Box sx={{ mt: 3, p: 2, bgcolor: '#fff3e0', borderRadius: 1, border: '1px solid #ff9800' }}>
            <Typography variant="body2" color="text.secondary">
              <strong>Note:</strong> Archiving a company will hide it from the default dashboard view. 
              You can view archived companies by toggling the archive filter in the 100% column.
            </Typography>
          </Box>
        </Box>

        {/* Footer */}
        <Box sx={{ p: 2, borderTop: '1px solid #e0e0e0', display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
          <Button onClick={() => setArchiveDrawerOpen(false)} disabled={isArchiving}>
            Cancel
          </Button>
          <Button 
            variant="contained"
            sx={{ bgcolor: '#9c27b0', '&:hover': { bgcolor: '#7b1fa2' } }}
            onClick={submitArchiveCompany}
            disabled={isArchiving}
            startIcon={isArchiving ? <CircularProgress size={16} color="inherit" /> : null}
          >
            {isArchiving ? 'Archiving...' : 'Archive Company'}
          </Button>
        </Box>
      </Drawer>

      {/* View Archive Details Drawer */}
      <Drawer
        anchor="right"
        open={viewArchiveDrawerOpen}
        onClose={() => setViewArchiveDrawerOpen(false)}
        PaperProps={{
          sx: { width: '400px', maxWidth: '90vw' }
        }}
      >
        {/* Header */}
        <Box sx={{ 
          bgcolor: '#9c27b0', 
          color: 'white', 
          p: 2, 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between' 
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <span>📦</span>
            <Typography variant="h6">Archive Details</Typography>
          </Box>
          <IconButton onClick={() => setViewArchiveDrawerOpen(false)} sx={{ color: 'white' }}>
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Content */}
        <Box sx={{ p: 3, flex: 1, overflow: 'auto' }}>
          <Box sx={{ mb: 3, p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
            <Typography variant="subtitle2" color="text.secondary">Company</Typography>
            <Typography variant="h6">{companyData?.company_name}</Typography>
          </Box>

          {archiveDetails && (
            <>
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Archive Note
                </Typography>
                <Typography variant="body1">
                  {archiveDetails.note || 'No note provided'}
                </Typography>
              </Box>

              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Archived By
                </Typography>
                <Typography variant="body1">
                  {archiveDetails.archived_by_name || 'Unknown'}
                </Typography>
              </Box>

              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Archived Date
                </Typography>
                <Typography variant="body1">
                  {archiveDetails.archived_at ? new Date(archiveDetails.archived_at).toLocaleString() : 'Unknown'}
                </Typography>
              </Box>
            </>
          )}
        </Box>

        {/* Footer */}
        <Box sx={{ p: 2, borderTop: '1px solid #e0e0e0', display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
          <Button onClick={() => setViewArchiveDrawerOpen(false)} disabled={isUnarchiving}>
            Close
          </Button>
          <Button 
            variant="outlined" 
            sx={{ color: '#9c27b0', borderColor: '#9c27b0' }}
            onClick={submitUnarchiveCompany}
            disabled={isUnarchiving}
            startIcon={isUnarchiving ? <CircularProgress size={16} color="inherit" /> : null}
          >
            {isUnarchiving ? 'Unarchiving...' : 'Unarchive Company'}
          </Button>
        </Box>
      </Drawer>

    </Box>
  );
}

// Error Boundary Component
class EditJourneyErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error('EditJourney Error:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <Box sx={{ p: 3, textAlign: 'center' }}>
          <Typography variant="h5" color="error" sx={{ mb: 2 }}>
            Something went wrong
          </Typography>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Please refresh the page or contact support if the issue persists.
          </Typography>
          <Button
            variant="contained"
            onClick={() => window.location.reload()}
          >
            Refresh Page
          </Button>
        </Box>
      );
    }

    return this.props.children;
  }
}

// Wrap with error boundary
export default function EditJourney() {
  return (
    <EditJourneyErrorBoundary>
      <EditJourneyContent />
    </EditJourneyErrorBoundary>
  );
}
// Document List View Component
const DocumentListView = ({
  documents,
  meetings,
  audioRecordings,
  scheduledMeetings,
  notes,
  stages,
  companyData,
  onDocumentClick,
  handleUniversalFileUpload,
  recordingStages,
  recordingTimes,
  startRecording,
  stopRecording,
  setCurrentStageForAction,
  setSidebarAction,
  setSidebarOpen,
  setTextNoteStage,
  setTextNoteTitle,
  setTextNoteContent,
  setTextNoteDialogOpen,
  onEditNote,
  uploadUniversalFile
}) => {
  const [dateFilter, setDateFilter] = useState({ startDate: '', endDate: '' });
  const allItems = [
    ...documents.map(doc => ({ ...doc, type: 'document', date: doc.created_at })),
    ...meetings.map(meeting => ({ ...meeting, type: 'meeting', date: meeting.created_at })),
    ...audioRecordings.map(audio => ({ ...audio, type: 'audio', date: audio.created_at })),
    ...scheduledMeetings.map(sm => ({ ...sm, type: 'scheduledMeeting', date: sm.start_date })),
    ...notes.map(note => ({ ...note, type: 'note', date: note.created_at }))
  ];

  // Apply client-side date filtering if dates are set
  const filteredItems = (dateFilter.startDate || dateFilter.endDate) ? allItems.filter(item => {
    const itemDate = new Date(item.date);
    const startDate = dateFilter.startDate ? new Date(dateFilter.startDate) : null;
    const endDate = dateFilter.endDate ? new Date(dateFilter.endDate + 'T23:59:59') : null;

    if (startDate && itemDate < startDate) return false;
    if (endDate && itemDate > endDate) return false;
    return true;
  }) : allItems;

  const groupedByStage = stages.reduce((groups, stage) => {
    const stageItems = filteredItems.filter(item => {
      const itemStage = item.stage;
      const stageNameLower = stage.name.toLowerCase();
      const itemStageLower = itemStage ? itemStage.toLowerCase() : '';

      return itemStage === stage.name ||
        itemStageLower === stageNameLower ||
        (itemStageLower.includes('intro') && stageNameLower.includes('intro')) ||
        (!itemStage && stageNameLower.includes('intro'));
    }).sort((a, b) => new Date(b.date) - new Date(a.date));

    if (stageItems.length > 0) {
      groups[stage.name] = { stage, items: stageItems };
    }
    return groups;
  }, {});

  const getFileIcon = (item) => {
    if (item.type === 'meeting') return <Video size={20} color="white" />;
    if (item.type === 'audio') return <Mic size={20} color="#ff9800" />;
    if (item.type === 'scheduledMeeting') return <CalendarToday sx={{ fontSize: 20, color: 'white' }} />;
    if (item.type === 'note') return <StickyNote size={20} color="#f57c00" />;
    return <FileText size={20} color="#1976d2" />;
  };

  const formatDate = (dateString) => {
    if (!dateString || dateString === 'Invalid Date') {
      return new Date().toLocaleDateString('en-US', {
        year: 'numeric', month: 'long', day: 'numeric'
      });
    }
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric', month: 'long', day: 'numeric'
      });
    } catch (error) {
      return new Date().toLocaleDateString('en-US', {
        year: 'numeric', month: 'long', day: 'numeric'
      });
    }
  };

  const handleDateFilterChange = (field, value) => {
    const newFilter = { ...dateFilter, [field]: value };
    setDateFilter(newFilter);
    // Apply filter if both dates are set or if clearing filter
    if ((newFilter.startDate && newFilter.endDate) || (!newFilter.startDate && !newFilter.endDate)) {
      // Adjust end date to include the entire day (23:59:59)
      const filterToApply = newFilter.startDate || newFilter.endDate ? {
        ...newFilter,
        endDate: newFilter.endDate ? newFilter.endDate + 'T23:59:59' : newFilter.endDate
      } : null;
      window.getJourneyDataWithFilter?.(filterToApply);
    }
  };

  return (
    <Box sx={{ backgroundColor: '#fff', minHeight: 600 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" sx={{ fontWeight: 600 }}>All Documents</Typography>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <TextField
            type="date"
            label="Start Date"
            size="small"
            value={dateFilter.startDate}
            onChange={(e) => handleDateFilterChange('startDate', e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{
              width: 220,
              mt: 1,
              '& .MuiInputLabel-root': {
                fontSize: '0.875rem',
                whiteSpace: 'nowrap',
                backgroundColor: 'white',
                paddingRight: '4px'
              },
              '& .MuiInputBase-input': {
                fontSize: '0.875rem'
              }
            }}
          />
          <TextField
            type="date"
            label="End Date"
            size="small"
            value={dateFilter.endDate}
            onChange={(e) => handleDateFilterChange('endDate', e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{
              width: 220,
              mt: 1,
              '& .MuiInputLabel-root': {
                fontSize: '0.875rem',
                whiteSpace: 'nowrap',
                backgroundColor: 'white',
                paddingRight: '4px'
              },
              '& .MuiInputBase-input': {
                fontSize: '0.875rem'
              }
            }}
          />
          {(dateFilter.startDate || dateFilter.endDate) && (
            <Button
              size="small"
              variant="outlined"
              onClick={() => {
                setDateFilter({ startDate: '', endDate: '' });
                window.getJourneyDataWithFilter?.(null);
              }}
              sx={{ minWidth: 'auto', px: 2 }}
            >
              Clear
            </Button>
          )}
        </Box>
      </Box>

      {Object.keys(groupedByStage).length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 8, color: 'text.secondary' }}>
          <FileText size={64} color="#ccc" />
          <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>No documents found</Typography>
          <Typography variant="body2">Upload documents, recordings, or audio files to get started</Typography>
        </Box>
      ) : (
        Object.entries(groupedByStage).map(([stageName, { stage, items }]) => {
          // Check if this stage is locked
          const stageLocked = isStageLockedForActions(companyData, stage);
          
          return (
          <Box key={stageName} sx={{ mb: 4 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1.1rem' }}>
                  {stageName}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  ({items.length} items)
                </Typography>
                {stageLocked && (
                  <Typography variant="caption" sx={{ color: '#9e9e9e', fontSize: '0.7rem' }}>
                    🔒 Editing disabled
                  </Typography>
                )}
              </Box>
              <Box sx={{ display: 'flex', gap: 1, opacity: stageLocked ? 0.5 : 1 }}>
                <Tooltip title={stageLocked ? "Editing disabled - Company at 100% for 10+ days" : "Upload Video"} arrow>
                  <span>
                    <IconButton
                      size="small"
                      component="label"
                      disabled={stageLocked}
                      sx={{
                        backgroundColor: stageLocked ? '#f5f5f5' : '#ffebee',
                        color: stageLocked ? '#bdbdbd' : '#d32f2f',
                        width: 36,
                        height: 36,
                        borderRadius: 2,
                        '&:hover': { backgroundColor: stageLocked ? '#f5f5f5' : '#ffcdd2' },
                        cursor: stageLocked ? 'not-allowed' : 'pointer'
                      }}
                    >
                      <Video size={18} />
                      {!stageLocked && (
                        <input
                          type="file"
                          hidden
                          accept="video/*,.mp4,.avi,.mov,.webm,.mkv,.flv,.wmv"
                          onChange={(e) => handleUniversalFileUpload(e, stage)}
                        />
                      )}
                    </IconButton>
                  </span>
                </Tooltip>

                <Tooltip title={stageLocked ? "Editing disabled - Company at 100% for 10+ days" : "Upload Document"} arrow>
                  <span>
                    <IconButton
                      size="small"
                      component="label"
                      disabled={stageLocked}
                      sx={{
                        backgroundColor: stageLocked ? '#f5f5f5' : '#e3f2fd',
                        color: stageLocked ? '#bdbdbd' : '#1976d2',
                        width: 36,
                        height: 36,
                        borderRadius: 2,
                        '&:hover': { backgroundColor: stageLocked ? '#f5f5f5' : '#bbdefb' },
                        cursor: stageLocked ? 'not-allowed' : 'pointer'
                      }}
                    >
                      <FileText size={18} />
                      {!stageLocked && (
                        <input
                          type="file"
                          hidden
                          accept=".pdf,.doc,.docx,.ppt,.pptx,.xls,.xlsx,.txt,.csv,.html,.htm"
                          onChange={(e) => handleUniversalFileUpload(e, stage)}
                        />
                      )}
                    </IconButton>
                  </span>
                </Tooltip>

                {recordingStages[stage.id] ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1, backgroundColor: '#ffebee', borderRadius: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Box sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: '#f44336', animation: 'pulse 1s infinite' }} />
                      <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
                        {Math.floor((recordingTimes[stage.id] || 0) / 60)}:{((recordingTimes[stage.id] || 0) % 60).toString().padStart(2, '0')}
                      </Typography>
                    </Box>
                    <IconButton size="small" onClick={() => stopRecording(stage)} sx={{ color: '#f44336' }}>
                      <Box sx={{ width: 12, height: 12, backgroundColor: '#f44336' }} />
                    </IconButton>
                  </Box>
                ) : (
                  <Tooltip title={stageLocked ? "Editing disabled - Company at 100% for 10+ days" : "Record Audio"} arrow>
                    <span>
                      <IconButton
                        size="small"
                        disabled={stageLocked}
                        onClick={() => !stageLocked && startRecording(stage)}
                        sx={{
                          backgroundColor: stageLocked ? '#f5f5f5' : '#fff3e0',
                          color: stageLocked ? '#bdbdbd' : '#f57c00',
                          width: 36,
                          height: 36,
                          borderRadius: 2,
                          '&:hover': { backgroundColor: stageLocked ? '#f5f5f5' : '#ffe0b2' },
                          cursor: stageLocked ? 'not-allowed' : 'pointer'
                        }}
                      >
                        <Mic size={18} />
                      </IconButton>
                    </span>
                  </Tooltip>
                )}

                <Tooltip title={stageLocked ? "Editing disabled - Company at 100% for 10+ days" : "Assign Team"} arrow>
                  <span>
                    <IconButton
                      size="small"
                      disabled={stageLocked}
                      onClick={() => {
                        if (stageLocked) return;
                        setCurrentStageForAction(stage);
                        setSidebarAction('team');
                        setSidebarOpen(true);
                      }}
                      sx={{
                        backgroundColor: stageLocked ? '#f5f5f5' : '#f3e5f5',
                        color: stageLocked ? '#bdbdbd' : '#7b1fa2',
                        width: 36,
                        height: 36,
                        borderRadius: 2,
                        '&:hover': { backgroundColor: stageLocked ? '#f5f5f5' : '#e1bee7' },
                        cursor: stageLocked ? 'not-allowed' : 'pointer'
                      }}
                    >
                      <Users size={18} />
                    </IconButton>
                  </span>
                </Tooltip>

                <Tooltip title={stageLocked ? "Editing disabled - Company at 100% for 10+ days" : "Text Note"} arrow>
                  <span>
                    <IconButton
                      size="small"
                      disabled={stageLocked}
                      onClick={() => {
                        if (stageLocked) return;
                        setTextNoteStage(stage);
                        setTextNoteTitle('');
                        setTextNoteContent('');
                        setTextNoteDialogOpen(true);
                      }}
                      sx={{
                        backgroundColor: stageLocked ? '#f5f5f5' : '#fff3e0',
                        color: stageLocked ? '#bdbdbd' : '#f57c00',
                        width: 36,
                        height: 36,
                        borderRadius: 2,
                        '&:hover': { backgroundColor: stageLocked ? '#f5f5f5' : '#ffe0b2' },
                        cursor: stageLocked ? 'not-allowed' : 'pointer'
                      }}
                    >
                      <StickyNote size={18} />
                    </IconButton>
                  </span>
                </Tooltip>

                <Tooltip title={stageLocked ? "Editing disabled - Company at 100% for 10+ days" : "Create Task"} arrow>
                  <span>
                    <IconButton
                      size="small"
                      disabled={stageLocked}
                      onClick={() => {
                        if (stageLocked) return;
                        setCurrentStageForAction(stage);
                        setSidebarAction('task');
                        setSidebarOpen(true);
                      }}
                      sx={{
                        backgroundColor: stageLocked ? '#f5f5f5' : '#e8f5e8',
                        color: stageLocked ? '#bdbdbd' : '#388e3c',
                        width: 36,
                        height: 36,
                        borderRadius: 2,
                        '&:hover': { backgroundColor: stageLocked ? '#f5f5f5' : '#c8e6c9' },
                        cursor: stageLocked ? 'not-allowed' : 'pointer'
                      }}
                    >
                      <CheckSquare size={18} />
                    </IconButton>
                  </span>
                </Tooltip>
              </Box>
            </Box>

            <DragDropWrapper stage={stage} onFileDrop={uploadUniversalFile}>
              {items.map((item, index) => (
                <Box key={`${item.type}-${item.id}`} sx={{ mb: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <Calendar size={14} color="#666" />
                    <Typography variant="body2" color="text.secondary" sx={{ ml: 1, fontWeight: 500 }}>
                      {formatDate(item.uploaded_on || item.date || item.created_at)}
                    </Typography>
                  </Box>

                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      p: 2,
                      cursor: 'pointer',
                      backgroundColor: '#f8f9fa',
                      borderRadius: 1,
                      '&:hover': { backgroundColor: '#e9ecef' }
                    }}
                    onClick={() => item.type === 'note' ? onEditNote(item) : onDocumentClick(item)}
                  >
                    <Box sx={{
                      width: 40,
                      height: 40,
                      borderRadius: 1,
                      backgroundColor: item.type === 'meeting' ? '#333' : item.type === 'audio' ? '#ff9800' : item.type === 'scheduledMeeting' ? '#1976d2' : item.type === 'note' ? '#f57c00' : '#1976d2',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mr: 2
                    }}>
                      {getFileIcon(item)}
                    </Box>

                    <Box sx={{ flex: 1 }}>
                      <Tooltip title={item.name || item.title || (item.note_text ? item.note_text.substring(0, 50) + '...' : 'Text Note')} arrow>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }} noWrap>
                          {item.name || item.title || (item.note_text ? item.note_text.replace(/<[^>]*>/g, '').substring(0, 50) + '...' : 'Text Note')}
                        </Typography>
                      </Tooltip>
                      <Typography variant="body2" color="text.secondary">
                        {item.duration ? `${item.duration} min • ` : ''}
                        {item.type === 'meeting' ? 'Video Recording' :
                          item.type === 'audio' ? 'Audio Recording' :
                            item.type === 'scheduledMeeting' ? 'Scheduled Meeting' :
                              item.type === 'note' ? 'Text Note' :
                                (item.file?.split('.').pop()?.toUpperCase() || 'PDF') + ' Document'}
                      </Typography>
                    </Box>

                    <Button
                      variant="outlined"
                      size="small"
                      sx={{ minWidth: 'auto', px: 2, fontWeight: 600 }}
                    >
                      {item.type === 'note' ? 'Edit' : 'View'}
                    </Button>
                  </Box>
                </Box>
              ))}
            </DragDropWrapper>
          </Box>
        );
        })
      )}
    </Box>
  );
};

// Document Card Component
const DocumentCard = ({ document, matches }) => {
  const [open, setOpen] = useState(false);

  const handleDocumentClick = () => {
    setOpen(true);
  };

  const getFileType = () => {
    if (!document.file) return 'unknown';
    const extension = document.file.split('.').pop().toLowerCase();
    if (['mp3', 'wav', 'ogg', 'm4a', 'aac', 'flac'].includes(extension)) return 'audio';
    if (['mp4', 'avi', 'mov', 'webm', 'mkv', '3gp', 'flv'].includes(extension)) return 'video';
    if (['jpg', 'jpeg', 'png', 'gif', 'bmp', 'svg'].includes(extension)) return 'image';
    return 'document';
  };

  const getDialogSize = () => {
    const fileType = getFileType();
    return fileType === 'audio' ? 'sm' : 'lg';
  };

  const getDialogHeight = () => {
    const fileType = getFileType();
    return fileType === 'audio' ? '50px' : '80vh';
  };

  const fileType = getFileType();
  const isAudio = fileType === 'audio';

  return (
    <>
      <Card
        sx={{
          mb: 2,
          cursor: 'pointer',
          '&:hover': {
            borderColor: '#1976d2',
            border: '2px solid #1976d2'
          }
        }}
        onClick={handleDocumentClick}
      >
        <Box sx={{
          height: 128,
          backgroundColor:
            fileType === 'audio' ? '#fff3e0' :
              fileType === 'video' ? '#2c3e50' :
                fileType === 'image' ? '#f3e5f5' : '#e8f4fd',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          position: 'relative',
          p: 2
        }}>
          {fileType === 'audio' ? (
            <>
              <Mic size={48} color="#ff9800" />
              <Chip
                label="AUDIO"
                size="small"
                sx={{
                  position: 'absolute',
                  top: 8,
                  right: 8,
                  backgroundColor: '#ff9800',
                  color: 'white',
                  fontSize: '0.75rem',
                  fontWeight: 'bold'
                }}
              />
            </>
          ) : fileType === 'video' ? (
            <>
              <Play size={48} color="rgba(255,255,255,0.8)" />
              <Chip
                label="VIDEO"
                size="small"
                sx={{
                  position: 'absolute',
                  top: 8,
                  right: 8,
                  backgroundColor: '#f44336',
                  color: 'white',
                  fontSize: '0.75rem',
                  fontWeight: 'bold'
                }}
              />
            </>
          ) : fileType === 'image' ? (
            <>
              <FileText size={48} color="#9c27b0" />
              <Chip
                label="IMAGE"
                size="small"
                sx={{
                  position: 'absolute',
                  top: 8,
                  right: 8,
                  backgroundColor: '#9c27b0',
                  color: 'white',
                  fontSize: '0.75rem',
                  fontWeight: 'bold'
                }}
              />
            </>
          ) : (
            <FileText size={48} color="#2196f3" />
          )}
        </Box>
        <CardContent sx={{ p: 1.5, pb: '12px !important' }}>
          <Tooltip title={document.name || document.title || 'Document'} arrow>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }} noWrap>
              {document.name || document.title || 'Document'}
            </Typography>
          </Tooltip>
          <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', mb: 0.25 }} noWrap>
            Created By: {document.created_by ? `${document.created_by.first_name} ${document.created_by.last_name}` : 'Unknown'}
          </Typography>
          <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary' }}>
            Date: {document.uploaded_on || document.created_at ? new Date(document.uploaded_on || document.created_at).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }) : 'N/A'}
          </Typography>
        </CardContent>
      </Card>
      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        maxWidth={getDialogSize()}
        fullWidth
      >
        <DialogTitle>{document.name || document.title || 'Document'}</DialogTitle>
        <DialogContent sx={{ p: 0, height: getDialogHeight() }}>
          <MediaViewer
            file={{ fileUrl: document.file }}
            showPlayBackSpeed={fileType === 'audio'}
            matches={matches}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

// Video Meeting Card Component for Video View
const VideoMeetingCard = ({ meeting, formatDateTime }) => {
  const [open, setOpen] = useState(false);
  // Media is served by the backend; a relative /media/... path would resolve against
  // the frontend origin and return index.html. Prefix the backend base like AudioCard.
  const rawVideoUrl = meeting.file || meeting.recordingUrl || '';
  const videoSrc = rawVideoUrl.startsWith('http') ? rawVideoUrl : `${constants.HTTP_METHOD}${constants.HTTP_URL}${constants.HTTP_PORT}${rawVideoUrl}`;

  return (
    <>
      <Card sx={{
        cursor: 'pointer',
        borderRadius: 2,
        overflow: 'hidden',
        '&:hover': {
          boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
          transform: 'translateY(-2px)'
        },
        transition: 'all 0.3s ease'
      }} onClick={() => setOpen(true)}>
        <Box sx={{
          height: 200,
          backgroundColor: '#2c3e50',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          position: 'relative'
        }}>
          <Play size={64} color="rgba(255,255,255,0.9)" />
          <Chip
            label="REC"
            size="small"
            sx={{
              position: 'absolute',
              top: 12,
              right: 12,
              backgroundColor: '#f44336',
              color: 'white',
              fontSize: '0.75rem',
              fontWeight: 'bold'
            }}
          />
        </Box>
        <CardContent sx={{ p: 2 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }} noWrap>
            {meeting.title || 'Meeting Recording'}
          </Typography>
          <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', mb: 0.25 }} noWrap>
            Created By: {meeting.created_by ? `${meeting.created_by.first_name} ${meeting.created_by.last_name}` : 'Unknown'}
          </Typography>
          <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary' }}>
            Date: {meeting.uploaded_on || meeting.created_at ? new Date(meeting.uploaded_on || meeting.created_at).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }) : 'N/A'}
          </Typography>
        </CardContent>
      </Card>

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="lg" fullWidth>
        <DialogTitle>{meeting.title || 'Meeting Recording 33'}</DialogTitle>
        <DialogContent sx={{ textAlign: 'center', py: 2 }}>
          <video
            controls
            style={{ width: '100%', height: 'auto', maxHeight: '500px' }}
            src={videoSrc}
          >
            Your browser does not support the video tag.
          </video>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

// Video Audio Card Component for Video View
const VideoAudioCard = ({ audio, formatDateTime }) => {
  const [open, setOpen] = useState(false);
  const fileUrl = audio?.file || audio?.audioUrl || '';
  const audioSrc = fileUrl.startsWith('http') ? fileUrl : `${constants.HTTP_METHOD}${constants.HTTP_URL}${constants.HTTP_PORT}${fileUrl}`;

  return (
    <>
      <Card sx={{
        cursor: 'pointer',
        borderRadius: 2,
        overflow: 'hidden',
        '&:hover': {
          boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
          transform: 'translateY(-2px)'
        },
        transition: 'all 0.3s ease'
      }} onClick={() => setOpen(true)}>
        <Box sx={{
          height: 200,
          backgroundColor: '#fff3e0',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          position: 'relative'
        }}>
          <Mic size={64} color="#ff9800" />
          <Chip
            label="AUDIO"
            size="small"
            sx={{
              position: 'absolute',
              top: 12,
              right: 12,
              backgroundColor: '#ff9800',
              color: 'white',
              fontSize: '0.75rem',
              fontWeight: 'bold'
            }}
          />
        </Box>
        <CardContent sx={{ p: 3 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
            {audio.title || 'Audio Recording'}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Play size={16} color="#666" />
              <Typography variant="body2" color="text.secondary">
                {audio.duration ? `${audio.duration} min` : 'Recording'}
              </Typography>
            </Box>
            <Typography variant="body2" color="text.secondary">
              {fileUrl ? fileUrl.split('.').pop().toUpperCase() : 'AUDIO'}
            </Typography>
          </Box>
          <Typography variant="caption" color="text.secondary">
            {formatDateTime(audio.created_at)}
          </Typography>
        </CardContent>
      </Card>

      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            height: '80vh',
            minHeight: 600
          }
        }}
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">{audio.title || 'Audio Recording'}</Typography>
          <IconButton onClick={() => setOpen(false)}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 2, height: '100%' }}>
          <MediaViewer
            file={{ fileUrl: fileUrl }}
            showPlayBackSpeed={true}
            matches={false}
          />
        </DialogContent>
      </Dialog>
    </>
  );
};

// Meeting Card Component
const MeetingCard = ({ meeting, formatDateTime }) => {
  const [open, setOpen] = useState(false);
  const [thumbnail, setThumbnail] = useState("https://resumex.gatewaydigital.ai/static/media/image.b6a9e459.png");
  // Media is served by the backend; prefix the backend base so a relative /media/...
  // path doesn't resolve against the frontend origin (which returns index.html).
  const rawVideoUrl = meeting.file || meeting.recordingUrl || '';
  const videoSrc = rawVideoUrl.startsWith('http') ? rawVideoUrl : `${constants.HTTP_METHOD}${constants.HTTP_URL}${constants.HTTP_PORT}${rawVideoUrl}`;

  useEffect(() => {
    const generate = async () => {
      try {
        const rawUrl = meeting.file || meeting.recordingUrl || '';
        if (!rawUrl) return;
        const src = rawUrl.startsWith('http') ? rawUrl : `${constants.HTTP_METHOD}${constants.HTTP_URL}${constants.HTTP_PORT}${rawUrl}`;
        const img = await createVideoThumbnail(src);
        if (img) setThumbnail(img);
      } catch (e) {
        console.error('❌ Thumbnail generation failed:', e.message, 'URL:', meeting.file);
      }
    };
    generate();
  }, [meeting]);

  return (
    <>
      <Card sx={{
        mb: 2,
        cursor: 'pointer',
        '&:hover': {
          borderColor: '#1976d2',
          border: '2px solid #1976d2'
        }
      }} onClick={() => setOpen(true)}>
        <Box sx={{
          height: 140,
          backgroundColor: '#2c3e50',
          backgroundImage: thumbnail ? `url(${thumbnail})` : 'none',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          position: 'relative',
          p: 2
        }}>
          <Play size={48} color="rgba(255,255,255,0.8)" />
          <Chip
            label="REC"
            size="small"
            sx={{
              position: 'absolute',
              top: 8,
              right: 8,
              backgroundColor: '#f44336',
              color: 'white',
              fontSize: '0.75rem',
              fontWeight: 'bold'
            }}
          />
        </Box>
        <CardContent sx={{ p: 1.5, pb: '12px !important' }}>
          <Tooltip title={meeting.title || 'Meeting Recording'} arrow>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }} noWrap>
              {meeting.title || 'Meeting Recording'}
            </Typography>
          </Tooltip>
          <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', mb: 0.25 }} noWrap>
            Created By: {meeting.created_by ? `${meeting.created_by.first_name} ${meeting.created_by.last_name}` : 'Unknown'}
          </Typography>
          <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary' }}>
            Date: {meeting.uploaded_on || meeting.created_at ? new Date(meeting.uploaded_on || meeting.created_at).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }) : 'N/A'}
          </Typography>
        </CardContent>
      </Card>

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>{meeting.title || 'Meeting Recording12'}</DialogTitle>
        <DialogContent sx={{ textAlign: 'center', py: 2 }}>
          <video
            controls
            style={{ width: '100%', height: 'auto', maxHeight: '500px' }}
            src={videoSrc}
          >
            Your browser does not support the video tag.
          </video>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

// Company Task Card Component (for All/Data tabs)
const CompanyTaskCard = ({ task, onStatusChange }) => {
  const userInfo = UserInfo();
  const isSuperAdmin = userInfo?.role?.includes('SUPER_ADMIN');

  const statusOptions = [
    { value: 'To Do', label: 'To Do', color: '#6c757d' },
    { value: 'In Progress', label: 'In Progress', color: '#007bff' },
    { value: 'Pending', label: 'Pending', color: '#ffc107' },
    { value: 'Blocked', label: 'Blocked', color: '#dc3545' },
    { value: 'Done', label: 'Done', color: '#28a745' }
  ];

  const formatDate = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric'
    });
  };

  const getStatusColor = (status) => {
    const statusOption = statusOptions.find(option => option.value === status);
    return statusOption ? statusOption.color : '#6c757d';
  };

  return (
    <Card sx={{
      mb: 2,
      cursor: 'pointer',
      '&:hover': {
        borderColor: '#1976d2',
        border: '2px solid #1976d2'
      }
    }}>
      <Box sx={{
        height: 128,
        backgroundColor: 'linear-gradient(135deg, #f3e5f5 0%, #e1bee7 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        position: 'relative'
      }}>
        <CheckBox sx={{ fontSize: 48, color: '#9c27b0' }} />
        <Chip
          label="TASK"
          size="small"
          sx={{
            position: 'absolute',
            top: 8,
            right: 8,
            backgroundColor: '#9c27b0',
            color: 'white',
            fontSize: '0.75rem'
          }}
        />
      </Box>
      <CardContent sx={{ p: 2 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }} noWrap>
          {task.title}
        </Typography>

        {task.description && (
          <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
            {task.description}
          </Typography>
        )}

        <Box>
          {isSuperAdmin && Array.isArray(task.assigned_to_details) && task.assigned_to_details.length > 0 && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
              Assigned:{' '}
              {task.assigned_to_details
                .map(u => `${u.first_name || ''} ${u.last_name || ''}`.trim())
                .join(', ')}
            </Typography>
          )}
          {task.due_date && (
            <Typography variant="caption" color={task.is_overdue ? 'error' : 'text.secondary'} sx={{ display: 'block' }}>
              Due: {formatDate(task.due_date)} {task.is_overdue && '⚠️'}
            </Typography>
          )}
        </Box>

        {/* <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <Autocomplete
            size="small"
            sx={{ minWidth: 100 }}
            value={statusOptions.find(opt => opt.value === (task.status || 'To Do')) || statusOptions[0]}
            onChange={(event, newValue) => onStatusChange(task.id, newValue ? newValue.value : 'To Do')}
            options={statusOptions}
            getOptionLabel={(option) => option.label || ''}
            isOptionEqualToValue={(option, value) => option.value === value.value}
            disableClearable
            renderInput={(params) => (
              <TextField
                {...params}
                sx={{
                  '& .MuiInputBase-input': {
                    color: getStatusColor(task.status),
                    fontWeight: 600,
                    fontSize: '0.75rem',
                    py: '0.5px !important'
                  }
                }}
              />
            )}
            renderOption={(props, option) => (
              <li {...props} key={option.value}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box
                    sx={{
                      width: 8,
                      height: 8,
                      borderRadius: '50%',
                      backgroundColor: option.color
                    }}
                  />
                  {option.label}
                </Box>
              </li>
            )}
          />
          
          <Chip 
            label={task.priority?.toUpperCase() || 'MEDIUM'}
            size="small"
            sx={{
              backgroundColor: 
                task.priority === 'high' ? '#ffebee' : 
                task.priority === 'medium' ? '#fff3e0' : '#e8f5e8',
              color: 
                task.priority === 'high' ? '#d32f2f' : 
                task.priority === 'medium' ? '#f57c00' : '#388e3c',
              fontSize: '0.65rem',
              fontWeight: 600
            }}
          />
        </Box> */}
      </CardContent>
    </Card>
  );
};

// Text Note Dialog Component
export function TextNoteDialog({
  open,
  onClose,
  noteTitle,
  onTitleChange,
  noteContent,
  onContentChange,
  onSave,
  isListening,
  onStartListening,
  onStopListening,
  isSaving,
  stageName,
  isEditing
}) {
  const theme = useTheme();
  const matches = useMediaQuery(theme.breakpoints.down('md'));

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          minHeight: '500px'
        }
      }}
    >
      <DialogTitle sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        pb: 1,
        borderBottom: '1px solid',
        borderColor: 'divider'
      }}>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            {isEditing ? 'Edit Note' : 'Create Note'}
          </Typography>
          {stageName && (
            <Typography variant="caption" color="text.secondary">
              Stage: {stageName}
            </Typography>
          )}
        </Box>
        <IconButton
          onClick={onClose}
          size="small"
          sx={{
            color: 'text.secondary',
            '&:hover': { backgroundColor: 'action.hover' }
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 2, minHeight: 400 }}>
        <TextField
          fullWidth
          label="Title"
          placeholder="Enter note title..."
          value={noteTitle || ''}
          onChange={(e) => onTitleChange(e.target.value)}
          sx={{ mb: 2, mt: 1 }}
          size="small"
        />
        <Box
          sx={{
            cursor: 'text',
            '& [contenteditable="true"]': {
              minHeight: 300,
              maxHeight: 350,
              width: '100%',
              outline: 'none',
            },
          }}
        >
          <TextEditor
            name="Text Note"
            placeholder="Type your notes here or click the audio button to speak..."
            value={noteContent}
            onChange={onContentChange}
            matches={matches}
            editorStyle={{
              fontSize: matches ? '0.675rem' : '16px',
              overflow: 'auto',
            }}
          />
        </Box>
      </DialogContent>

      <DialogActions sx={{
        px: 3,
        py: 2,
        borderTop: '1px solid',
        borderColor: 'divider',
        justifyContent: 'space-between'
      }}>
        <Button
          onClick={onSave}
          variant="contained"
          disabled={isSaving || !noteContent?.trim()}
          startIcon={isSaving ? <CircularProgress size={16} /> : null}
          sx={{
            backgroundColor: '#00AB55',
            '&:hover': { backgroundColor: '#007B55' }
          }}
        >
          {isSaving ? 'Saving...' : 'Save Note'}
        </Button>

        <Button
          onClick={isListening ? onStopListening : onStartListening}
          variant={isListening ? "contained" : "outlined"}
          color={isListening ? "error" : "primary"}
          startIcon={<Mic size={18} />}
          sx={{
            minWidth: 140,
            animation: isListening ? 'pulse 1.5s infinite' : 'none',
            '@keyframes pulse': {
              '0%': { opacity: 1 },
              '50%': { opacity: 0.7 },
              '100%': { opacity: 1 }
            }
          }}
        >
          {isListening ? 'Stop Recording' : 'Voice Input'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// Team Assignment Drawer Component
function TeamAssignmentDrawer({ stage, teamMembers, companyId, userRoles, companyData, onClose, onSave, onTeamAssignmentComplete, getFilteredTeamMembersForRole }) {
  const [assignments, setAssignments] = useState({});
  const [initialAssignments, setInitialAssignments] = useState({}); // Track original state
  const [loading, setLoading] = useState(true);
  const [roleErrors, setRoleErrors] = useState({}); // Track errors per role
  const [existingMappings, setExistingMappings] = useState([]);
  const [isSaving, setIsSaving] = useState(false);

  // Define stage name early for use in handleAssignmentChange
  const stageNameLower = stage?.name?.toLowerCase() || '';
  const [filteredMembersByRole, setFilteredMembersByRole] = useState({}); // Store filtered members per role

  // Clear errors when drawer opens
  useEffect(() => {
    setRoleErrors({});
  }, [stage?.id]);

  // Fetch filtered team members for each role when drawer opens or stage changes
  useEffect(() => {
    const fetchFilteredMembers = async () => {
      if (!stage || !getFilteredTeamMembersForRole || !userRoles || userRoles.length === 0) {
        return;
      }

      const filtered = {};
      for (const role of userRoles) {
        try {
          const filteredMembers = await getFilteredTeamMembersForRole(stage, role.label);
          filtered[role.id] = filteredMembers;
        } catch (error) {
          console.warn(`Error fetching filtered members for role ${role.label}:`, error);
          filtered[role.id] = teamMembers; // Fallback to all members
        }
      }
      setFilteredMembersByRole(filtered);
    };

    fetchFilteredMembers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stage?.id, userRoles]);

  // Fetch existing mappings only (userRoles now come from parent)
  useEffect(() => {
    const fetchData = async () => {
      if (!companyId) return;

      try {
        setLoading(true);

        // Fetch only existing mappings
        const mappingsResponse = await apiInstance.get(`company-user-mapping/?company=${companyId}&page_size=1000`, {
          headers: {
            "Authorization": `Token ${localStorage.getItem('authToken')}`,
            'Content-Type': 'application/json'
          }
        });


        const mappings = mappingsResponse.data?.results?.data || [];

        setExistingMappings(mappings);

        // Initialize assignments with existing mappings
        const initialAssignments = {};

        // Initialize only the three assignable roles (BA / BM / Intro Call Taken By) as empty single-select
        const assignableRoles = TEAM_DRAWER_ROLE_NAMES
          .map((n) => userRoles.find((r) => r.name === n))
          .filter(Boolean);
        assignableRoles.forEach(role => {
          initialAssignments[role.id] = '';
        });

        // Then override with existing mappings
        // First sort by modified date in descending order to get the most recent mapping for each role
        const sortedMappings = [...mappings].sort((a, b) =>
          new Date(b.modified) - new Date(a.modified)
        );

        // Create a map to track which roles we've already processed
        const processedRoles = new Set();

        sortedMappings.forEach(mapping => {
          if (mapping.role && mapping.user && mapping.is_active) {
            const roleId = mapping.role.toString();
            const userId = mapping.user.toString();

            // Single-select only: take the most recent mapping per role (due to descending sort)
            if (!processedRoles.has(roleId)) {
              initialAssignments[roleId] = userId;
              processedRoles.add(roleId);
            }

            // If we have a user_name but the user is not in teamMembers, add them
            if (mapping.user_name && !teamMembers.some(m => m.pk?.toString() === userId)) {
              const userObj = {
                pk: userId,
                first_name: mapping.user_name.split(' ')[0],
                last_name: mapping.user_name.split(' ').slice(1).join(' '),
                email: ''
              };
              teamMembers.push(userObj);

              // Also add to filtered members for this role if not already present
              if (roleId && filteredMembersByRole[roleId]) {
                if (!filteredMembersByRole[roleId].some(m => m.pk?.toString() === userId)) {
                  setFilteredMembersByRole(prev => ({
                    ...prev,
                    [roleId]: [...(prev[roleId] || []), userObj]
                  }));
                }
              }
            }
          }
        });

        setAssignments(initialAssignments);
        setInitialAssignments(initialAssignments); // Store initial state for comparison
      } catch (error) {
        console.error('Error fetching data:', error);
        // Don't set error state to prevent any error toasts from appearing
        // Errors will be shown inline for specific roles when needed
      } finally {
        setLoading(false);
      }
    };

    if (userRoles && userRoles.length > 0) {
      fetchData();
    }
  }, [companyId, userRoles]);

  const handleAssignmentChange = (roleId, userId) => {
    // Only update local state - no API calls until user clicks Done
    setAssignments(prev => ({
      ...prev,
      [roleId]: userId
    }));

    // Clear any existing error for this role
    setRoleErrors(prev => {
      const newErrors = { ...prev };
      delete newErrors[roleId];
      return newErrors;
    });
  };

  // Check if this is the 100% (Closed) stage
  const is100PercentStage = stageNameLower.includes('100') || stageNameLower.includes('closed - 100');

  // Fixed set of assignable roles for this drawer, keyed on stable role.name (no stage filtering).
  const assignableRoles = TEAM_DRAWER_ROLE_NAMES
    .map((n) => userRoles.find((r) => r.name === n))
    .filter(Boolean);

  // Handle completing team assignment and proceed with stage change
  const handleComplete = async () => {
    // Save all changes
    try {
      setIsSaving(true);

      // Process each assignable role and compare with initial state (single-select only)
      for (const role of assignableRoles) {
        const roleId = role.id;
        const currentValue = assignments[roleId];
        const initialValue = initialAssignments[roleId];

        // Handle single-select roles
        const currentUserId = currentValue && currentValue.toString().trim() !== '' ? currentValue : null;
        const initialUserId = initialValue && initialValue.toString().trim() !== '' ? initialValue : null;

        // Only process if value changed
        if (currentUserId !== initialUserId) {
          const existingMapping = existingMappings.find(mapping =>
            (mapping.role === parseInt(roleId) || mapping.role?.id === parseInt(roleId)) && mapping.is_active
          );

          if (currentUserId) {
            // User assigned to role
            const payload = {
              company: companyId,
              user: currentUserId,
              role: roleId,
              is_active: true,
              is_multiple: false
            };

            if (existingMapping) {
              // Update existing mapping
              await apiInstance.put(
                `company-user-mapping/${existingMapping.id}/`,
                payload,
                {
                  headers: {
                    "Authorization": `Token ${localStorage.getItem('authToken')}`,
                    'Content-Type': 'application/json'
                  }
                }
              );
            } else {
              // Create new mapping
              await apiInstance.post(
                'company-user-mapping/',
                payload,
                {
                  headers: {
                    "Authorization": `Token ${localStorage.getItem('authToken')}`,
                    'Content-Type': 'application/json'
                  }
                }
              );
            }
          } else if (existingMapping) {
            // User unassigned - deactivate mapping
            const payload = {
              company: companyId,
              user: existingMapping.user,
              role: roleId,
              is_active: false,
              is_multiple: false
            };

            await apiInstance.put(
              `company-user-mapping/${existingMapping.id}/`,
              payload,
              {
                headers: {
                  "Authorization": `Token ${localStorage.getItem('authToken')}`,
                  'Content-Type': 'application/json'
                }
              }
            );
          }
        }
      }

      showToast('Team assignments saved successfully', 'success');

      // All required roles assigned, trigger completion callback
      if (onTeamAssignmentComplete) {
        onTeamAssignmentComplete(stage.id);
      }
      onClose();
    } catch (error) {
      console.error('Error saving team assignments:', error);
      showErrorToast('Failed to save team assignments');
    } finally {
      setIsSaving(false);
    }
  };

  if (loading && userRoles.length === 0) {
    return (
      <Box sx={{ width: 400, p: 3, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ width: 400, p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Box>
          <Typography variant="h6">Assign Team for {stage?.name}</Typography>
          {is100PercentStage && (
            <Typography variant="caption" color="text.secondary">
              Step 2 of 2 • Deal details saved ✓
            </Typography>
          )}
        </Box>
        <IconButton onClick={onClose} disabled={loading}>
          <CloseIcon />
        </IconButton>
      </Box>

      {assignableRoles.length > 0 && (
        <Box sx={{ mb: 3, p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
          <Typography variant="body2" color="text.secondary">
            <>📋 <strong>Roles:</strong> {assignableRoles.map(teamRoleDisplay).join(', ')}</>
          </Typography>
          {companyData?.created_by?.first_name && companyData?.created_by?.last_name && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              👤 <strong>Company created:</strong> {companyData.created_by?.first_name} {companyData.created_by?.last_name}
            </Typography>
          )}
        </Box>
      )}

      {assignableRoles.length > 0 ? (
        assignableRoles.map((role) => {
          const fieldLabel = teamRoleDisplay(role);

          return (
            <Box key={role.id} sx={{ mb: 2 }}>
              <Typography
                variant="body2"
                sx={{ mb: 1, fontWeight: 500 }}
              >
                {fieldLabel}
              </Typography>

              <Autocomplete
                size="small"
                value={(() => {
                  const selectedId = assignments[role.id]?.toString();
                  if (!selectedId) return null;
                  const membersForRole = filteredMembersByRole[role.id] || teamMembers;
                  return membersForRole.find(m => m.pk?.toString() === selectedId) || null;
                })()}
                onChange={async (event, newValue) => {
                  // Clear error when user makes a selection
                  setRoleErrors(prev => {
                    const newErrors = { ...prev };
                    delete newErrors[role.id];
                    return newErrors;
                  });

                  try {
                    setIsSaving(true);
                    await handleAssignmentChange(role.id, newValue?.pk?.toString() || '');
                  } finally {
                    setIsSaving(false);
                  }
                }}
                options={filteredMembersByRole[role.id] || teamMembers}
                getOptionLabel={(member) => {
                  const memberId = member.pk?.toString();
                  const userMapping = existingMappings.find(m =>
                    m.user?.toString() === memberId || m.user_id?.toString() === memberId
                  );
                  return userMapping?.user_name ||
                    `${member.first_name || ''} ${member.last_name || ''}`.trim() ||
                    member.email ||
                    `User ${memberId}`;
                }}
                disabled={loading || isSaving}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder={`Select ${fieldLabel} (or leave empty to remove)`}
                    error={!!roleErrors[role.id]}
                  />
                )}
              />

              {roleErrors[role.id] && (
                <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
                  {roleErrors[role.id]}
                </Typography>
              )}
            </Box>
          );
        })
      ) : (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography variant="body2" color="textSecondary">
            No assignable roles available
          </Typography>
        </Box>
      )}

      <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, mt: 3 }}>
        <Button
          variant="outlined"
          onClick={onClose}
          disabled={loading}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleComplete}
          disabled={loading || isSaving}
          color="primary"
        >
          {isSaving ? 'Saving...' : 'Done'}
        </Button>
      </Box>
    </Box>
  );
}

// Task Assignment Drawer Component for 10% Intro Call Stage
function TaskAssignmentDrawer({ stage, companyId, companyData, teamMembers, onClose, onTaskAssignmentComplete }) {
  const [taskTitle, setTaskTitle] = useState('Assign BM and arrange introductory call with company');
  const [dueDate, setDueDate] = useState('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [teamLeader, setTeamLeader] = useState(null);
  const [existingMappings, setExistingMappings] = useState([]);

  // Fetch team leader from company-user-mapping
  useEffect(() => {
    const fetchTeamLeader = async () => {
      if (!companyId) return;

      try {
        setLoading(true);

        // Fetch company-user-mapping to get team leader
        const mappingsResponse = await apiInstance.get(`company-user-mapping/?company=${companyId}`, {
          headers: {
            "Authorization": `Token ${localStorage.getItem('authToken')}`,
            'Content-Type': 'application/json'
          }
        });

        const mappings = mappingsResponse.data?.results?.data || [];
        setExistingMappings(mappings);

        // Find Team Lead from mappings
        const teamLeadMapping = mappings.find(mapping =>
          mapping.role_label === "Team Lead" || mapping.role === 10
        );

        if (teamLeadMapping) {
          const teamLeadName = teamLeadMapping.user_name ||
            (() => {
              const user = teamMembers.find(m => m.pk?.toString() === teamLeadMapping.user?.toString());
              return user ? `${user.first_name} ${user.last_name}` : '';
            })();

          setTeamLeader({
            id: teamLeadMapping.user,
            name: teamLeadName
          });
        }
      } catch (error) {
        console.error('Error fetching team leader:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchTeamLeader();
  }, [companyId, teamMembers]);

  const handleAssignTask = async () => {
    // Validation
    if (!taskTitle.trim()) {
      showToast('Please enter a task title', 'error');
      return;
    }

    if (!dueDate) {
      showToast('Please select a due date', 'error');
      return;
    }

    if (!teamLeader) {
      showToast('Team leader not found. Please assign a team leader first.', 'error');
      return;
    }

    try {
      setSubmitting(true);

      // Create task
      const taskData = {
        title: taskTitle,
        due_date: new Date(dueDate + 'T23:59:59').toISOString(),
        priority: 'high',
        status: 'To Do',
        company: companyId,
        stage: stage?.id,
        assigned_to: teamLeader?.id ? [teamLeader.id] : []
      };

      await tasksApi.createTask(taskData);

      showToast('Task assigned successfully to team leader!', 'success');

      // Trigger completion callback
      if (onTaskAssignmentComplete) {
        onTaskAssignmentComplete(stage.id);
      }
    } catch (error) {
      console.error('Error creating task:', error);
      showToast('Error creating task: ' + (error.response?.data?.message || error.message), 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const createdByName = companyData?.created_by
    ? `${companyData.created_by.first_name} ${companyData.created_by.last_name}`
    : 'Unknown';

  return (
    <Box sx={{ width: 500, p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" sx={{ fontWeight: 600 }}>
          Assign Task for {stage?.name || stage?.label}
        </Typography>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          {/* Information Section */}
          <Box sx={{
            bgcolor: '#f5f5f5',
            p: 2,
            borderRadius: 2,
            mb: 3
          }}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Task Assignment Information
            </Typography>

            <Box sx={{ mt: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                <Typography variant="body2" sx={{ fontWeight: 600, minWidth: 140 }}>
                  Company Created By:
                </Typography>
                <Chip
                  label={createdByName}
                  size="small"
                  sx={{ bgcolor: '#e3f2fd', color: '#1976d2' }}
                />
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                <Typography variant="body2" sx={{ fontWeight: 600, minWidth: 140 }}>
                  Assigning Task To:
                </Typography>
                {teamLeader ? (
                  <Chip
                    label={`${teamLeader.name} (Team Leader)`}
                    size="small"
                    sx={{ bgcolor: '#e8f5e9', color: '#2e7d32' }}
                  />
                ) : (
                  <Chip
                    label="Team Leader Not Assigned"
                    size="small"
                    color="error"
                  />
                )}
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Typography variant="body2" sx={{ fontWeight: 600, minWidth: 140 }}>
                  Stage:
                </Typography>
                <Chip
                  label={getStageDisplayName(stage?.name || stage?.label)}
                  size="small"
                  sx={{ bgcolor: '#fff3e0', color: '#e65100' }}
                />
              </Box>
            </Box>
          </Box>

          {/* Task Details Section */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
              Task Details
            </Typography>

            <TextField
              fullWidth
              label="Task Title"
              value={taskTitle}
              onChange={(e) => setTaskTitle(e.target.value)}
              required
              multiline
              rows={2}
              sx={{ mb: 2 }}
              helperText="Describe what needs to be done"
            />

            <TextField
              fullWidth
              label="Due Date"
              type="date"
              value={dueDate}
              onChange={(e) => setDueDate(e.target.value)}
              required
              InputLabelProps={{
                shrink: true,
              }}
              inputProps={{
                min: new Date().toISOString().split('T')[0]
              }}
            />
          </Box>

          {/* Action Buttons */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, mt: 3 }}>
            <Button
              variant="outlined"
              onClick={onClose}
              disabled={submitting}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleAssignTask}
              disabled={submitting || !teamLeader}
              color="primary"
              startIcon={submitting ? <CircularProgress size={20} /> : null}
            >
              {submitting ? 'Assigning...' : 'Assign Task'}
            </Button>
          </Box>

          {!teamLeader && (
            <Typography variant="caption" color="error" sx={{ display: 'block', mt: 2, textAlign: 'center' }}>
              Please assign a Team Leader to the company before creating tasks.
            </Typography>
          )}
        </>
      )}
    </Box>
  );
}

// View Field Component for Calling Card - auto-sizes based on content
const CallingCardViewField = ({ label, value, compact = false, showEmpty = false }) => {
  // For compact fields in grid, show placeholder to maintain alignment
  const displayValue = value || (showEmpty ? '-' : null);
  if (!displayValue && !showEmpty) return null;
  
  if (compact) {
    return (
      <Box sx={{ p: 1.5, backgroundColor: '#f8f9fa', borderRadius: 1.5, border: '1px solid #e9ecef', height: 'auto', minHeight: 50 }}>
        <Typography variant="caption" sx={{ color: '#6c757d', fontWeight: 500, fontSize: '0.7rem', display: 'block', mb: 0.5 }}>
          {label}
        </Typography>
        <Typography variant="body2" sx={{ color: displayValue === '-' ? '#adb5bd' : '#212529', fontWeight: 500, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
          {displayValue}
        </Typography>
      </Box>
    );
  }
  
  if (!value) return null;
  
  return (
    <Box sx={{ mb: 2, p: 2, backgroundColor: '#f8f9fa', borderRadius: 2, border: '1px solid #e9ecef' }}>
      <Typography variant="caption" sx={{ color: '#6c757d', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', display: 'block', mb: 0.5 }}>
        {label}
      </Typography>
      <Typography variant="body1" sx={{ color: '#212529', whiteSpace: 'pre-wrap', wordBreak: 'break-word', lineHeight: 1.6 }}>
        {value}
      </Typography>
    </Box>
  );
};

// Calling Card Drawer Component
function CallingCardDrawer({ callingCardData, onClose, onSave, mode = 'edit', onEdit }) {
  const isViewMode = mode === 'view';
  const [formData, setFormData] = useState({
    startedYr: '',
    noOfShareholders: '',
    ownershipWithManagement: '',
    institutionalInvestor: 'No',
    revenueYear1: '',
    revenueYear2: '',
    revenueYear3: '',
    currentYearRev: '',
    projectedRev: '',
    mrrMonthlyRecurringRevenue: '',
    arrAnnualRecurringRevenue: '',
    esopPlan: 'No',
    lastFundRaise: '',
    anyConvertibleSafe: 'No',
    currentFundRaise: '',
    commitmentForFund: '',
    existingInvestorParticipating: '',
    numberOfCustomers: '',
    totalExpensesPerMonth: '',
    utilizationOfFund: '',
    bankLoans: 'No',
    teamSize: '',
    techSalesOutsourcedBreakdown: '',
    comments: ''
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (callingCardData) {
      setFormData({
        startedYr: callingCardData.startedYr || '',
        noOfShareholders: callingCardData.noOfShareholders || '',
        ownershipWithManagement: callingCardData.ownershipWithManagement || '',
        institutionalInvestor: callingCardData.institutionalInvestor || 'No',
        revenueYear1: callingCardData.revenueYear1 || '',
        revenueYear2: callingCardData.revenueYear2 || '',
        revenueYear3: callingCardData.revenueYear3 || '',
        currentYearRev: callingCardData.currentYearRev || '',
        projectedRev: callingCardData.projectedRev || '',
        mrrMonthlyRecurringRevenue: callingCardData.mrrMonthlyRecurringRevenue || '',
        arrAnnualRecurringRevenue: callingCardData.arrAnnualRecurringRevenue || '',
        esopPlan: callingCardData.esopPlan || 'No',
        lastFundRaise: callingCardData.lastFundRaise || '',
        anyConvertibleSafe: callingCardData.anyConvertibleSafe || 'No',
        currentFundRaise: callingCardData.currentFundRaise || '',
        commitmentForFund: callingCardData.commitmentForFund || '',
        existingInvestorParticipating: callingCardData.existingInvestorParticipating || '',
        numberOfCustomers: callingCardData.numberOfCustomers || '',
        totalExpensesPerMonth: callingCardData.totalExpensesPerMonth || '',
        utilizationOfFund: callingCardData.utilizationOfFund || '',
        bankLoans: callingCardData.bankLoans || 'No',
        teamSize: callingCardData.teamSize || '',
        techSalesOutsourcedBreakdown: callingCardData.techSalesOutsourcedBreakdown || '',
        comments: callingCardData.comments || ''
      });
    }
  }, [callingCardData]);

  const handleSave = async () => {
    setLoading(true);
    try {
      const dataToSave = {
        started_year: formData.startedYr,
        number_of_shareholders: formData.noOfShareholders,
        ownership_with_management: formData.ownershipWithManagement,
        institutional_investor: formData.institutionalInvestor,
        revenue_year_1: formData.revenueYear1,
        revenue_year_2: formData.revenueYear2,
        revenue_year_3: formData.revenueYear3,
        current_year_revenue: formData.currentYearRev,
        projected_revenue: formData.projectedRev,
        mrr_monthly_recurring_revenue: formData.mrrMonthlyRecurringRevenue,
        arr_annual_recurring_revenue: formData.arrAnnualRecurringRevenue,
        esop_plan: formData.esopPlan,
        last_fund_raise: formData.lastFundRaise,
        any_convertible_safe: formData.anyConvertibleSafe,
        current_fund_raise: formData.currentFundRaise,
        commitment_for_fund: formData.commitmentForFund,
        existing_investor_participating: formData.existingInvestorParticipating,
        number_of_customers: formData.numberOfCustomers,
        total_expenses_per_month: formData.totalExpensesPerMonth,
        utilization_of_fund: formData.utilizationOfFund,
        bank_loans: formData.bankLoans,
        team_size: formData.teamSize,
        tech_sales_outsourced_breakdown: formData.techSalesOutsourcedBreakdown,
        comments: formData.comments
      };
      await onSave(dataToSave);
      onClose();
    } catch (error) {
      console.error('Error in handleSave:', error);
    } finally {
      setLoading(false);
    }
  };

  // View Mode Render
  if (isViewMode) {
    return (
      <Box sx={{ width: 520, height: '100vh', display: 'flex', flexDirection: 'column' }}>
        <Box sx={{ p: 2.5, borderBottom: '1px solid #e0e0e0', backgroundColor: '#fafafa' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>Calling Card</Typography>
            <IconButton onClick={onClose} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>Financial and business metrics overview</Typography>
        </Box>
        
        <Box sx={{ 
          flex: 1, 
          p: 2.5, 
          overflowY: 'auto',
          '&::-webkit-scrollbar': { width: '6px' },
          '&::-webkit-scrollbar-track': { background: '#f1f1f1', borderRadius: '10px' },
          '&::-webkit-scrollbar-thumb': { background: '#c1c1c1', borderRadius: '10px' }
        }}>
          {/* Cap Table & PNL Section */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle1" sx={{ mb: 1.5, fontSize: '0.95rem', fontWeight: 600, color: '#1976d2', display: 'flex', alignItems: 'center', gap: 1 }}>
              📊 Cap Table & PNL
            </Typography>
            <Grid container spacing={1.5}>
              <Grid item xs={3}><CallingCardViewField label="Year" value={formData.startedYr} compact /></Grid>
              <Grid item xs={3}><CallingCardViewField label="Shareholders" value={formData.noOfShareholders} compact /></Grid>
              <Grid item xs={3}><CallingCardViewField label="Ownership %" value={formData.ownershipWithManagement} compact /></Grid>
              <Grid item xs={3}><CallingCardViewField label="Inst. Investor" value={formData.institutionalInvestor} compact /></Grid>
            </Grid>
          </Box>
          
          {/* Revenue Section */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle1" sx={{ mb: 1.5, fontSize: '0.95rem', fontWeight: 600, color: '#1976d2', display: 'flex', alignItems: 'center', gap: 1 }}>
              💰 Revenue <Chip label="All values in € Million" size="small" sx={{ height: 18, fontSize: '0.65rem', ml: 1 }} />
            </Typography>
            <Grid container spacing={1.5}>
              <Grid item xs={3}><CallingCardViewField label="Year 1" value={formData.revenueYear1} compact showEmpty /></Grid>
              <Grid item xs={3}><CallingCardViewField label="Year 2" value={formData.revenueYear2} compact showEmpty /></Grid>
              <Grid item xs={3}><CallingCardViewField label="Year 3" value={formData.revenueYear3} compact showEmpty /></Grid>
              <Grid item xs={3}><CallingCardViewField label="Current" value={formData.currentYearRev} compact showEmpty /></Grid>
              <Grid item xs={4}><CallingCardViewField label="Projected" value={formData.projectedRev} compact showEmpty /></Grid>
              <Grid item xs={4}><CallingCardViewField label="MRR" value={formData.mrrMonthlyRecurringRevenue} compact showEmpty /></Grid>
              <Grid item xs={4}><CallingCardViewField label="ARR" value={formData.arrAnnualRecurringRevenue} compact showEmpty /></Grid>
            </Grid>
          </Box>
          
          {/* Funding Section */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle1" sx={{ mb: 1.5, fontSize: '0.95rem', fontWeight: 600, color: '#1976d2', display: 'flex', alignItems: 'center', gap: 1 }}>
              💼 Funding <Chip label="Values in € Million" size="small" sx={{ height: 18, fontSize: '0.65rem', ml: 1 }} />
            </Typography>
            <Grid container spacing={1.5} sx={{ mb: 1.5 }}>
              <Grid item xs={6}><CallingCardViewField label="ESOP" value={formData.esopPlan} compact /></Grid>
              <Grid item xs={6}><CallingCardViewField label="Conv/Safe" value={formData.anyConvertibleSafe} compact /></Grid>
            </Grid>
            <CallingCardViewField label="Last Raise Details" value={formData.lastFundRaise} />
            <CallingCardViewField label="Current Raise Details" value={formData.currentFundRaise} />
            <CallingCardViewField label="Commitment Received" value={formData.commitmentForFund} />
            <CallingCardViewField label="Existing Investor Commitment" value={formData.existingInvestorParticipating} />
          </Box>
          
          {/* Operations Section */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle1" sx={{ mb: 1.5, fontSize: '0.95rem', fontWeight: 600, color: '#1976d2', display: 'flex', alignItems: 'center', gap: 1 }}>
              ⚙️ Operations <Chip label="Expenses in € Million" size="small" sx={{ height: 18, fontSize: '0.65rem', ml: 1 }} />
            </Typography>
            <Grid container spacing={1.5} sx={{ mb: 1.5 }}>
              <Grid item xs={4}><CallingCardViewField label="Customers" value={formData.numberOfCustomers} compact /></Grid>
              <Grid item xs={4}><CallingCardViewField label="Expenses/Month" value={formData.totalExpensesPerMonth} compact /></Grid>
              <Grid item xs={4}><CallingCardViewField label="Loans" value={formData.bankLoans} compact /></Grid>
            </Grid>
            <CallingCardViewField label="Fund Utilization" value={formData.utilizationOfFund} />
          </Box>
          
          {/* Team Section */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle1" sx={{ mb: 1.5, fontSize: '0.95rem', fontWeight: 600, color: '#1976d2' }}>
              👥 Team
            </Typography>
            <Grid container spacing={1.5} sx={{ mb: 1.5 }}>
              <Grid item xs={6}><CallingCardViewField label="Team Size" value={formData.teamSize} compact /></Grid>
            </Grid>
            <CallingCardViewField label="Tech/Sales/Outsourced Breakdown" value={formData.techSalesOutsourcedBreakdown} />
          </Box>
          
          {/* Comments Section */}
          {formData.comments && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle1" sx={{ mb: 1.5, fontSize: '0.95rem', fontWeight: 600, color: '#1976d2' }}>
                💬 Comments
              </Typography>
              <CallingCardViewField label="Additional Notes" value={formData.comments} />
            </Box>
          )}
        </Box>
        
        <Box sx={{ p: 2, borderTop: '1px solid #e0e0e0', display: 'flex', gap: 1.5 }}>
          <Button 
            variant="outlined" 
            fullWidth 
            onClick={onClose}
            sx={{ py: 1.2, fontSize: '0.95rem', fontWeight: 600 }}
          >
            Close
          </Button>
          {onEdit && (
            <Button 
              variant="contained" 
              fullWidth 
              onClick={onEdit}
              startIcon={<Edit2 size={18} />}
              sx={{ py: 1.2, fontSize: '0.95rem', fontWeight: 600, backgroundColor: '#1976d2', '&:hover': { backgroundColor: '#1565c0' } }}
            >
              Edit
            </Button>
          )}
        </Box>
      </Box>
    );
  }

  // Edit Mode Render
  return (
    <Box sx={{ width: 500, height: '100vh', display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ p: 2, borderBottom: '1px solid #e0e0e0' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">Edit Calling Card</Typography>
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>
      </Box>

      {/* Form Content - All sections in one scrollable area */}
      <Box sx={{
        flex: 1,
        p: 2,
        overflowY: 'auto',
        '&::-webkit-scrollbar': {
          width: '6px',
        },
        '&::-webkit-scrollbar-track': {
          background: '#f1f1f1',
          borderRadius: '10px',
        },
        '&::-webkit-scrollbar-thumb': {
          background: '#c1c1c1',
          borderRadius: '10px',
        }
      }}>
        {/* Cap Table & PNL Section */}
        <Box sx={{ mb: 1.5 }}>
          <Typography variant="h6" sx={{ mb: 1.5, fontSize: '1rem', fontWeight: 600, color: '#1976d2' }}>📊 Cap Table & PNL</Typography>
          <Box sx={{ display: 'flex', gap: 1, mb: 1.5 }}>
            <Tooltip title="Year company started" arrow placement="top">
              <TextField
                label="Year"
                value={formData.startedYr}
                onChange={(e) => setFormData(prev => ({ ...prev, startedYr: e.target.value }))}
                size="small"
                sx={{ flex: 1 }}
              />
            </Tooltip>
            <Tooltip title="Number of shareholders" arrow placement="top">
              <TextField
                label="Shareholders"
                value={formData.noOfShareholders}
                onChange={(e) => setFormData(prev => ({ ...prev, noOfShareholders: e.target.value }))}
                size="small"
                sx={{ flex: 1 }}
              />
            </Tooltip>
            <Tooltip title="Ownership percentage with management" arrow placement="top">
              <TextField
                label="Ownership %"
                value={formData.ownershipWithManagement}
                onChange={(e) => setFormData(prev => ({ ...prev, ownershipWithManagement: e.target.value }))}
                placeholder="45%"
                size="small"
                sx={{ flex: 1 }}
              />
            </Tooltip>
            <Tooltip title="Has institutional investor" arrow placement="top">
              <FormControl size="small" sx={{ flex: 1 }}>
                <InputLabel>Inst. Investor</InputLabel>
                <Select
                  value={formData.institutionalInvestor}
                  onChange={(e) => setFormData(prev => ({ ...prev, institutionalInvestor: e.target.value }))}
                  label="Inst. Investor"
                >
                  <MenuItem value="Yes">Yes</MenuItem>
                  <MenuItem value="No">No</MenuItem>
                </Select>
              </FormControl>
            </Tooltip>
          </Box>
        </Box>

        {/* Revenue Section */}
        <Box sx={{ mb: 1.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
            <Typography variant="h6" sx={{ fontSize: '1rem', fontWeight: 600, color: '#1976d2' }}>💰 Revenue</Typography>
            <Chip label="All values in € Million" size="small" sx={{ height: 20, fontSize: '0.7rem', backgroundColor: '#e3f2fd', color: '#1976d2' }} />
          </Box>
          <Box sx={{ display: 'flex', gap: 1, mb: 1.5 }}>
            <Tooltip title="Revenue Year 1" arrow placement="top">
              <TextField
                label="Year 1"
                value={formData.revenueYear1}
                onChange={(e) => setFormData(prev => ({ ...prev, revenueYear1: e.target.value }))}
                placeholder="€2M"
                size="small"
                sx={{ flex: 1 }}
              />
            </Tooltip>
            <Tooltip title="Revenue Year 2" arrow placement="top">
              <TextField
                label="Year 2"
                value={formData.revenueYear2}
                onChange={(e) => setFormData(prev => ({ ...prev, revenueYear2: e.target.value }))}
                placeholder="€4M"
                size="small"
                sx={{ flex: 1 }}
              />
            </Tooltip>
            <Tooltip title="Revenue Year 3" arrow placement="top">
              <TextField
                label="Year 3"
                value={formData.revenueYear3}
                onChange={(e) => setFormData(prev => ({ ...prev, revenueYear3: e.target.value }))}
                placeholder="€7M"
                size="small"
                sx={{ flex: 1 }}
              />
            </Tooltip>
            <Tooltip title="Current year revenue" arrow placement="top">
              <TextField
                label="Current"
                value={formData.currentYearRev}
                onChange={(e) => setFormData(prev => ({ ...prev, currentYearRev: e.target.value }))}
                placeholder="€10M"
                size="small"
                sx={{ flex: 1 }}
              />
            </Tooltip>
          </Box>
          <Box sx={{ display: 'flex', gap: 1, mb: 1.5 }}>
            <Tooltip title="Projected revenue" arrow placement="top">
              <TextField
                label="Projected"
                value={formData.projectedRev}
                onChange={(e) => setFormData(prev => ({ ...prev, projectedRev: e.target.value }))}
                placeholder="€15M"
                size="small"
                sx={{ flex: 1 }}
              />
            </Tooltip>
            <Tooltip title="Monthly Recurring Revenue" arrow placement="top">
              <TextField
                label="MRR"
                value={formData.mrrMonthlyRecurringRevenue}
                onChange={(e) => setFormData(prev => ({ ...prev, mrrMonthlyRecurringRevenue: e.target.value }))}
                placeholder="€0.8M"
                size="small"
                sx={{ flex: 1 }}
              />
            </Tooltip>
            <Tooltip title="Annual Recurring Revenue" arrow placement="top">
              <TextField
                label="ARR"
                value={formData.arrAnnualRecurringRevenue}
                onChange={(e) => setFormData(prev => ({ ...prev, arrAnnualRecurringRevenue: e.target.value }))}
                placeholder="€9.6M"
                size="small"
                sx={{ flex: 1 }}
              />
            </Tooltip>
          </Box>
        </Box>

        {/* Funding Section */}
        <Box sx={{ mb: 1.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
            <Typography variant="h6" sx={{ fontSize: '1rem', fontWeight: 600, color: '#1976d2' }}>💼 Funding</Typography>
            <Chip label="Values in € Million" size="small" sx={{ height: 20, fontSize: '0.7rem', backgroundColor: '#e3f2fd', color: '#1976d2' }} />
          </Box>
          <Box sx={{ display: 'flex', gap: 1, mb: 1.5 }}>
            <Tooltip title="Employee Stock Option Plan" arrow placement="top">
              <FormControl size="small" sx={{ flex: 0.5 }}>
                <InputLabel>ESOP</InputLabel>
                <Select
                  value={formData.esopPlan}
                  onChange={(e) => setFormData(prev => ({ ...prev, esopPlan: e.target.value }))}
                  label="ESOP"
                >
                  <MenuItem value="Yes">Yes</MenuItem>
                  <MenuItem value="No">No</MenuItem>
                </Select>
              </FormControl>
            </Tooltip>
            <Tooltip title="Any convertible notes or SAFE" arrow placement="top">
              <FormControl size="small" sx={{ flex: 0.5 }}>
                <InputLabel>Conv/Safe</InputLabel>
                <Select
                  value={formData.anyConvertibleSafe}
                  onChange={(e) => setFormData(prev => ({ ...prev, anyConvertibleSafe: e.target.value }))}
                  label="Conv/Safe"
                >
                  <MenuItem value="Yes">Yes</MenuItem>
                  <MenuItem value="No">No</MenuItem>
                </Select>
              </FormControl>
            </Tooltip>
          </Box>
          <Box sx={{ display: 'flex', gap: 1, mb: 1.5 }}>
            <Tooltip title="Last raise details - time & valuation" arrow placement="top">
              <TextField
                label="Last Raise Details"
                value={formData.lastFundRaise}
                onChange={(e) => setFormData(prev => ({ ...prev, lastFundRaise: e.target.value }))}
                placeholder="Last raise details - time & valuation"
                multiline
                rows={2}
                sx={{ flex: 1 }}
              />
            </Tooltip>
            <Tooltip title="Current raise - valuation & other details" arrow placement="top">
              <TextField
                label="Current Raise Details"
                value={formData.currentFundRaise}
                onChange={(e) => setFormData(prev => ({ ...prev, currentFundRaise: e.target.value }))}
                placeholder="Current raise - valuation & other details"
                multiline
                rows={2}
                sx={{ flex: 1 }}
              />
            </Tooltip>
          </Box>
          <Box sx={{ display: 'flex', gap: 1, mb: 1.5 }}>
            <Tooltip title="Commitment received till date" arrow placement="top">
              <TextField
                label="Commitment Received"
                value={formData.commitmentForFund}
                onChange={(e) => setFormData(prev => ({ ...prev, commitmentForFund: e.target.value }))}
                placeholder="Commitment received till date"
                multiline
                rows={2}
                sx={{ flex: 1 }}
              />
            </Tooltip>
            <Tooltip title="Existing investor commitment" arrow placement="top">
              <TextField
                label="Existing Investor Commitment"
                value={formData.existingInvestorParticipating}
                onChange={(e) => setFormData(prev => ({ ...prev, existingInvestorParticipating: e.target.value }))}
                placeholder="Existing investor commitment"
                multiline
                rows={2}
                sx={{ flex: 1 }}
              />
            </Tooltip>
          </Box>
        </Box>

        {/* Operations Section */}
        <Box sx={{ mb: 1.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
            <Typography variant="h6" sx={{ fontSize: '1rem', fontWeight: 600, color: '#1976d2' }}>⚙️ Operations</Typography>
            <Chip label="Expenses in € Million" size="small" sx={{ height: 20, fontSize: '0.7rem', backgroundColor: '#e3f2fd', color: '#1976d2' }} />
          </Box>
          <Box sx={{ display: 'flex', gap: 1, mb: 1.5 }}>
            <Tooltip title="Total number of customers" arrow placement="top">
              <TextField
                label="Customers"
                value={formData.numberOfCustomers}
                onChange={(e) => setFormData(prev => ({ ...prev, numberOfCustomers: e.target.value }))}
                placeholder="450"
                size="small"
                sx={{ flex: 1 }}
              />
            </Tooltip>
            <Tooltip title="Total monthly expenses" arrow placement="top">
              <TextField
                label="Expenses/Mo"
                value={formData.totalExpensesPerMonth}
                onChange={(e) => setFormData(prev => ({ ...prev, totalExpensesPerMonth: e.target.value }))}
                placeholder="€0.6M"
                size="small"
                sx={{ flex: 1 }}
              />
            </Tooltip>
            <Tooltip title="How funds will be utilized" arrow placement="top">
              <TextField
                label="Fund Use"
                value={formData.utilizationOfFund}
                onChange={(e) => setFormData(prev => ({ ...prev, utilizationOfFund: e.target.value }))}
                placeholder="€5M Product, €3M Sales"
                size="small"
                sx={{ flex: 1.5 }}
              />
            </Tooltip>
            <Tooltip title="Any bank loans taken" arrow placement="top">
              <FormControl size="small" sx={{ flex: 0.8 }}>
                <InputLabel>Loans</InputLabel>
                <Select
                  value={formData.bankLoans}
                  onChange={(e) => setFormData(prev => ({ ...prev, bankLoans: e.target.value }))}
                  label="Loans"
                >
                  <MenuItem value="Yes">Yes</MenuItem>
                  <MenuItem value="No">No</MenuItem>
                </Select>
              </FormControl>
            </Tooltip>
          </Box>
        </Box>

        {/* Team Section */}
        <Box sx={{ mb: 1.5 }}>
          <Typography variant="h6" sx={{ mb: 1.5, fontSize: '1rem', fontWeight: 600, color: '#1976d2' }}>👥 Team</Typography>
          <Box sx={{ display: 'flex', gap: 1, mb: 1.5 }}>
            <Tooltip title="Total team size" arrow placement="top">
              <TextField
                label="Team Size"
                value={formData.teamSize}
                onChange={(e) => setFormData(prev => ({ ...prev, teamSize: e.target.value }))}
                placeholder="85"
                size="small"
                sx={{ flex: 1 }}
              />
            </Tooltip>
            <Tooltip title="Breakdown of Tech, Sales, and Outsourced team members" arrow placement="top">
              <TextField
                label="Tech/Sales/Outsourced"
                value={formData.techSalesOutsourcedBreakdown}
                onChange={(e) => setFormData(prev => ({ ...prev, techSalesOutsourcedBreakdown: e.target.value }))}
                placeholder="Tech:45, Sales:20, Out:5"
                size="small"
                sx={{ flex: 2 }}
              />
            </Tooltip>
          </Box>
        </Box>

        {/* Comments Section */}
        <Box sx={{ mb: 0 }}>
          <Typography variant="h6" sx={{ mb: 1.5, fontSize: '1rem', fontWeight: 600, color: '#1976d2' }}>💬 Comments</Typography>
          <TextField
            label="Additional Comments"
            value={formData.comments}
            onChange={(e) => setFormData(prev => ({ ...prev, comments: e.target.value }))}
            placeholder="Add any additional notes or comments..."
            multiline
            rows={2}
            fullWidth
          />
        </Box>
      </Box>

      {/* Save Button */}
      <Box sx={{ p: 2, borderTop: '1px solid #e0e0e0' }}>
        <Button
          variant="contained"
          fullWidth
          onClick={handleSave}
          disabled={loading}
          sx={{
            backgroundColor: '#4caf50',
            '&:hover': { backgroundColor: '#45a049' },
            py: 1.2,
            fontSize: '1rem',
            fontWeight: 600
          }}
        >
          {loading ? 'Saving...' : 'Save Changes'}
        </Button>
      </Box>
    </Box>
  );
}

// Add Card Drawer Component
function AddCardDrawer({ selectedAction, setSelectedAction, teamMembers, companyId, onClose, getJourneyData }) {
  const [formData, setFormData] = useState({
    file: null,
    title: '',
    duration: '',
    description: '',
    assignedTo: [],
    deadline: '',
    priority: 'medium',
    stage: 'INTRO'
  });
  const [loading, setLoading] = useState(false);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    setFormData(prev => ({ ...prev, file }));
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const formDataToSend = new FormData();

      if (selectedAction === 'call') {
        formDataToSend.append('file', formData.file);
        formDataToSend.append('title', formData.title);
        formDataToSend.append('duration', formData.duration);
        formDataToSend.append('company_id', companyId);
        formDataToSend.append('stage', formData.stage);

        await apiInstance.post('journey/meetings/', formDataToSend, {
          headers: {
            'Content-Type': 'multipart/form-data',
            Authorization: `Token ${localStorage.getItem('authToken')}`
          }
        });
      } else if (selectedAction === 'document') {
        formDataToSend.append('file', formData.file);
        formDataToSend.append('name', formData.title);
        formDataToSend.append('company_id', companyId);
        formDataToSend.append('stage', formData.stage);

        await apiInstance.post('journey/documents/', formDataToSend, {
          headers: {
            'Content-Type': 'multipart/form-data',
            Authorization: `Token ${localStorage.getItem('authToken')}`
          }
        });
      } else if (selectedAction === 'audio') {
        formDataToSend.append('file', formData.file);
        formDataToSend.append('title', formData.title);
        formDataToSend.append('company_id', companyId);
        formDataToSend.append('stage', formData.stage);

        await apiInstance.post('journey/audio-recordings/', formDataToSend, {
          headers: {
            'Content-Type': 'multipart/form-data',
            Authorization: `Token ${localStorage.getItem('authToken')}`
          }
        });
      } else if (selectedAction === 'task') {
        const taskPayload = {
          title: formData.title,
          description: formData.description,
          company: companyId,
          stage: formData.stage,
          // ensure assigned_to is an array of user IDs
          assigned_to: Array.isArray(formData.assignedTo)
            ? formData.assignedTo
            : (formData.assignedTo ? [formData.assignedTo] : []),
          due_date: formData.deadline,
          priority: formData.priority,
          status: 'To Do'
        };

        await apiInstance.post('tasks/', taskPayload, {
          headers: {
            Authorization: `Token ${localStorage.getItem('authToken')}`
          }
        });
      }

      showToast('Item added successfully', 'success');
      onClose();
      setFormData({ file: null, title: '', duration: '', description: '', assignedTo: [], deadline: '', priority: 'medium', stage: 'INTRO' });
      getJourneyData();
    } catch (error) {
      showErrorToast('Error adding item');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ width: 400, p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h6">Add Item</Typography>
        <IconButton onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </Box>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Upload calls, documents, audio, or create AI summaries
      </Typography>
      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          {[
            { key: 'call', label: 'Call', icon: Phone, color: '#4285f4' },
            { key: 'document', label: 'Document', icon: CloudUpload, color: '#34a853' },
            { key: 'audio', label: 'Audio', icon: Mic, color: '#fbbc04' },
            { key: 'ai', label: 'AI Summary', icon: AutoAwesome, color: '#9c27b0' },
            { key: 'task', label: 'Create Task', icon: CheckBox, color: '#ea4335' }
          ].map(({ key, label, icon: Icon, color }) => (
            <Button
              key={key}
              fullWidth
              variant={selectedAction === key ? 'contained' : 'outlined'}
              startIcon={<Icon />}
              onClick={() => setSelectedAction(key)}
              sx={{
                mb: 1,
                justifyContent: 'flex-start',
                backgroundColor: selectedAction === key ? color : 'transparent',
                borderColor: selectedAction === key ? color : '#e0e0e0',
                color: selectedAction === key ? 'white' : color,
                '&:hover': {
                  backgroundColor: selectedAction === key ? color : `${color}15`
                }
              }}
            >
              {label}
            </Button>
          ))}
        </Box>
      </Box>
      {selectedAction === 'call' && (
        <>
          <Button
            variant="outlined"
            component="label"
            fullWidth
            sx={{ mb: 2, p: 2, textAlign: 'left', justifyContent: 'flex-start' }}
          >
            {formData.file ? formData.file.name : 'Choose file | No file chosen'}
            <input
              type="file"
              hidden
              accept="audio/*,video/*"
              onChange={handleFileChange}
            />
          </Button>
          <TextField
            fullWidth
            label="Call Title"
            placeholder="e.g., Initial intro call"
            value={formData.title}
            onChange={(e) => handleInputChange('title', e.target.value)}
            sx={{ mb: 2 }}
          />
          <TextField
            fullWidth
            label="Duration (minutes)"
            type="number"
            placeholder="30"
            value={formData.duration}
            onChange={(e) => handleInputChange('duration', e.target.value)}
          />
        </>
      )}

      {selectedAction === 'document' && (
        <>
          <Button
            variant="outlined"
            component="label"
            fullWidth
            sx={{ mb: 2, p: 2, textAlign: 'left', justifyContent: 'flex-start' }}
          >
            {formData.file ? formData.file.name : 'Choose file | No file chosen'}
            <input
              type="file"
              hidden
              accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.html,.htm"
              onChange={handleFileChange}
            />
          </Button>
          <TextField
            fullWidth
            label="Document Title"
            placeholder="e.g., Pitch Deck"
            value={formData.title}
            onChange={(e) => handleInputChange('title', e.target.value)}
          />
        </>
      )}

      {selectedAction === 'audio' && (
        <>
          <Button
            variant="outlined"
            component="label"
            fullWidth
            sx={{ mb: 2, p: 2, textAlign: 'left', justifyContent: 'flex-start' }}
          >
            {formData.file ? formData.file.name : 'Choose file | No file chosen'}
            <input
              type="file"
              hidden
              accept="audio/*"
              onChange={handleFileChange}
            />
          </Button>
          <TextField
            fullWidth
            label="Summary Title"
            placeholder="e.g., Meeting notes"
            value={formData.title}
            onChange={(e) => handleInputChange('title', e.target.value)}
            sx={{ mb: 2 }}
          />
          <Box sx={{ p: 2, backgroundColor: '#f5f5f5', borderRadius: 1, textAlign: 'center' }}>
            <Mic sx={{ fontSize: 32, color: 'text.secondary', mb: 1 }} />
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Or record directly
            </Typography>
            <Button variant="outlined" size="small">
              Start Recording
            </Button>
          </Box>
        </>
      )}

      {selectedAction === 'task' && (
        <>
          <TextField
            fullWidth
            label="Task Name"
            placeholder="e.g., Review financial documents"
            value={formData.title}
            onChange={(e) => handleInputChange('title', e.target.value)}
            sx={{ mb: 2 }}
          />
          <Autocomplete
            fullWidth
            size="small"
            multiple
            value={Array.isArray(formData.assignedTo)
              ? teamMembers.filter(m => formData.assignedTo.includes(m.pk))
              : []}
            onChange={(event, newValue) =>
              handleInputChange(
                'assignedTo',
                Array.isArray(newValue) ? newValue.map(m => m.pk) : []
              )
            }
            options={teamMembers}
            getOptionLabel={(member) => `${member.first_name} ${member.last_name}`}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Assign To"
                placeholder="Search team members..."
              />
            )}
            sx={{ mb: 2 }}
          />
          <TextField
            fullWidth
            label="Deadline"
            type="date"
            InputLabelProps={{ shrink: true }}
            value={formData.deadline}
            onChange={(e) => handleInputChange('deadline', e.target.value)}
            sx={{ mb: 2 }}
          />
          <Autocomplete
            fullWidth
            size="small"
            value={{ value: formData.priority, label: formData.priority.charAt(0).toUpperCase() + formData.priority.slice(1) }}
            onChange={(event, newValue) => handleInputChange('priority', newValue ? newValue.value : 'medium')}
            options={[
              { value: 'high', label: 'High' },
              { value: 'medium', label: 'Medium' },
              { value: 'low', label: 'Low' }
            ]}
            getOptionLabel={(option) => option.label}
            isOptionEqualToValue={(option, value) => option.value === value.value}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Priority"
                placeholder="Select priority..."
              />
            )}
            sx={{ mb: 2 }}
          />
          <TextField
            fullWidth
            multiline
            rows={4}
            label="Description"
            placeholder="Task details..."
            value={formData.description}
            onChange={(e) => handleInputChange('description', e.target.value)}
          />
        </>
      )}

      <Button
        variant="contained"
        fullWidth
        onClick={handleSubmit}
        disabled={loading || (!formData.title || (selectedAction !== 'task' && !formData.file))}
        sx={{
          mt: 3,
          backgroundColor: selectedAction === 'call' ? '#4285f4' :
            selectedAction === 'document' ? '#34a853' :
              selectedAction === 'audio' ? '#fbbc04' :
                selectedAction === 'ai' ? '#9c27b0' : '#ea4335'
        }}
      >
        {loading ? 'Uploading...' :
          selectedAction === 'call' ? 'Upload Call' :
            selectedAction === 'document' ? 'Upload Document' :
              selectedAction === 'audio' ? 'Save Audio' :
                selectedAction === 'ai' ? 'Save Summary' : 'Create Task'}
      </Button>
    </Box>
  );
}

function CompanyTasksView({ companyId, tasksByStage, stages, onTasksUpdate }) {
  const userInfo = UserInfo();
  const isSuperAdmin = userInfo?.role?.includes('SUPER_ADMIN');

  const [tasks, setTasks] = useState([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [teamMembers, setTeamMembers] = useState([]);
  const [teams, setTeams] = useState([]);
  const [isTeamLead, setIsTeamLead] = useState(false);
  const [currentTab, setCurrentTab] = useState(0); // 0: All, 1: Self created, 2: Assigned to me, 3: Assigned by me
  const [confirmDialog, setConfirmDialog] = useState({ open: false, task: null });
  const [deleteDialog, setDeleteDialog] = useState({ open: false, task: null });
  const [editingTaskId, setEditingTaskId] = useState(null);
  const [editTask, setEditTask] = useState({
    title: '',
    description: '',
    priority: 'medium',
    due_date: '',
    company: companyId,
    stage: ''
  });

  const [quickAddTask, setQuickAddTask] = useState({
    title: '',
    stage: '',
    due_date: '',
    assigned_to: [],
    assigned_team: [],
    assignment_type: 'user' // 'user' or 'team'
  });
  const [filters, setFilters] = useState({
    search: '',
    user: '',
    stage: '',
    team: '',
    startDate: '',
    endDate: ''
  });
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });

  const statusOptions = [
    { value: 'To Do', label: 'To Do', color: '#6c757d' },
    { value: 'In Progress', label: 'In Progress', color: '#007bff' },
    { value: 'Pending', label: 'Pending', color: '#ffc107' },
    { value: 'Blocked', label: 'Blocked', color: '#dc3545' },
    { value: 'Done', label: 'Done', color: '#28a745' }
  ];

  useEffect(() => {
    if (!userInfo?.pk) return;
    fetchInitialData();
    if (!isSuperAdmin && !isTeamLead) {
      setQuickAddTask(prev => ({ ...prev, assigned_to: userInfo.pk }));
    }
  }, [userInfo?.pk, isSuperAdmin, isTeamLead]);

  useEffect(() => {
    if (currentTab === 1 || currentTab === 2) {
      setQuickAddTask(prev => ({
        ...prev,
        assignment_type: 'user',
        assigned_to: userInfo?.pk || '',
        assigned_team: ''
      }));
    } else {
      setQuickAddTask(prev => ({
        ...prev,
        assignment_type: 'user',
        assigned_to: '',
        assigned_team: ''
      }));
    }
  }, [currentTab, userInfo?.pk]);

  useEffect(() => {
    if (userInfo?.pk) {
      fetchTeamMembers();
    }
  }, [currentTab, isTeamLead]);

  useEffect(() => {
    // Reset pagination when filters or tab change
    setPage(1);
    setHasMore(true);
    fetchTasks(1, false);
  }, [filters, currentTab]);

  const fetchInitialData = async () => {
    try {
      const teamsRes = await tasksApi.getTeams();
      const teamsData = Array.isArray(teamsRes?.data)
        ? teamsRes.data
        : Array.isArray(teamsRes?.data?.data)
          ? teamsRes.data.data
          : [];
      setTeams(teamsData);

      if (isSuperAdmin) {
        setIsTeamLead(false);
        await fetchTeamMembers(false);
      } else {
        await checkTeamLeadStatus();
      }
    } catch (error) {
      showToast('Error loading data', 'error');
    }
  };

  const checkTeamLeadStatus = async () => {
    try {
      const response = await tasksApi.checkTeamLeadStatus();
      const isLead = !!response?.data?.is_team_lead;
      setIsTeamLead(isLead);
      await fetchTeamMembers(isLead);
    } catch (error) {
      setIsTeamLead(false);
      await fetchTeamMembers(false);
    }
  };

  const fetchTeamMembers = async (teamLeadStatus = null) => {
    try {
      if (!userInfo?.pk) return;

      const currentUserMember = {
        pk: userInfo.pk,
        username: userInfo.username,
        first_name: userInfo.first_name,
        last_name: userInfo.last_name,
        is_active: true
      };

      if (isSuperAdmin) {
        const membersRes = await tasksApi.getAllTeamMembers(
          companyId ? { company_id: companyId } : undefined
        );
        const rows = Array.isArray(membersRes?.data?.data)
          ? membersRes.data.data
          : Array.isArray(membersRes?.data)
            ? membersRes.data
            : [];
        const users = rows
          .map(item => item?.user || item)
          .filter(u => u && u.pk);
        setTeamMembers(users);
        return;
      }

      // const isLead = teamLeadStatus !== null ? teamLeadStatus : isTeamLead;
      // if (!isLead) {
      //   setTeamMembers([currentUserMember]);
      //   return;
      // }

      if (currentTab === 1 || currentTab === 2) {
        setTeamMembers([currentUserMember]);
        return;
      }

      const membersRes = await tasksApi.getAllTeamMembers({
        // team_lead_id: userInfo.pk,
        ...(companyId && { company_id: companyId }),
      });
      const rows = Array.isArray(membersRes?.data?.data)
        ? membersRes.data.data
        : Array.isArray(membersRes?.data)
          ? membersRes.data
          : [];
      const users = rows
        .map(item => item?.user || item)
        .filter(u => u && u.pk);
      setTeamMembers(users);
    } catch (error) {
      if (userInfo?.pk) {
        setTeamMembers([{
          pk: userInfo.pk,
          username: userInfo.username,
          first_name: userInfo.first_name,
          last_name: userInfo.last_name,
          is_active: true
        }]);
      }
    }
  };

  const fetchTasks = async (pageToLoad = 1, append = false) => {
    try {
      if (append) {
        setIsLoadingMore(true);
      } else {
        setIsLoading(true);
      }

      const serverFilters = { company_id: companyId };

      if (filters.search) serverFilters.search = filters.search;
      if (filters.user) serverFilters.assigned_to = filters.user;
      if (filters.stage) serverFilters.stage_id = filters.stage;
      if (filters.team) serverFilters.assigned_team = filters.team;
      if (filters.startDate) serverFilters.start_date = filters.startDate;
      if (filters.endDate) serverFilters.end_date = filters.endDate;

      if (currentTab === 1) {
        serverFilters.type = 'self';
      } else if (currentTab === 2) {
        serverFilters.type = 'to_me';
      } else if (currentTab === 3) {
        serverFilters.type = 'by_me';
      } else if (currentTab === 4) {
        serverFilters.type = 'done';
      }

      // Pagination params
      serverFilters.page = pageToLoad;
      serverFilters.page_size = 10;

      const response = await tasksApi.getAllTasks(serverFilters);
      const data = response?.data;
      let newTasks = [];
      let nextExists = false;

      if (Array.isArray(data)) {
        // Backward compatibility: API returned a plain list
        newTasks = data;
        nextExists = false;
      } else if (data && Array.isArray(data.results)) {
        // Paginated response from CustomPagination
        newTasks = data.results;
        nextExists = !!data.next;
      }

      setTasks(prev => (append ? [...prev, ...newTasks] : newTasks));
      setPage(pageToLoad);
      setHasMore(nextExists);
    } catch (error) {
      showToast('Error fetching tasks', 'error');
    } finally {
      setIsLoading(false);
      setIsLoadingMore(false);
    }
  };

  const handleLoadMore = () => {
    if (isLoadingMore || !hasMore) return;
    fetchTasks(page + 1, true);
  };

  const handleTableScroll = (event) => {
    const target = event.currentTarget;
    if (!target) return;
    const { scrollTop, scrollHeight, clientHeight } = target;
    // When user scrolls within 200px of bottom, try to load more
    if (scrollHeight - scrollTop - clientHeight < 200) {
      handleLoadMore();
    }
  };

  const handleQuickAddTask = async () => {
    const hasAssignment = quickAddTask.assignment_type === 'user' ? quickAddTask.assigned_to : quickAddTask.assigned_team;

    if (!quickAddTask.title || !quickAddTask.due_date || !hasAssignment) {
      showToast('Please fill all required fields', 'error');
      return;
    }

    try {
      const taskData = {
        title: quickAddTask.title,
        due_date: quickAddTask.due_date ? new Date(quickAddTask.due_date + 'T23:59:59').toISOString() : null,
        priority: 'medium',
        status: 'To Do',
        company: companyId
      };

      if (quickAddTask.stage) taskData.stage = quickAddTask.stage;

      if (quickAddTask.assignment_type === 'user') {
        taskData.assigned_to = quickAddTask.assigned_to;
      } else {
        taskData.assigned_team = quickAddTask.assigned_team;
      }

      await tasksApi.createTask(taskData);

      setQuickAddTask({
        title: '',
        stage: '',
        due_date: '',
        assigned_to: [],
        assigned_team: [],
        assignment_type: 'user'
      });

      fetchTasks();
      showToast('Task created successfully', 'success');
    } catch (error) {
      showToast('Error creating task', 'error');
    }
  };

  const handleMarkAsDone = async (task) => {
    try {
      await tasksApi.updateTaskStatus(task.id, 'Done');
      setConfirmDialog({ open: false, task: null });
      fetchTasks();
      showToast('Task marked as done', 'success');
    } catch (error) {
      showToast('Error updating task', 'error');
    }
  };

  const handleEditTask = (task) => {
    setEditTask({
      title: task.title,
      description: task.description || '',
      priority: task.priority || 'medium',
      due_date: task.due_date ? task.due_date.split('T')[0] : '',
      stage: task.stage
    });
    setEditingTaskId(task.id);
  };

  const handleUpdateTask = async (taskId) => {
    try {
      const taskData = { ...editTask };
      if (taskData.due_date) {
        taskData.due_date = new Date(taskData.due_date + 'T23:59:59').toISOString();
      }
      await tasksApi.updateTask(taskId, taskData);
      setEditingTaskId(null);
      fetchTasks();
      showToast('Task updated successfully', 'success');
    } catch (error) {
      showToast('Error updating task', 'error');
    }
  };

  const handleCancelEdit = () => {
    setEditingTaskId(null);
    setEditTask({
      title: '',
      description: '',
      priority: 'medium',
      due_date: '',
      stage: ''
    });
  };

  const canEditTask = (task) => {
    return task.created_by === userInfo?.pk;
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const getStatusColor = (status) => {
    const statusOption = statusOptions.find(option => option.value === status);
    return statusOption ? statusOption.color : '#6c757d';
  };

  const getStageName = (stageId) => {
    const stage = stages.find(s => s.id === stageId);
    return stage ? stage.name : 'Unknown';
  };

  const getAssigneeName = (task) => {
    if (task.assigned_team_name) {
      return `Team: ${task.assigned_team_name}`;
    }
    if (Array.isArray(task.assigned_to_details) && task.assigned_to_details.length) {
      return task.assigned_to_details
        .map(u => `${u.first_name || ''} ${u.last_name || ''}`.trim())
        .filter(name => name.length > 0)
        .join(', ');
    }
    return 'Unassigned';
  };

  const getDueStatus = (dueDate) => {
    if (!dueDate) return 'no-date';

    const now = new Date();
    const due = new Date(dueDate);
    const diffDays = Math.ceil((due - now) / (1000 * 60 * 60 * 24));

    if (diffDays < 0) return 'overdue';
    if (diffDays <= 3) return 'due-soon';
    return 'on-time';
  };

  const handleDeleteTask = async (task) => {
    try {
      await tasksApi.deleteTask(task.id);
      setDeleteDialog({ open: false, task: null });
      fetchTasks();
      showToast('Task deleted successfully', 'success');
    } catch (error) {
      showToast('Error deleting task', 'error');
    }
  };

  const isTaskNew = (createdDate) => {
    const now = new Date();
    const created = new Date(createdDate);
    const diffHours = (now - created) / (1000 * 60 * 60);
    return diffHours < 24;
  };

  const isTaskAssignee = (task) => {
    return task.assigned_to === userInfo?.pk;
  };

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const getSortIcon = (columnKey) => {
    if (sortConfig.key !== columnKey) {
      return '↕️';
    }
    return sortConfig.direction === 'asc' ? '↑' : '↓';
  };

  const filteredTasks = tasks.sort((a, b) => {
    if (sortConfig.key === 'due_date') {
      const aDate = a.due_date ? new Date(a.due_date) : null;
      const bDate = b.due_date ? new Date(b.due_date) : null;

      if (!aDate && !bDate) return 0;
      if (!aDate) return sortConfig.direction === 'asc' ? 1 : -1;
      if (!bDate) return sortConfig.direction === 'asc' ? -1 : 1;

      const comparison = aDate - bDate;
      return sortConfig.direction === 'asc' ? comparison : -comparison;
    }

    if (a.status === 'Done' && b.status === 'Done' && a.completed_at && b.completed_at) {
      return new Date(b.completed_at) - new Date(a.completed_at);
    }
    return 0;
  });

  const debouncedSearch = debounce((query) => {
    setFilters({ ...filters, search: query })
  });

  return (
    <Stack sx={{ minWidth: "100%", maxHeight: "100%", p: 0 }}>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{ mb: 3 }}
      >
        <Typography variant="h4" sx={{ fontWeight: 600, mb: 1 }}>
          Company Tasks
        </Typography>

        {/* Tabs */}
        <Tabs
          value={currentTab}
          onChange={(e, newValue) => setCurrentTab(newValue)}
        >
          <Tab label="All" />
          <Tab label="Self created" />
          <Tab label="Assigned to me" />
          <Tab label="Assigned by me" />
          <Tab label="Done Tasks" />
        </Tabs>
      </Stack>

      {/* Filters */}
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, mb: 2 }}>
        {/* Row 1: Applied Filters */}
        {/* {(filters.user ||
          filters.stage ||
          filters.team ||
          filters.startDate ||
          filters.endDate) && (
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              gap: 1,
              alignItems: "center",
            }}
          >
            {filters.user && (
              <Chip
                label={`User: ${
                  teamMembers?.find((u) => u.pk == filters.user)
                    ? `${
                        teamMembers.find((u) => u.pk == filters.user)
                          .first_name
                      } ${
                        teamMembers.find((u) => u.pk == filters.user)
                          .last_name
                      }`
                    : ""
                }`}
                color="primary"
                variant="outlined"
                size="small"
              />
            )}
            {filters.stage && (
              <Chip
                label={`Stage: ${
                  stages.find((s) => s.id == filters.stage)?.name || ""
                }`}
                color="success"
                variant="outlined"
                size="small"
              />
            )}
            {filters.team && (
              <Chip
                label={`Team: ${
                  teams.find((t) => t.id == filters.team)?.name || ""
                }`}
                color="info"
                variant="outlined"
                size="small"
              />
            )}
            {filters.startDate && (
              <Chip
                label={`From: ${filters.startDate}`}
                color="default"
                variant="outlined"
                size="small"
              />
            )}
            {filters.endDate && (
              <Chip
                label={`To: ${filters.endDate}`}
                color="default"
                variant="outlined"
                size="small"
              />
            )}
          </Box>
        )} */}

        {/* Row 2: Search + Filters */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: "wrap",
            gap: 2,
          }}
        >
          <Box
            sx={{
              display: "flex",
              gap: 2,
              flexWrap: "wrap",
              justifyContent: "flex-start",
            }}
          >
            {/* Search */}
            <Box sx={{ flex: 1, maxWidth: 300 }}>
              <TextField
                size="small"
                placeholder="Search tasks..."
                defaultValue={filters.search}
                onChange={(e) => debouncedSearch(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
                fullWidth
              />
            </Box>

            {/* Autocomplete for User */}
            <Autocomplete
              sx={{ minWidth: 150 }}
              size="small"
              options={teamMembers || []}
              getOptionLabel={(o) => o ? `${o.first_name || ''} ${o.last_name || ''}` : ''}
              value={teamMembers?.find((u) => u?.pk === filters.user) || null}
              onChange={(e, val) =>
                setFilters({ ...filters, user: val?.pk || "" })
              }
              renderInput={(params) => <TextField {...params} label="User" />}
            />

            {/* Autocomplete for Team */}
            <Autocomplete
              sx={{ minWidth: 200 }}
              size="small"
              options={teams}
              getOptionLabel={(o) => o.name}
              value={teams.find((t) => t.id === filters.team) || null}
              onChange={(e, val) =>
                setFilters({ ...filters, team: val?.id || "" })
              }
              renderInput={(params) => <TextField {...params} label="Team" />}
            />

            {/* Autocomplete for Stage */}
            <Autocomplete
              sx={{ minWidth: 200 }}
              size="small"
              options={stages}
              getOptionLabel={(o) => o.name}
              value={stages.find((s) => s.id === filters.stage) || null}
              onChange={(e, val) =>
                setFilters({ ...filters, stage: val?.id || "" })
              }
              renderInput={(params) => (
                <TextField {...params} label="Stage" />
              )}
            />
          </Box>

          {/* Date Filters */}
          <Box sx={{ display: "flex", flexDirection: "row", gap: 2 }}>
            <TextField
              size="small"
              type="date"
              label="From Date"
              InputLabelProps={{ shrink: true }}
              value={filters.startDate}
              onChange={(e) => setFilters({ ...filters, startDate: e.target.value })}
            />

            <TextField
              size="small"
              type="date"
              label="To Date"
              InputLabelProps={{ shrink: true }}
              value={filters.endDate}
              onChange={(e) => setFilters({ ...filters, endDate: e.target.value })}
            />

            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
              <Button
                variant="outlined"
                size="small"
                onClick={() => {
                  setFilters({
                    search: '',
                    user: '',
                    stage: '',
                    team: '',
                    startDate: '',
                    endDate: '',
                  });
                }}
              >
                Clear
              </Button>
            </Box>
          </Box>
        </Box>
      </Box>

      {/* Task Table */}
      <TableContainer
        component={Paper}
        sx={{ mb: 2, height: 'calc(100vh - 440px)', flexGrow: 1 }}
        onScroll={handleTableScroll}
      >
        <Table>
          <TableHead>
            <TableRow sx={{ bgcolor: '#f5f5f5' }}>
              <TableCell sx={{ fontWeight: 600 }}>STAGE</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>TASK</TableCell>
              <TableCell
                sx={{
                  fontWeight: 600,
                  cursor: "pointer",
                  userSelect: "none",
                  "&:hover": { bgcolor: "#e0e0e0" },
                }}
                onClick={() => handleSort("due_date")}
              >
                DUE DATE {getSortIcon("due_date")}
              </TableCell>
              <TableCell sx={{ fontWeight: 600, textAlign: "center" }}>
                ASSIGNED BY
              </TableCell>
              <TableCell sx={{ fontWeight: 600, textAlign: "center" }}>
                ASSIGNEE
              </TableCell>
              <TableCell sx={{ fontWeight: 600, textAlign: "center" }}>
                ACTIONS
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody sx={{ width: "100%" }}>
            {filteredTasks.length ? (
              filteredTasks.map((task) => (
                editingTaskId === task.id ? (
                  <TableRow key={`edit-${task.id}`} sx={{ bgcolor: '#f8f9fa' }}>
                    <TableCell>
                      <Autocomplete
                        size="small"
                        sx={{ minWidth: 120 }}
                        value={stages.find(s => s.id === editTask.stage) || null}
                        onChange={(event, newValue) => setEditTask({ ...editTask, stage: newValue ? newValue.id : '' })}
                        options={stages}
                        getOptionLabel={(option) => option.name || ''}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            placeholder="Select stage..."
                          />
                        )}
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        size="small"
                        value={editTask.title}
                        onChange={(e) => setEditTask({ ...editTask, title: e.target.value })}
                        sx={{ minWidth: 200 }}
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        size="small"
                        type="date"
                        value={editTask.due_date}
                        onChange={(e) => setEditTask({ ...editTask, due_date: e.target.value })}
                        sx={{ minWidth: 140 }}
                      />
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, justifyContent: 'center' }}>
                        <Tooltip title={task.created_by_details ? `${task.created_by_details.first_name} ${task.created_by_details.last_name}` : 'Unknown'} arrow>
                          <Avatar sx={{ width: 24, height: 24, bgcolor: '#ff9800', fontSize: '0.75rem' }}>
                            {task.created_by_details ? task.created_by_details.first_name.charAt(0) : 'U'}
                          </Avatar>
                        </Tooltip>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Tooltip title={getAssigneeName(task)} arrow>
                          <Avatar sx={{ width: 24, height: 24, bgcolor: '#1976d2', fontSize: '0.75rem' }}>
                            {getAssigneeName(task).charAt(0)}
                          </Avatar>
                        </Tooltip>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Button
                          size="small"
                          variant="contained"
                          onClick={() => handleUpdateTask(task.id)}
                          disabled={!editTask.title}
                          sx={{ bgcolor: '#28a745', '&:hover': { bgcolor: '#218838' } }}
                        >
                          Save
                        </Button>
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={handleCancelEdit}
                        >
                          Cancel
                        </Button>
                      </Box>
                    </TableCell>
                  </TableRow>
                ) : (
                  <StyledTableRow
                    key={task.id}
                    isNew={isTaskNew(task.created_at)}
                    dueStatus={getDueStatus(task.due_date)}
                    isCompleted={task.status === 'Done'}
                  >
                    <TableCell>
                      <Typography variant="body2" sx={{ color: '#666' }}>
                        {getStageName(task.stage)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {task.title}
                      </Typography>
                      {task.description && (
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                          {task.description}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ color: '#666', whiteSpace: 'nowrap' }}>
                        {formatDate(task.due_date) || '-'}
                        {task.is_overdue && ' ⚠️'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ color: '#666', textAlign: 'center' }}>
                        {task.created_by_details ? `${task.created_by_details.first_name} ${task.created_by_details.last_name}` : 'Unknown'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ color: '#666', textAlign: 'center' }}>
                        {getAssigneeName(task)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Box
                        sx={{
                          display: "flex",
                          gap: 1,
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        {canEditTask(task) && task.status !== "Done" && (
                          <IconButton
                            size="small"
                            onClick={() => handleEditTask(task)}
                            sx={{ color: "#1976d2" }}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        )}
                        {canEditTask(task) && (
                          <IconButton
                            size="small"
                            onClick={() =>
                              setDeleteDialog({ open: true, task })
                            }
                            sx={{ color: "#d32f2f" }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        )}
                        {task.status !== "Done" && isTaskAssignee(task) ? (
                          <DoneButton
                            size="small"
                            onClick={() =>
                              setConfirmDialog({ open: true, task })
                            }
                          >
                            Done
                          </DoneButton>
                        ) : task.status === "Done" ? (
                          <IconButton
                            size="small"
                            sx={{ color: "#000", bgcolor: "transparent" }}
                          >
                            <CheckIcon fontSize="small" />
                          </IconButton>
                        ) : null}
                      </Box>
                    </TableCell>
                  </StyledTableRow>
                )
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={6}>
                  <Typography
                    variant="body2"
                    sx={{ p: 3, color: "#666", textAlign: "center" }}
                  >
                    No tasks exist.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>

        {/* Infinite scroll loading indicator for company tasks */}
        {isLoadingMore && (
          <Box sx={{ py: 1.5, textAlign: 'center', color: '#666', fontSize: '0.8rem' }}>
            Loading more tasks...
          </Box>
        )}
      </TableContainer>

      {/* Quick Add Task Row */}
      <Box sx={{
        position: 'sticky',
        bottom: 0,
      }}>
        <Box sx={{
          display: 'flex',
          gap: 1,
          p: 1.5,
          alignItems: 'center',
          bgcolor: '#ffffff',
          border: '1px solid #e0e0e0',
          borderRadius: 1,
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 150 }}>
            <Box sx={{ width: 4, height: 40, bgcolor: '#ff5722', borderRadius: 1 }} />
            <Typography variant="body2" sx={{ color: '#666' }}>
              Current Company
            </Typography>
          </Box>

          <Autocomplete
            size="small"
            sx={{ minWidth: 120 }}
            value={stages.find(s => s.id === quickAddTask.stage) || null}
            onChange={(event, newValue) => setQuickAddTask({ ...quickAddTask, stage: newValue ? newValue.id : '' })}
            options={stages}
            getOptionLabel={(option) => option.name || ''}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder="Stages"
                sx={{
                  '& .MuiInputBase-root': {
                    bgcolor: '#f8f9fa',
                    border: '1px solid #dee2e6',
                    borderRadius: '4px',
                    '&:hover': {
                      borderColor: '#adb5bd'
                    }
                  },
                  '& .MuiOutlinedInput-notchedOutline': {
                    border: 'none'
                  }
                }}
              />
            )}
          />

          <TextField
            size="small"
            placeholder="Task title..."
            value={quickAddTask.title}
            onChange={(e) => setQuickAddTask({ ...quickAddTask, title: e.target.value })}
            sx={{
              flex: 1,
              '& .MuiOutlinedInput-root': {
                bgcolor: '#f8f9fa',
                border: '1px solid #dee2e6',
                borderRadius: '4px',
                '&:hover': {
                  borderColor: '#adb5bd'
                }
              }
            }}
            variant="outlined"
          />

          <Tooltip title="Due Date (dd/mm/yyyy)" arrow>
            <TextField
              size="small"
              type="date"
              placeholder="dd/mm/yyyy"
              value={quickAddTask.due_date}
              onChange={(e) => setQuickAddTask({ ...quickAddTask, due_date: e.target.value })}
              sx={{
                minWidth: 140,
                '& .MuiOutlinedInput-root': {
                  bgcolor: '#f8f9fa',
                  border: '1px solid #dee2e6',
                  borderRadius: '4px',
                  '&:hover': {
                    borderColor: '#adb5bd'
                  }
                }
              }}
              variant="outlined"
            />
          </Tooltip>

          <Autocomplete
            size="small"
            multiple
            options={
              quickAddTask.assignment_type === 'user'
                ? (currentTab === 1 || currentTab === 2)
                  ? [{ pk: userInfo?.pk, first_name: userInfo?.first_name, last_name: userInfo?.last_name }]
                  : teamMembers
                : teams
            }
            getOptionLabel={(option) => {
              if (quickAddTask.assignment_type === 'user') {
                return option.first_name && option.last_name
                  ? `${option.first_name} ${option.last_name}`
                  : '';
              }
              return option.name || '';
            }}
            value={
              quickAddTask.assignment_type === 'user'
                ? (currentTab === 1 || currentTab === 2)
                  ? [
                    {
                      pk: userInfo?.pk,
                      first_name: userInfo?.first_name,
                      last_name: userInfo?.last_name
                    }
                  ]
                  : (Array.isArray(teamMembers)
                    ? teamMembers.filter(m => Array.isArray(quickAddTask.assigned_to) && quickAddTask.assigned_to.includes(m.pk))
                    : [])
                : (Array.isArray(teams)
                  ? teams.filter(t => Array.isArray(quickAddTask.assigned_team) && quickAddTask.assigned_team.includes(t.id))
                  : [])
            }
            onChange={(event, newValue) => {
              if (quickAddTask.assignment_type === 'user') {
                setQuickAddTask({ ...quickAddTask, assigned_to: Array.isArray(newValue) ? newValue.map(m => m.pk) : [] });
              } else {
                setQuickAddTask({ ...quickAddTask, assigned_team: Array.isArray(newValue) ? newValue.map(t => t.id) : [] });
              }
            }}
            disabled={!isSuperAdmin && quickAddTask.assignment_type === 'team'}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder={quickAddTask.assignment_type === 'user' ? 'Select User' : 'Select Team'}
                sx={{
                  minWidth: 200,
                  '& .MuiOutlinedInput-root': {
                    bgcolor: '#f8f9fa',
                    border: '1px solid #dee2e6',
                    borderRadius: '4px',
                    '&:hover': {
                      borderColor: '#adb5bd'
                    },
                    '& .MuiOutlinedInput-notchedOutline': {
                      border: 'none'
                    }
                  }
                }}
              />
            )}
            sx={{ minWidth: 200 }}
            isOptionEqualToValue={(option, value) => {
              if (quickAddTask.assignment_type === 'user') {
                return option.pk === value?.pk;
              }
              return option.id === value?.id;
            }}
          />

          <Button
            variant="contained"
            size="small"
            onClick={handleQuickAddTask}
            disabled={
              !quickAddTask.title ||
              (!quickAddTask.assigned_to && !quickAddTask.assigned_team)
            }
            sx={{
              bgcolor: '#28a745',
              color: 'white',
              px: 3,
              py: 1,
              fontSize: '0.875rem',
              fontWeight: 600,
              textTransform: 'none',
              borderRadius: '4px',
              '&:hover': {
                bgcolor: '#218838'
              },
              '&:disabled': {
                bgcolor: '#6c757d',
                color: 'white'
              }
            }}
          >
            Add
          </Button>
        </Box>
        <Typography variant="body2" sx={{ mt: 1, color: "#666" }}>
          Showing {filteredTasks.length} of {filteredTasks.length} tasks
        </Typography>
      </Box>


      {/* Confirmation Dialog */}
      <Dialog open={confirmDialog.open} onClose={() => setConfirmDialog({ open: false, task: null })}>
        <DialogTitle>Mark Task as Done</DialogTitle>
        <DialogContent>
          <Typography>
            Mark "{confirmDialog.task?.title}" as done?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDialog({ open: false, task: null })}>Cancel</Button>
          <Button
            onClick={() => handleMarkAsDone(confirmDialog.task)}
            variant="contained"
            color="success"
          >
            Mark as Done
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialog.open}
        onClose={() => setDeleteDialog({ open: false, task: null })}
      >
        <DialogTitle>Delete Task</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete "{deleteDialog.task?.title}"? This
            action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setDeleteDialog({ open: false, task: null })}
          >
            Cancel
          </Button>
          <Button
            onClick={() => handleDeleteTask(deleteDialog.task)}
            variant="contained"
            color="error"
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}

// Action Sidebar Component
function ActionSidebar({ action, stage, stages = [], teamMembers, companyId, companyData, companyTeamAssignments = [], userRoles = [], onClose, getJourneyData, getTasksByStages }) {
  const [formData, setFormData] = useState({
    file: null,
    title: '',
    duration: '',
    description: '',
    assignedTo: [],
    deadline: '',
    priority: 'medium',
    selectedStage: stage?.id || ''
  });
  const [loading, setLoading] = useState(false);

  // Update selectedStage when stage prop changes
  useEffect(() => {
    if (stage?.id && !formData.selectedStage) {
      setFormData(prev => ({ ...prev, selectedStage: parseInt(stage.id) }));
    }
  }, [stage, formData.selectedStage]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    setFormData(prev => ({ ...prev, file }));
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const formDataToSend = new FormData();

      if (action === 'call') {
        formDataToSend.append('file', formData.file);
        formDataToSend.append('title', formData.title);
        formDataToSend.append('duration', formData.duration);
        formDataToSend.append('company_id', companyId);
        formDataToSend.append('stage', stage?.name || 'INTRO');

        await apiInstance.post('journey/meetings/', formDataToSend, {
          headers: {
            'Content-Type': 'multipart/form-data',
            Authorization: `Token ${localStorage.getItem('authToken')}`
          }
        });
      } else if (action === 'audio') {
        formDataToSend.append('file', formData.file);
        formDataToSend.append('title', formData.title);
        formDataToSend.append('company_id', companyId);
        formDataToSend.append('stage', stage?.name || 'INTRO');

        await apiInstance.post('journey/audio-recordings/', formDataToSend, {
          headers: {
            'Content-Type': 'multipart/form-data',
            Authorization: `Token ${localStorage.getItem('authToken')}`
          }
        });
      } else if (action === 'task') {
        const taskData = {
          title: formData.title,
          description: formData.description,
          priority: formData.priority,
          status: 'To Do',
          company: parseInt(companyId),
          stage: parseInt(formData.selectedStage) || parseInt(stage?.id) || 1,
          // ensure assigned_to is an array of user IDs
          assigned_to: Array.isArray(formData.assignedTo)
            ? formData.assignedTo.map(id => parseInt(id))
            : (formData.assignedTo ? [parseInt(formData.assignedTo)] : []),
          due_date: formData.deadline || ''
        };

        if (taskData.due_date) {
          taskData.due_date = new Date(taskData.due_date + 'T23:59:59').toISOString();
        }

        await tasksApi.createTask(taskData);
      }

      showToast('Item added successfully', 'success');
      onClose();
      setFormData({ file: null, title: '', duration: '', description: '', assignedTo: [], deadline: '', priority: 'medium', selectedStage: parseInt(stage?.id) || '' });
      if (action === 'task' && getTasksByStages) {
        getTasksByStages();
      }
      getJourneyData();
    } catch (error) {
      showErrorToast('Error adding item');
    } finally {
      setLoading(false);
    }
  };

  const getActionConfig = () => {
    switch (action) {
      case 'call':
        return {
          title: 'Call',
          color: '#4285f4',
          icon: Phone,
          buttonText: 'Upload Call'
        };
      case 'audio':
        return {
          title: 'Audio',
          color: '#fbbc04',
          icon: Mic,
          buttonText: 'Save Audio'
        };
      case 'document':
        return {
          title: 'AI Summary',
          color: '#9c27b0',
          icon: AutoAwesome,
          buttonText: 'Save Summary'
        };
      case 'task':
        return {
          title: 'Create Task',
          color: '#34a853',
          icon: CheckBox,
          buttonText: 'Create Task'
        };
      case 'meeting':
        return {
          title: 'Schedule Meeting',
          color: '#1976d2',
          icon: CalendarToday,
          buttonText: 'Schedule Meeting'
        };
      default:
        return {
          title: 'Add Item',
          color: '#666',
          icon: Add,
          buttonText: 'Save'
        };
    }
  };

  const config = getActionConfig();
  const Icon = config.icon;

  return (
    <Box sx={{ width: 400, p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: 1,
              backgroundColor: config.color,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <Icon sx={{ color: 'white', fontSize: 20 }} />
          </Box>
          <Typography variant="h6">{config.title}</Typography>
        </Box>
        <IconButton onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </Box>

      {/* Description text - only show for non-meeting actions */}
      {action !== 'meeting' && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Upload calls, documents, audio, or create AI summaries
        </Typography>
      )}

      {/* Action-specific content */}
      {action === 'meeting' && (
        <MeetingScheduler
          companyId={companyId}
          companyName={companyData?.company_name}
          stages={stages}
          currentStageId={stage?.id}
          currentStageName={stage?.name || stage?.label}
          companyCountry={companyData?.country?.label}
          companyTeamAssignments={companyTeamAssignments}
          userRoles={userRoles}
          onClose={onClose}
          onSuccess={() => {
            getJourneyData();
            onClose();
          }}
        />
      )}

      {action === 'call' && (
        <>
          <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>Call Recording File</Typography>
          <Button
            variant="outlined"
            component="label"
            fullWidth
            sx={{ mb: 2, p: 2, textAlign: 'left', justifyContent: 'flex-start' }}
          >
            {formData.file ? formData.file.name : 'Choose file | No file chosen'}
            <input
              type="file"
              hidden
              accept="audio/*,video/*"
              onChange={handleFileChange}
            />
          </Button>

          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>Call Title</Typography>
          <TextField
            fullWidth
            placeholder="e.g., Initial intro call"
            value={formData.title}
            onChange={(e) => handleInputChange('title', e.target.value)}
            sx={{ mb: 2 }}
          />

          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>Duration (minutes)</Typography>
          <TextField
            fullWidth
            type="number"
            placeholder="30"
            value={formData.duration}
            onChange={(e) => handleInputChange('duration', e.target.value)}
            sx={{ mb: 2 }}
          />
        </>
      )}

      {action === 'audio' && (
        <>
          <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>Audio File</Typography>
          <Button
            variant="outlined"
            component="label"
            fullWidth
            sx={{ mb: 2, p: 2, textAlign: 'left', justifyContent: 'flex-start' }}
          >
            {formData.file ? formData.file.name : 'Choose file | No file chosen'}
            <input
              type="file"
              hidden
              accept="audio/*"
              onChange={handleFileChange}
            />
          </Button>

          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>Summary Title</Typography>
          <TextField
            fullWidth
            placeholder="e.g., Meeting notes"
            value={formData.title}
            onChange={(e) => handleInputChange('title', e.target.value)}
            sx={{ mb: 3 }}
          />

          <Box sx={{ p: 3, backgroundColor: '#f5f5f5', borderRadius: 1, textAlign: 'center', mb: 2 }}>
            <Mic sx={{ fontSize: 32, color: 'text.secondary', mb: 1 }} />
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Or record directly
            </Typography>
            <Button variant="outlined" size="small">
              Start Recording
            </Button>
          </Box>
        </>
      )}

      {action === 'task' && (
        <>
          <TextField
            fullWidth
            placeholder="Task Title"
            value={formData.title}
            onChange={(e) => handleInputChange('title', e.target.value)}
            sx={{ mb: 2 }}
          />

          {/* <TextField 
            fullWidth 
            multiline 
            rows={4} 
            placeholder="Description"
            value={formData.description}
            onChange={(e) => handleInputChange('description', e.target.value)}
            sx={{ mb: 2 }}
          /> */}

          <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
            <Autocomplete
              fullWidth
              size="small"
              value={stages.find(s => s.id === formData.selectedStage) || null}
              onChange={(event, newValue) => handleInputChange('selectedStage', newValue ? parseInt(newValue.id) : '')}
              options={stages}
              getOptionLabel={(stageOption) => stageOption.name || ''}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Select Stage"
                  placeholder="Search stages..."
                />
              )}
            />

            {/* <FormControl fullWidth>
              <InputLabel>Priority</InputLabel>
              <Select
                value={formData.priority}
                onChange={(e) => handleInputChange('priority', e.target.value)}
                label="Priority"
              >
                <MenuItem value="high">High</MenuItem>
                <MenuItem value="medium">Medium</MenuItem>
                <MenuItem value="low">Low</MenuItem>
              </Select>
            </FormControl> */}
          </Box>

          <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
            <TextField
              fullWidth
              type="date"
              label="Due Date"
              value={formData.deadline}
              onChange={(e) => handleInputChange('deadline', e.target.value)}
              InputLabelProps={{ shrink: true }}
            />

            <Autocomplete
              fullWidth
              size="small"
              multiple
              value={Array.isArray(formData.assignedTo)
                ? teamMembers.filter(m => formData.assignedTo.includes(m.pk))
                : []}
              onChange={(event, newValue) =>
                handleInputChange(
                  'assignedTo',
                  Array.isArray(newValue) ? newValue.map(m => m.pk) : []
                )
              }
              options={teamMembers}
              getOptionLabel={(member) => `${member.first_name} ${member.last_name}`}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Assign To"
                  placeholder="Search team members..."
                />
              )}
            />
          </Box>
        </>
      )}

      {action !== 'meeting' && (
        <Button
          variant="contained"
          fullWidth
          onClick={handleSubmit}
          disabled={loading || (!formData.title || (action === 'task' && !formData.selectedStage) || (action !== 'task' && action !== 'document' && !formData.file))}
          sx={{
            mt: 3,
            backgroundColor: config.color,
            '&:hover': {
              backgroundColor: config.color,
              opacity: 0.9
            }
          }}
        >
          {loading ? 'Processing...' : config.buttonText}
        </Button>
      )}
    </Box>
  );
}

// 5% Stage Reminder Drawer Component
function FivePercentReminderDialog({ open, onClose, onSave, onNewsletterSave, companyName, sourceStage }) {
  const [note, setNote] = useState('');
  const [reminderDate, setReminderDate] = useState('');
  const [reminderTime, setReminderTime] = useState('10:00');
  const [isSaving, setIsSaving] = useState(false);
  const [actionType, setActionType] = useState('contact_later'); // 'contact_later' or 'newsletter_only'

  // Get minimum date (tomorrow)
  const getMinDate = () => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    return tomorrow.toISOString().split('T')[0];
  };

  // Get default date (10 days from now)
  const getDefaultDate = () => {
    const defaultDate = new Date();
    defaultDate.setDate(defaultDate.getDate() + 10);
    return defaultDate.toISOString().split('T')[0];
  };

  // Time options
  const timeOptions = React.useMemo(() => {
    const options = [];
    for (let hour = 9; hour <= 18; hour++) {
      options.push({ value: `${hour.toString().padStart(2, '0')}:00`, label: `${hour > 12 ? hour - 12 : hour}:00 ${hour >= 12 ? 'PM' : 'AM'}` });
      options.push({ value: `${hour.toString().padStart(2, '0')}:30`, label: `${hour > 12 ? hour - 12 : hour}:30 ${hour >= 12 ? 'PM' : 'AM'}` });
    }
    return options;
  }, []);

  // Reset form when dialog opens
  useEffect(() => {
    if (open) {
      setNote('');
      setReminderDate(getDefaultDate());
      setReminderTime('10:00');
      setActionType('contact_later');
    }
  }, [open]);

  const handleSave = async () => {
    if (!note.trim()) {
      return;
    }
    
    // For contact later, require reminder date
    if (actionType === 'contact_later' && !reminderDate) {
      return;
    }

    setIsSaving(true);
    try {
      if (actionType === 'newsletter_only') {
        // Call newsletter save handler
        await onNewsletterSave({
          note: note.trim()
        });
      } else {
        // Call regular reminder save handler
        const reminderDateTime = `${reminderDate}T${reminderTime}:00`;
        await onSave({
          note: note.trim(),
          reminderDate: reminderDateTime
        });
      }
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: { width: { xs: '100%', sm: 450 } }
      }}
    >
      {/* Header */}
      <Box sx={{ 
        bgcolor: '#ff9800', 
        color: 'white', 
        p: 2, 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'space-between'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box component="span" sx={{ fontSize: '1.2rem' }}>⚠️</Box>
          <Typography variant="h6">Moving to 5% Stage</Typography>
        </Box>
        <IconButton onClick={onClose} sx={{ color: 'white' }}>
          <CloseIcon />
        </IconButton>
      </Box>

      {/* Content */}
      <Box sx={{ p: 3, flex: 1, overflowY: 'auto' }}>
        <Box sx={{ mb: 3 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            You are moving <strong>{companyName}</strong> from <strong>{sourceStage}</strong> to <strong>5% (On Hold)</strong>.
          </Typography>
        </Box>

        {/* Action Type Selection */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
            What would you like to do with this company?
          </Typography>
          <RadioGroup
            value={actionType}
            onChange={(e) => setActionType(e.target.value)}
          >
            <FormControlLabel 
              value="contact_later" 
              control={<Radio />} 
              label={
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>Contact Later</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Set a reminder to follow up with this company
                  </Typography>
                </Box>
              }
              sx={{ mb: 1, alignItems: 'flex-start', '& .MuiRadio-root': { pt: 0.5 } }}
            />
            <FormControlLabel 
              value="newsletter_only" 
              control={<Radio />} 
              label={
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>Newsletter Only</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Park this company for newsletters only - no future direct contact
                  </Typography>
                </Box>
              }
              sx={{ alignItems: 'flex-start', '& .MuiRadio-root': { pt: 0.5 } }}
            />
          </RadioGroup>
        </Box>

        {/* Note Field */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
            {actionType === 'newsletter_only' 
              ? 'Why is this company being parked for newsletter only?' 
              : 'Why is this company being moved to 5%?'} <span style={{ color: '#f44336' }}>*</span>
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={4}
            placeholder={actionType === 'newsletter_only' 
              ? "Enter the reason for parking this company for newsletter only..."
              : "Enter the reason for moving this company to 5% stage..."}
            value={note}
            onChange={(e) => setNote(e.target.value.slice(0, 2000))}
            helperText={`${note.length}/2000 characters`}
            error={note.length === 0}
          />
        </Box>

        {/* Reminder Date - Only show for Contact Later */}
        {actionType === 'contact_later' && (
          <>
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                When should we remind you? <span style={{ color: '#f44336' }}>*</span>
              </Typography>
              <TextField
                type="date"
                fullWidth
                size="small"
                value={reminderDate}
                onChange={(e) => setReminderDate(e.target.value)}
                inputProps={{ min: getMinDate() }}
              />
            </Box>

            {/* Reminder Time */}
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                At what time?
              </Typography>
              <FormControl fullWidth size="small">
                <Select
                  value={reminderTime}
                  onChange={(e) => setReminderTime(e.target.value)}
                >
                  {timeOptions.map(option => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          </>
        )}

        {/* Newsletter Info Box */}
        {actionType === 'newsletter_only' && (
          <Box sx={{ 
            p: 2, 
            bgcolor: '#fff3e0', 
            borderRadius: 1, 
            border: '1px solid #ffcc80',
            mb: 2
          }}>
            <Typography variant="body2" color="text.secondary">
              <strong>Note:</strong> This company will be moved to the 5% stage and marked for newsletter only. 
              They will receive newsletters but won't be contacted directly for business.
            </Typography>
          </Box>
        )}
      </Box>

      {/* Footer */}
      <Box sx={{ p: 2, borderTop: '1px solid #e0e0e0', display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
        <Button onClick={onClose} disabled={isSaving}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={isSaving || !note.trim() || (actionType === 'contact_later' && !reminderDate)}
          sx={{ 
            bgcolor: actionType === 'newsletter_only' ? '#2196f3' : '#ff9800', 
            '&:hover': { bgcolor: actionType === 'newsletter_only' ? '#1976d2' : '#f57c00' } 
          }}
        >
          {isSaving ? <CircularProgress size={20} /> : (
            actionType === 'newsletter_only' 
              ? 'Move to 5% & Mark Newsletter Only' 
              : 'Move to 5% & Set Reminder'
          )}
        </Button>
      </Box>
    </Drawer>
  );
}

// Reminder Note View/Edit Drawer Component
function ReminderNoteDialog({ open, onClose, reminder, onEdit, onDeactivate }) {
  const [isEditing, setIsEditing] = useState(false);
  const [editNote, setEditNote] = useState('');
  const [editDate, setEditDate] = useState('');
  const [editTime, setEditTime] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (reminder && open) {
      setEditNote(reminder.note || '');
      if (reminder.reminder_date) {
        const date = new Date(reminder.reminder_date);
        setEditDate(date.toISOString().split('T')[0]);
        setEditTime(date.toTimeString().slice(0, 5));
      }
      setIsEditing(false);
    }
  }, [reminder, open]);

  const handleSave = async () => {
    console.log('Save clicked, reminder:', reminder);
    console.log('Reminder ID:', reminder?.id);
    if (!reminder?.id) {
      console.error('No reminder ID found!');
      return;
    }
    setIsSaving(true);
    try {
      const updateData = { note: editNote, reminder_date: `${editDate}T${editTime}:00` };
      console.log('Updating with:', updateData);
      const success = await onEdit(reminder.id, updateData);
      console.log('Update result:', success);
      if (success) {
        setIsEditing(false);
        onClose(); // Close dialog after successful save
      }
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeactivate = async () => {
    if (!reminder?.id) return;
    await onDeactivate(reminder.id);
    onClose();
  };

  if (!reminder) return null;

  const reminderDate = reminder.reminder_date ? new Date(reminder.reminder_date) : null;
  const isDue = reminderDate && reminderDate <= new Date();

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: { width: { xs: '100%', sm: 450 } }
      }}
    >
      {/* Header */}
      <Box sx={{ 
        bgcolor: isDue ? '#f44336' : '#2196f3', 
        color: 'white',
        p: 2,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box component="span" sx={{ fontSize: '1.2rem' }}>{isDue ? '🔔' : '📝'}</Box>
          <Typography variant="h6">{isDue ? 'Reminder Due!' : 'Reminder Details'}</Typography>
        </Box>
        <IconButton onClick={onClose} sx={{ color: 'white' }}>
          <CloseIcon />
        </IconButton>
      </Box>

      {/* Content */}
      <Box sx={{ p: 3, flex: 1, overflowY: 'auto' }}>
        {!isEditing ? (
          <>
            <Box sx={{ mb: 2 }}>
              <Typography variant="caption" color="text.secondary">Reminder Date</Typography>
              <Typography variant="body1" sx={{ fontWeight: 600, color: isDue ? '#f44336' : 'inherit' }}>
                {reminderDate ? reminderDate.toLocaleString() : 'Not set'}
                {isDue && ' (OVERDUE)'}
              </Typography>
            </Box>
            <Box sx={{ mb: 2 }}>
              <Typography variant="caption" color="text.secondary">Note</Typography>
              <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap', bgcolor: '#f5f5f5', p: 2, borderRadius: 1, mt: 0.5 }}>
                {reminder.note || 'No note provided'}
              </Typography>
            </Box>
            {reminder.marked_by_name && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="caption" color="text.secondary">Set by</Typography>
                <Typography variant="body2">{reminder.marked_by_name}</Typography>
              </Box>
            )}
          </>
        ) : (
          <>
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>New Reminder Date</Typography>
              <TextField type="date" fullWidth size="small" value={editDate} onChange={(e) => setEditDate(e.target.value)} />
            </Box>
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>Time</Typography>
              <TextField type="time" fullWidth size="small" value={editTime} onChange={(e) => setEditTime(e.target.value)} />
            </Box>
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>Note</Typography>
              <TextField
                fullWidth
                multiline
                rows={4}
                value={editNote}
                onChange={(e) => setEditNote(e.target.value.slice(0, 2000))}
                helperText={`${editNote.length}/2000 characters`}
              />
            </Box>
          </>
        )}
      </Box>

      {/* Footer */}
      <Box sx={{ p: 2, borderTop: '1px solid #e0e0e0', display: 'flex', justifyContent: 'space-between' }}>
        <Button color="error" onClick={handleDeactivate} disabled={isSaving}>Deactivate Reminder</Button>
        <Box sx={{ display: 'flex', gap: 1 }}>
          {isEditing ? (
            <>
              <Button onClick={() => setIsEditing(false)} disabled={isSaving}>Cancel</Button>
              <Button variant="contained" onClick={handleSave} disabled={isSaving}>
                {isSaving ? <CircularProgress size={20} /> : 'Save Changes'}
              </Button>
            </>
          ) : (
            <>
              <Button onClick={onClose}>Close</Button>
              <Button variant="outlined" onClick={() => setIsEditing(true)}>Edit Reminder</Button>
            </>
          )}
        </Box>
      </Box>
    </Drawer>
  );
}